import { AbstractMesh } from "@babylonjs/core/Meshes/abstractMesh";
import { Color3 } from "@babylonjs/core/Maths/math.color";
import { Matrix } from "@babylonjs/core/Maths/math.vector";
import { Mesh } from "@babylonjs/core/Meshes/mesh";
import { CreateLines, CreateLineSystem } from "@babylonjs/core/Meshes/Builders/linesBuilder";
import { CreatePlane } from "@babylonjs/core/Meshes/Builders/planeBuilder";
import { Plane } from "@babylonjs/core/Maths/math.plane";
import { TransformNode } from "@babylonjs/core/Meshes/transformNode";
import { Vector2, Vector3, Quaternion } from "@babylonjs/core/Maths/math.vector";
import { VertexBuffer } from "@babylonjs/core/Buffers/buffer";
import { Sound } from "@babylonjs/core/Audio/sound";
import { Area2D, ChangePolygonPoints, MoveLine, MoveSpline } from '../tool/tools';
import { RoomElementInteractors } from '../tool/interactors';
import { RoomManager, VisibilityMode, ShapeLevel } from './../RoomManager';
import { ROOM_ELEMENT_CLASSID } from './io/Types';
import { Util, GridFieldPointMode, PointOnPolygons } from "../utils/Util";
import { DummyElement } from './../mdl/DummyElement';
import { MainArticleElement } from '@egr/wcf/modules/cf';
import { BoundingBox as coreBoundingBox } from "@egr/wcf/modules/core/mdl";
import { CameraModeMask, CameraControl } from '@egr/wcf/modules/core/view/CameraControl';
import { ElementCategory, SceneElement, UndoOperation } from '@egr/wcf/modules/core/mdl';
import { ElementChangedFlags } from '@egr/wcf/modules/core/mdl';
import { PbrMaterial } from '@egr/wcf/modules/core/mdl';
import { PropertyValue, StringProperty, LengthProperty, PropertyChangedResult, NumberProperty, Color3Property, BoolProperty } from '@egr/wcf/modules/core/prop';
import { ShadowGenerator } from "@egr/wcf/modules/core/rendering/ShadowGenerator";
import { contains, clone as arrClone, removeElement } from "@egr/wcf/modules/utils/array";
import { eq, clamp, EPSILON, degToRad, radToDeg } from "@egr/wcf/modules/utils/math";
import { VECTOR3_ZERO, VECTOR2_Y, signedAngleBetweenVectors2 } from "@egr/wcf/modules/core/utils/MathUtils";
import { getRotationQuaternion } from "@egr/wcf/modules/core/utils/MeshUtils";
import { serializeVector2, serializeVector3, serializeQuaternion, deserializeVector3, deserializeQuaternion } from "@egr/wcf/modules/core/io";
import { CsgUtils } from "./CsgUtils";
import { createType } from './geo/ElementGeometryHelper';
import { isNotNullOrEmpty } from '@egr/wcf/modules/utils/string';
import { LengthUnit } from "@egr/wcf/modules/utils/number";
import { Log, ListenableEvent } from "@egr/wcf/modules/utils";
import { BoundingBox } from "@babylonjs/core";
import { ERR_NO_FILE_VIA_URL, ERR_SOUNDID_NOT_FOUND, loadSoundFile } from "../utils/BackgroundSound";
var ArenaPart;
(function (ArenaPart) {
    ArenaPart[ArenaPart["PlayField"] = 0] = "PlayField";
    ArenaPart[ArenaPart["SideSegments"] = 1] = "SideSegments";
    ArenaPart[ArenaPart["RunningTracks"] = 2] = "RunningTracks";
    ArenaPart[ArenaPart["TrackLines"] = 3] = "TrackLines";
})(ArenaPart || (ArenaPart = {}));
export class RoomElement extends SceneElement {
    // tslint:disable-next-line:cyclomatic-complexity
    constructor(pRoomManager, pType, pParent = null, pCurvedLine = true, pDescription = '', pMaterialSet, pMaterialCategory, pMaterialValue, onTrigger) {
        //super(pRoomManager.app.model, new TransformNode("room:RoomElement_" + pType, pRoomManager.app.model.scene));
        let tNode = null;
        if (pParent == null) {
            tNode = new TransformNode("room:" + pType, pRoomManager.app.model.scene);
        }
        else {
            tNode = pParent;
        }
        super(pRoomManager.app.model, tNode);
        this.eventPreviewReadyToPlay = new ListenableEvent(this);
        this.eventPreviewStopped = new ListenableEvent(this);
        this.preview_ended = (pSound, eventState) => {
            this._clearTimeouts();
            this.eventPreviewStopped.trigger(this);
        };
        this.mRoomManager = pRoomManager;
        this.mScene = pRoomManager.app.model.scene;
        this.mInteractors = new RoomElementInteractors(this.mRoomManager, this);
        this.mInteractorsVisible = false;
        this.mId = Util.randomString(10);
        this.mIdStr = Util.HashString();
        this.misTransforming = false;
        this.mIsDrawingNew = false;
        this.mRE_reference = null;
        this.mMainElement = null;
        this.mDummy_REF = null;
        this.mGridRE_REF = null;
        this.mREforGrid_REF = [];
        this.mGridFieldData = {};
        this.mGridFieldData.full = 0;
        this.mGridFieldData.diagonal = 0;
        this.mGridFieldData.vertical = 0;
        this.mGridFieldData.horizontal = 0;
        this.mSZ_SZmeshes = new Array();
        this.mSZ_FSmeshes = new Array();
        this.mSZ_FHmeshes = new Array();
        this.mSZ_offset = new Vector3(0, 0, 0);
        this.mSZ_visible = true;
        this.mType = pType;
        this.mShapeLevel = RoomManager.ROOM_2dSHAPE_POSITION_DEFAULT_Y;
        // description
        switch (pType) {
            case "Spline":
            case "Line":
            case "Rectangle":
            case "Building":
            case "CustomBuilding":
            case "Circle":
            case "DummySafetyZone":
            case "SafetyZone":
            case "Road":
            case "Hedge":
            case "Fence":
            case "Sphere":
            case "BackgroundImage":
            case "Text":
                this.mDescription = pType.toString();
                break;
            case "Plane_BottomTop":
                if (pDescription.trim() !== '') {
                    this.mDescription = pDescription;
                }
                else {
                    this.mDescription = pType.toString();
                }
                break;
            case "DummyElement":
                if (pDescription.trim() !== '') {
                    this.mDescription = pDescription;
                }
                else {
                    this.mDescription = pType.toString();
                }
                break;
            default:
                this.mDescription = '';
        }
        // numbering
        switch (pType) {
            case "Spline":
            case "Line":
            case "Rectangle":
            case "Circle":
            case "SafetyZone":
                this.mRENo = this.mRoomManager._getNextREId();
                this.mLabel = "Area [" + this.mRENo.toString() + "]";
                break;
            default:
                this.mLabel = "";
                break;
        }
        this.mPosition = Vector3.Zero();
        this.mWall = null;
        this.mWallAttachData = null;
        this.mFloorOffset = 0;
        this.mMirrorVertical = false;
        this.mMirrorHorizontal = false;
        this.mWallSidePolygon = null;
        this.mGeometryUpdateDepth = 0;
        this.mPendingGeometryUpdate = false;
        this.mHandleTansformationChange = true;
        this.mPendingTransformMatrix = null;
        this.getScaleFactor();
        //create geometry groups
        if (this.mNode != null) {
            this.mNode.freezeWorldMatrix();
        }
        this.m3dGroup = new TransformNode("group_3d", this.mScene);
        this.m3dGroup.parent = this.mNode;
        this.m3dGroup.position.y = 0;
        if (this.mType === "SafetyZone" || this.mType === "Road")
            this.m3dGroup.position.y = RoomManager.ROOM_SAFETYZONE_POSITION_Y;
        this.m3dGroup.freezeWorldMatrix();
        this.m2dGroup = this.m3dGroup.clone("group_2d", this.mNode);
        this.m2dGroup.position.y = 0;
        if (this.mType === "SafetyZone" || this.mType === "Road") {
            this.m2dGroup.position.y = RoomManager.ROOM_SAFETYZONE_POSITION_Y;
        }
        if (this.mType === "Grid") {
            this.m2dGroup.position.y = RoomManager.ROOM_SAFETYZONE_POSITION_Y * 2;
        }
        this.m2dGroup.freezeWorldMatrix();
        this.mSpecialGeometryGroup = new TransformNode("group_Special", this.mScene);
        this.mSpecialGeometryGroup.parent = this.mNode;
        this.mSpecialGeometryGroup.position.y = 0;
        if (this.mType === "SafetyZone" || this.mType === "Road")
            this.mSpecialGeometryGroup.position.y = RoomManager.ROOM_SAFETYZONE_POSITION_Y;
        this.mSpecialGeometryGroup.freezeWorldMatrix();
        this.mAdditionalGeometryGroup = new TransformNode("group_Additional", this.mScene);
        this.mAdditionalGeometryGroup.parent = this.mNode;
        this.mAdditionalGeometryGroup.position.y = 0;
        this.mAdditionalGeometryGroup.setEnabled(false);
        this.mAdditionalGeometryGroup.freezeWorldMatrix();
        this.mInitalPosition = null;
        this.mgeo_Latitude = 50;
        this.mgeo_Longitude = 10;
        if (onTrigger != null) {
            this.monTriggerGeoAction = onTrigger;
        }
        this.mSound = null;
        this.mSound_Preview = null;
        this.mAudioURL = '';
        this.mCurrentVolume = 1;
        this.marr_TimeOut = [];
        this.mCustomParent = null;
        this.mElementStatus = "";
        this.mTransformStatus = "";
        this.mBuildingOpacity = this.mRoomManager.BuildingOpacity;
        this.mGroupMeshOpacity = 1;
        this.mParams = {};
        this.initElementParams();
        this.mcurvedRoad = pCurvedLine;
        this.mcurvedHedge = pCurvedLine;
        this.mRefPoint = new Vector3(0, 0, 0);
        this.mRefPointStartIndex = 0;
        this.mShowCrossHair = false;
        // grid measure
        this.mgridCellSize = this.mRoomManager.gridFieldDim[0];
        //create geometry instance and setup intial parameters
        let tParams = {};
        if (this.mType !== "DummyElement") {
            this.mGeometry = createType(this.mRoomManager, this, pType, pCurvedLine);
            if (this.mGeometry == null)
                throw new Error("Failed to create ElementGeometry: " + pType);
            tParams = this.mGeometry.getInitialParams();
            this.mWidth = tParams.width;
            this.mDepth = tParams.depth;
            this.mHeight = tParams.height;
            this.mHeightSet = false;
            this.mColor = tParams.color;
            if (tParams.text != null)
                this.mText = tParams.text;
            if (tParams.textScale != null)
                this.mTextHeight = tParams.textScale;
            if (tParams.textColor != null)
                this.mTextColor = tParams.textColor;
            if (tParams.imageURL != null)
                this.mImageURL = tParams.imageURL;
            if (tParams.imageProportional != null)
                this.mImageProportional = tParams.imageProportional;
            this.mParams.terrainType = "Flat";
            this.mParams.hillOffset = this.mType === "Road" ? 0.2 : 1;
            this.mParams.hillProfile = "Medium";
            this.mParams.hillHeight = 0.5;
            this.mParams.hillTopOffset = Vector3.Zero();
        }
        this.mDiameter = 0;
        this.mMaterial = -1;
        this.mMaterialId = "";
        if (pMaterialSet != null) {
            this.mMaterialSet = pMaterialSet;
            this.mIdStr = pMaterialSet.idAddOn.replace('_-_', '');
            this.mMaterialSet.loaded = true;
            this.mMaterialSet.setInit = true;
        }
        else {
            this.mMaterialSet = {};
            this.mMaterialSet.category = "color";
            this.mMaterialSet.loadMatId = "";
            this.mMaterialSet.rotation = 0;
            this.mMaterialSet.offsetU = 0;
            this.mMaterialSet.offsetU_init = 0;
            this.mMaterialSet.offsetV = 0;
            this.mMaterialSet.offsetV_init = 0;
            this.mMaterialSet.scaleU = 1;
            this.mMaterialSet.scaleU_init = 1;
            this.mMaterialSet.scaleV = 1;
            this.mMaterialSet.scaleV_init = 1;
            this.mMaterialSet.setInit = false;
            this.mMaterialSet.idAddOn = this.mRoomManager.materialSplitStr + this.mIdStr;
            this.mMaterialSet.loaded = false;
            switch (pType) {
                case "Rectangle":
                case "Spline":
                case "Circle":
                case "Line":
                    this.mMaterialSet.value = "#777777";
                    this.setMaterialCategory("color", "#777777", false, this.mMaterialSet);
                    break;
                case "GeoMarker":
                    this.mMaterialSet.value = this.mRoomManager.app.theme.mainColorBright;
                    this.setMaterialCategory("color", this.mRoomManager.app.theme.mainColorBright, false, this.mMaterialSet);
                    break;
                case "PillarB":
                case "PillarC":
                case "Building":
                case "CustomBuilding":
                    this.mMaterialSet.value = "#ffffff";
                    this.setMaterialCategory("color", "#ffffff", false, this.mMaterialSet);
                    break;
                case "Floor":
                    this.mMaterialSet.value = "#aaaaaa";
                    this.setMaterialCategory("color", "#aaaaaa", false, this.mMaterialSet);
                    break;
                case "WallSideDummy":
                    this.mMaterialSet.value = "#777777";
                    break;
                case "DummySafetyZone":
                    this.mMaterialSet.value = "#000000";
                    break;
                case "Sphere":
                    if (this.mParams.audioSphere) {
                        this.mMaterialSet.value = "#ffffff";
                    }
                    break;
                case "Arena":
                    this.mMaterialSet = {};
                    this.mMaterialSet.category = "wetpour";
                    this.mMaterialSet.value = "SUR11107-902";
                    this.mMaterialSet.loadMatId = "";
                    this.mMaterialSet.rotation = 0;
                    this.mMaterialSet.offsetU = 0;
                    this.mMaterialSet.offsetU_init = 0;
                    this.mMaterialSet.offsetV = 0;
                    this.mMaterialSet.offsetV_init = 0;
                    this.mMaterialSet.scaleU = 0;
                    this.mMaterialSet.scaleU_init = 0;
                    this.mMaterialSet.scaleV = 0;
                    this.mMaterialSet.scaleV_init = 0;
                    this.mMaterialSet.idAddOn = this.mRoomManager.materialSplitStr + this.mIdStr;
                    this.mMaterialSet.loaded = false;
                    this.mMaterialSet_Arena_PlayField = {};
                    this.mMaterialSet_Arena_PlayField.category = "other";
                    this.mMaterialSet_Arena_PlayField.value = "GRASS1";
                    this.mMaterialSet_Arena_PlayField.loadMatId = "";
                    this.mMaterialSet_Arena_PlayField.rotation = 0;
                    this.mMaterialSet_Arena_PlayField.offsetU = 0;
                    this.mMaterialSet_Arena_PlayField.offsetU_init = 0;
                    this.mMaterialSet_Arena_PlayField.offsetV = 0;
                    this.mMaterialSet_Arena_PlayField.offsetV_init = 0;
                    this.mMaterialSet_Arena_PlayField.scaleU = 0;
                    this.mMaterialSet_Arena_PlayField.scaleU_init = 0;
                    this.mMaterialSet_Arena_PlayField.scaleV = 0;
                    this.mMaterialSet_Arena_PlayField.scaleV_init = 0;
                    this.mMaterialSet_Arena_PlayField.idAddOn = this.mRoomManager.materialSplitStr + this.mIdStr;
                    this.mMaterialSet_Arena_PlayField.loaded = false;
                    this.mMaterialSet_Arena_SideSegments = {};
                    this.mMaterialSet_Arena_SideSegments.category = "other";
                    this.mMaterialSet_Arena_SideSegments.value = "SAND1";
                    this.mMaterialSet_Arena_SideSegments.loadMatId = "";
                    this.mMaterialSet_Arena_SideSegments.rotation = 0;
                    this.mMaterialSet_Arena_SideSegments.offsetU = 0;
                    this.mMaterialSet_Arena_SideSegments.offsetU_init = 0;
                    this.mMaterialSet_Arena_SideSegments.offsetV = 0;
                    this.mMaterialSet_Arena_SideSegments.offsetV_init = 0;
                    this.mMaterialSet_Arena_SideSegments.scaleU = 0;
                    this.mMaterialSet_Arena_SideSegments.scaleU_init = 0;
                    this.mMaterialSet_Arena_SideSegments.scaleV = 0;
                    this.mMaterialSet_Arena_SideSegments.scaleV_init = 0;
                    this.mMaterialSet_Arena_SideSegments.idAddOn = this.mRoomManager.materialSplitStr + this.mIdStr;
                    this.mMaterialSet_Arena_SideSegments.loaded = false;
                    this.mMaterialSet_Arena_TrackLines = {};
                    this.mMaterialSet_Arena_TrackLines.category = "color";
                    this.mMaterialSet_Arena_TrackLines.value = "#ffffff";
                    this.mMaterialSet_Arena_TrackLines.loadMatId = "";
                    this.mMaterialSet_Arena_TrackLines.rotation = 0;
                    this.mMaterialSet_Arena_TrackLines.offsetU = 0;
                    this.mMaterialSet_Arena_TrackLines.offsetU_init = 0;
                    this.mMaterialSet_Arena_TrackLines.offsetV = 0;
                    this.mMaterialSet_Arena_TrackLines.offsetV_init = 0;
                    this.mMaterialSet_Arena_TrackLines.scaleU = 0;
                    this.mMaterialSet_Arena_TrackLines.scaleU_init = 0;
                    this.mMaterialSet_Arena_TrackLines.scaleV = 0;
                    this.mMaterialSet_Arena_TrackLines.scaleV_init = 0;
                    this.mMaterialSet_Arena_TrackLines.idAddOn = this.mRoomManager.materialSplitStr + this.mIdStr;
                    this.mMaterialSet_Arena_TrackLines.loaded = false;
                    this.mParams.arenaLaneScale = 0.125;
                    this.mParams.arenaFieldScale = 1;
                    this.mParams.arenaLaneCount = 2;
                    this.mParams.arenaPlayFieldVisible = true;
                    this.mParams.arenaSideSegmentsVisible = true;
                    this.mParams.arenaCircle = false;
                    this.setMaterialCategory("wetpour", "SUR11107-902", false, this.mMaterialSet);
                    this.setMaterialCategory("other", "GRASS1", false, this.mMaterialSet_Arena_PlayField);
                    this.setMaterialCategory("other", "SAND1", false, this.mMaterialSet_Arena_SideSegments);
                    this.setMaterialCategory("color", "#ffffff", false, this.mMaterialSet_Arena_TrackLines);
                    break;
                default:
                    this.mMaterialSet.value = "#aaaaaa";
                    this.setMaterialCategory("color", "#aaaaaa", false, this.mMaterialSet);
            }
        }
        this.setMaterialId(this.mRoomManager.getMaterialId(this.mMaterialSet));
        this.mSoundVolume = 100;
        this.setSoundSet_init();
        this.mLength = 0;
        if (this.mType !== "SafetyZone") {
            if (this.mType === "Fence") {
                this.setDefaultMATCategory("fence");
            }
            if (this.mType === "Hedge") {
                this.setDefaultMATCategory("hedge");
            }
            if (this.mType === "Road") {
                this.setDefaultMATCategory("other");
            }
            if (this.mType === "Line" && pMaterialCategory != null && pMaterialValue != null) {
                this.setInitialMaterial(pMaterialCategory, pMaterialValue);
            }
        }
        this.mImageURL = "";
        this.mImageBase64 = "";
        this.mArea = 0;
        this.mPerimeter = 0;
        this.mOnSite = this.mType === "Road" ? true : false;
        this.mClosedPolygon = false;
        this.mOrderIndex = -1;
        this.mCSG = null;
        this.mCAG = null;
        this.mCAG_Arena_InField = null;
        this.mCAG_Arena_SideSegments = null;
        this.mCAG_Arena_RunningsTracks = null;
        this.mCAG_Arena_TrackLines = null;
        this.mCAGoriginal = null;
        this.mCAGoriginal_Arena_InField = null;
        this.mCAGoriginal_Arena_SideSegments = null;
        this.mCAGoriginal_Arena_RunningsTracks = null;
        this.mCAGoriginal_Arena_TrackLines = null;
        this.mCAGoriginal = null;
        this.mCAG_notVisible = null;
        this.mCAG_notVisible_Arena_InField = null;
        this.mCAG_notVisible_Arena_SideSegments = null;
        this.mCAG_notVisible_Arena_TrackLines = null;
        this.mCAG_notVisible_Arena_RunningTracks = null;
        this.mCutCAG = new Array();
        this.mDeletedCAG = new Array();
        this.mCAG_freespace = new Array();
        this.mOutlines = new Array();
        this.mWallIds = "";
        this.mMeshBounds = new BoundingBox(new Vector3(0, 0, 0), new Vector3(0, 0, 0));
        this.mInteractorPoints = new Array();
        this.mPickPlane = new Plane(0, 1, 0, 0);
        // if (this.mType === "CameraPath") {
        //     this.mPickPlane = new Plane(0, 0, 0, -this.mHeight);
        // } else {
        //     this.mPickPlane = new Plane(0, 0, 0, 0);
        // }
        this.mARStartPoint = new Vector3(0, 0, 0);
        this.mARStartPointSet = false;
        this.mSizeChanging = false;
        this.mToolRunning = false;
        let tOType = "";
        switch (pType) {
            case "Arena":
            case "Spline":
            case "Line":
            case "Rectangle":
            case "Building":
            case "CustomBuilding":
            case "Circle":
            case "DummySafetyZone":
            case "SafetyZone":
            case "Road":
            case "Hedge":
            case "Fence":
            case "Plane_BottomTop":
            case "DummyElement":
                tOType = "MeshObject";
                break;
            case "BackgroundImage":
                tOType = "Image";
                break;
            case "Sphere":
                if (!this.mParams.audioSphere) {
                    tOType = "MeshObject";
                }
                break;
            case "Text":
                tOType = "TextObject";
                break;
            default:
                tOType = "";
        }
        this.mObjectType = tOType;
        this.mBasketId = "";
        this.mCuttedRoomElements = new Array();
        this.mOverlapped = false;
        this.mRoomManager.app.model.eventBeginTransformElements.addListener(this.onBeginTransform(this));
        this.mRoomManager.app.model.eventEndTransformElements.addListener(this.onEndTransform(this));
        this.mRoomManager.app.i18n.eventLengthUnitChanged.addListener(this.onLengthUnitChanged(this));
        if (this.mType === "CameraPath") {
            const tPos = this.mNode.position.clone();
            tPos.y = this.mHeight;
            this.setPosition(tPos);
            this.mRoomManager.createCamLogo();
        }
        this.mRoomManager.app.viewer.scene.onBeforeCameraRenderObservable.add(this.onBeforeCameraChanged(this));
        if (this.mType !== "WallSideDummy" && this.mType !== "SafetyZone" &&
            !this.mRoomManager.isSceneLoading) {
            if (this.mType === "Arena") {
                this.mRoomManager.checkMaterial(this.mMaterialSet).then((pResult) => {
                    this.mRoomManager.checkMaterial(this.mMaterialSet_Arena_PlayField).then((pResult1) => {
                        this.mRoomManager.checkMaterial(this.mMaterialSet_Arena_SideSegments).then((pResult2) => {
                            this.mRoomManager.checkMaterial(this.mMaterialSet_Arena_TrackLines).then((pResult3) => {
                                this.setMeshMaterial1();
                            });
                        });
                    });
                });
            }
            else {
                this.mRoomManager.checkMaterial(this.mMaterialSet).then((pResult) => {
                    this.setMeshMaterial1();
                    if (this.mType !== "DummyElement")
                        this.setFloorOffset(tParams.floorOffset, false);
                });
            }
        }
        this.setMesh2d = this.setMesh2d.bind(this);
        this.setMesh3d = this.setMesh3d.bind(this);
    }
    get type() {
        return (this.mType);
    }
    get IdStr() {
        return (this.mIdStr);
    }
    get Id() {
        return (this.mId);
    }
    get RE_reference() {
        return (this.mRE_reference);
    }
    get GridRE_REF() {
        return (this.mGridRE_REF);
    }
    get REforGrid_REF() {
        return (this.mREforGrid_REF);
    }
    get width() {
        return (this.mWidth);
    }
    get gridFieldsData() {
        return (this.mGridFieldData);
    }
    get gridCellSize() {
        return (this.mgridCellSize);
    }
    get height() {
        return (this.mHeight);
    }
    get scaleFactor() {
        return (this.mScaleFactor);
    }
    get shapeLevel() {
        return (this.mShapeLevel);
    }
    get sound() {
        return (this.mSound);
    }
    get soundSet() {
        return (this.mSoundSet);
    }
    get audioVolume() {
        return (this.mSoundVolume);
    }
    get audioURL() {
        return (this.mAudioURL);
    }
    get interactors() {
        return (this.mInteractors);
    }
    get interactorsVisible() {
        return (this.mInteractorsVisible);
    }
    get heightSet() {
        return (this.mHeightSet);
    }
    get ratioWH() {
        return (this.mWidth / this.mHeight);
    }
    get depth() {
        return (this.mDepth);
    }
    get dummy_REF() {
        return (this.mDummy_REF);
    }
    get isTransforming() {
        return (this.misTransforming);
    }
    get isDrawingNew() {
        return (this.mIsDrawingNew);
    }
    curvedRoad() {
        return (this.mcurvedRoad);
    }
    curvedHedge() {
        return (this.mcurvedHedge);
    }
    get customMin() {
        return (this.mCustomMin);
    }
    get customMax() {
        return (this.mCustomMax);
    }
    get area() {
        return (this.mArea);
    }
    get perimeter() {
        let toutLines = this.getOutlines();
        let tRes = 0;
        for (let tLine of toutLines) {
            tRes += Vector3.Distance(tLine[0], tLine[1]);
        }
        this.mPerimeter = tRes;
        return (this.mPerimeter);
    }
    get meshCenter() {
        let tRes = new Vector3(0, 0, 0);
        let tchildMeshes = this.m3dGroup.getChildMeshes();
        if (tchildMeshes.length === 0)
            return (tRes);
        tRes = tchildMeshes[0].getBoundingInfo().boundingBox.centerWorld;
        return (tRes);
    }
    get RefPoint() {
        return (this.mRefPoint);
    }
    get RefPointIndices() {
        let tRes = {};
        tRes.P1 = 0;
        tRes.P2 = 1;
        if (this.mRefPointStartIndex >= 0 && this.mRefPointStartIndex < this.mParams.ElementPoints.length) {
            tRes.P1 = this.mRefPointStartIndex;
            if (this.mRefPointStartIndex === this.mParams.ElementPoints.length - 1)
                tRes.P2 = 0;
            else
                tRes.P2 = tRes.P1 + 1;
        }
        return (tRes);
    }
    get roadWidth() {
        return (this.mWidth);
    }
    get BasketID() {
        return (this.mBasketId);
    }
    get Overlapped() {
        return this.mOverlapped;
    }
    get onsite() {
        return (this.mOnSite);
    }
    get onTriggerAction() {
        return (this.monTriggerGeoAction);
    }
    get label() {
        return (this.mLabel);
    }
    get crossHairVisible() {
        return (this.mShowCrossHair);
    }
    get closedPolygon() {
        return (this.mClosedPolygon);
    }
    get BuildingOpacity() {
        return (this.mBuildingOpacity);
    }
    get GroupMeshOpacity() {
        return (this.mGroupMeshOpacity);
    }
    get boundingBox() {
        return (this.getSelectionHighlightBoundingBox());
    }
    get diameter() {
        return (this.mDiameter);
    }
    get material() {
        return (this.mMaterial);
    }
    get materialId() {
        return (this.mMaterialId);
    }
    get materialSet() {
        return (this.mMaterialSet);
    }
    get material_Arena_PlayField() {
        return (this.mMaterial_Arena_PlayField);
    }
    get material_Arena_SideSegments() {
        return (this.mMaterial_Arena_SideSegments);
    }
    get material_Arena_TrackLines() {
        return (this.mMaterial_Arena_TrackLines);
    }
    get materialId_Arena_PlayField() {
        return (this.mMaterialId_Arena_PlayField);
    }
    get materialId_Arena_SideSegments() {
        return (this.mMaterialId_Arena_SideSegments);
    }
    get materialId_Arena_TrackLines() {
        return (this.mMaterialId_Arena_TrackLines);
    }
    get materialSet_Arena_PlayField() {
        return (this.mMaterialSet_Arena_PlayField);
    }
    get materialSet_Arena_SideSegments() {
        return (this.mMaterialSet_Arena_SideSegments);
    }
    get materialSet_Arena_TrackLines() {
        return (this.mMaterialSet_Arena_TrackLines);
    }
    get materialId_local() {
        return (this.mMaterialId + "__local__" + this.mIdStr);
    }
    get materialCategory() {
        return (this.mMaterialSet.category);
    }
    get materialValue() {
        return (this.mMaterialSet.value);
    }
    get localMaterial() {
        let tmatId = this.mRoomManager.getMaterialId(this.mMaterialSet);
        let tMat = null;
        if (this.mMaterialSet.rotation === 0 &&
            this.mMaterialSet.offsetU === this.mMaterialSet.offsetU_init && this.mMaterialSet.offsetV === this.mMaterialSet.offsetV_init &&
            this.mMaterialSet.scaleU === this.mMaterialSet.scaleU_init && this.mMaterialSet.scaleV === this.mMaterialSet.scaleV_init) {
            tMat = this.mRoomManager.MatManager.getMaterial(tmatId);
        }
        else {
            const tmatIdAddOn = tmatId + this.mMaterialSet.idAddOn;
            tMat = this.mRoomManager.MatManager.getMaterial(tmatIdAddOn);
            if (tMat == null) {
                tMat = this.mRoomManager.MatManager.getMaterial(tmatId);
            }
        }
        if (tMat != null) {
            this.mRoomManager.setInitialTextureScaleValues(tMat, this.mMaterialSet);
            return (tMat);
        }
        return (this.mRoomManager.wallDefaultMaterial);
    }
    get localMaterial_Arena_PlayField() {
        let tmatId = this.mRoomManager.getMaterialId(this.mMaterialSet_Arena_PlayField);
        let tMat = null;
        if (this.mMaterialSet_Arena_PlayField.rotation === 0 &&
            this.mMaterialSet_Arena_PlayField.offsetU === this.mMaterialSet_Arena_PlayField.offsetU_init && this.mMaterialSet_Arena_PlayField.offsetV === this.mMaterialSet_Arena_PlayField.offsetV_init &&
            this.mMaterialSet_Arena_PlayField.scaleU === this.mMaterialSet_Arena_PlayField.scaleU_init && this.mMaterialSet_Arena_PlayField.scaleV === this.mMaterialSet_Arena_PlayField.scaleV_init) {
            tMat = this.mRoomManager.MatManager.getMaterial(tmatId);
        }
        else {
            tmatId = tmatId + this.mMaterialSet_Arena_PlayField.idAddOn;
            tMat = this.mRoomManager.MatManager.getMaterial(tmatId);
        }
        if (tMat != null) {
            return (tMat);
        }
        return (this.mRoomManager.wallDefaultMaterial);
    }
    get localMaterial_Arena_SideSegments() {
        let tmatId = this.mRoomManager.getMaterialId(this.mMaterialSet_Arena_SideSegments);
        let tMat = null;
        if (this.mMaterialSet_Arena_SideSegments.rotation === 0 &&
            this.mMaterialSet_Arena_SideSegments.offsetU === this.mMaterialSet_Arena_SideSegments.offsetU_init && this.mMaterialSet_Arena_SideSegments.offsetV === this.mMaterialSet_Arena_SideSegments.offsetV_init &&
            this.mMaterialSet_Arena_SideSegments.scaleU === this.mMaterialSet_Arena_SideSegments.scaleU_init && this.mMaterialSet_Arena_SideSegments.scaleV === this.mMaterialSet_Arena_SideSegments.scaleV_init) {
            tMat = this.mRoomManager.MatManager.getMaterial(tmatId);
        }
        else {
            tmatId = tmatId + this.mMaterialSet_Arena_SideSegments.idAddOn;
            tMat = this.mRoomManager.MatManager.getMaterial(tmatId);
        }
        if (tMat != null) {
            return (tMat);
        }
        return (this.mRoomManager.wallDefaultMaterial);
    }
    get localMaterial_Arena_TrackLines() {
        let tmatId = this.mRoomManager.getMaterialId(this.mMaterialSet_Arena_TrackLines);
        let tMat = null;
        if (this.mMaterialSet_Arena_TrackLines.rotation === 0 &&
            this.mMaterialSet_Arena_TrackLines.offsetU === this.mMaterialSet_Arena_TrackLines.offsetU_init && this.mMaterialSet_Arena_TrackLines.offsetV === this.mMaterialSet_Arena_TrackLines.offsetV_init &&
            this.mMaterialSet_Arena_TrackLines.scaleU === this.mMaterialSet_Arena_TrackLines.scaleU_init && this.mMaterialSet_Arena_TrackLines.scaleV === this.mMaterialSet_Arena_TrackLines.scaleV_init) {
            tMat = this.mRoomManager.MatManager.getMaterial(tmatId);
        }
        else {
            tmatId = tmatId + this.mMaterialSet_Arena_TrackLines.idAddOn;
            tMat = this.mRoomManager.MatManager.getMaterial(tmatId);
        }
        if (tMat != null) {
            return (tMat);
        }
        return (this.mRoomManager.wallDefaultMaterial);
    }
    get localMaterial_Hill() {
        let tmatId = this.mRoomManager.getMaterialId(this.mMaterialSet);
        let tMat = null;
        if (this.mMaterialSet.rotation === 0) {
            tMat = this.mRoomManager.MatManager.getMaterial(tmatId + '_hill');
        }
        else {
            tmatId = tmatId + this.mMaterialSet.idAddOn;
            tMat = this.mRoomManager.MatManager.getMaterial(tmatId + '_hill');
        }
        if (tMat != null) {
            return (tMat);
        }
        if (this.mMaterialSet.rotation === 0) {
            tMat = this.mRoomManager.MatManager.getMaterial(tmatId);
        }
        else {
            tmatId = tmatId + this.mMaterialSet.idAddOn;
            tMat = this.mRoomManager.MatManager.getMaterial(tmatId);
        }
        if (tMat != null) {
            if (tMat.albedoTexture == null) {
                return (tMat);
            }
            let thillMat = null;
            thillMat = this.mRoomManager.getHillMaterial(tMat);
            if (thillMat != null) {
                return (thillMat);
            }
            thillMat = getPBR(tMat, this.mScene, this.mRoomManager.app);
            this.mRoomManager.addHillMaterial(tMat.id, thillMat);
            return (thillMat);
        }
        return (this.mRoomManager.wallDefaultMaterial);
    }
    assignUV() {
        const tms = this.m3dGroup.getChildMeshes();
        if (tms.length == 0) {
            return;
        }
        const tmesh = tms[0];
        Util.generateBoxUV(tmesh, false);
        this.mRoomManager.app.viewer.requestRenderFrame();
    }
    get imageURL() {
        return (this.mImageURL);
    }
    get imageBase64() {
        return (this.mImageBase64);
    }
    get imageProportional() {
        return (this.mImageProportional);
    }
    get color() {
        return (this.mColor);
    }
    get text() {
        return (this.mText);
    }
    get textColor() {
        return (this.mTextColor);
    }
    get textHeight() {
        return (this.mTextHeight);
    }
    get floorOffset() {
        return (this.mFloorOffset);
    }
    get mirrorVertical() {
        return (this.mMirrorVertical);
    }
    get mirrorHorizontal() {
        return (this.mMirrorHorizontal);
    }
    get wall() {
        return (this.mWall);
    }
    get wallSidePolygon() {
        return (this.mWallSidePolygon);
    }
    get Params() {
        return (this.mParams);
    }
    get geometry() {
        return (this.mGeometry);
    }
    get geoPosition() {
        return ({ latitude: this.mgeo_Latitude, longitude: this.mgeo_Longitude });
    }
    get position() {
        return (this.mPosition);
    }
    get category() {
        let tElementCategory = ElementCategory.None;
        switch (this.mType) {
            case "Spline":
            case "Line":
            case "Rectangle":
            case "Circle":
            case "Floor":
            case "Road":
            case "WallSideDummy":
            case "BackgroundImage":
            case "Hedge":
            case "Text":
            case "Sphere":
            case "CameraPath":
            case "Arena":
            case "Grid":
                tElementCategory = ElementCategory.FloorPlan;
                break;
            case "CustomBuilding":
            case "Building":
                tElementCategory = ElementCategory.None;
                break;
            default:
                tElementCategory = ElementCategory.None;
        }
        return (tElementCategory);
    }
    get parentWall() {
        return (this.mParentWall);
    }
    get status() {
        return (this.mElementStatus);
    }
    get transformStatus() {
        return (this.mTransformStatus);
    }
    get orderIndex() {
        return (this.mOrderIndex);
    }
    get get2dNode() {
        let t2dNodes = this.m2dGroup.getChildTransformNodes();
        if (t2dNodes.length > 0)
            return (t2dNodes[0]);
        return (null);
    }
    get get3dNode() {
        let t3dNodes = this.m3dGroup.getChildTransformNodes();
        if (t3dNodes.length > 0)
            return (t3dNodes[0]);
        return (null);
    }
    get group2d() {
        return (this.m2dGroup);
    }
    get group3d() {
        return (this.m3dGroup);
    }
    get SpecialGeometryGroup() {
        return (this.mSpecialGeometryGroup);
    }
    get AdditionalGeometryGroup() {
        return (this.mAdditionalGeometryGroup);
    }
    get CustomParent() {
        return (this.mCustomParent);
    }
    get InteractorPoints() {
        return (this.mInteractorPoints);
    }
    get PickPlane() {
        return (this.mPickPlane);
    }
    get ARStartPointSet() {
        return (this.mARStartPointSet);
    }
    get ARStartPoint() {
        return (this.mARStartPoint);
    }
    get SizeChanging() {
        return (this.mSizeChanging);
    }
    get ToolRunning() {
        return (this.mToolRunning);
    }
    get ObjectType() {
        return (this.mObjectType);
    }
    get Vertices() {
        let tRes = null;
        let tMeshes = this.m3dGroup.getChildMeshes();
        if (tMeshes != null) {
            tRes = tMeshes[0].getVerticesData(VertexBuffer.PositionKind);
        }
        return (tRes);
    }
    get Indices() {
        let tRes = null;
        let tMeshes = this.m3dGroup.getChildMeshes();
        if (tMeshes != null) {
            tRes = tMeshes[0].getIndices();
        }
        return (tRes);
    }
    get CSG() {
        return (this.mCSG);
    }
    get CAG() {
        return (this.mCAG);
    }
    get CAG_Arena_PlayField() {
        return (this.mCAG_Arena_InField);
    }
    get CAG_Arena_SideSegments() {
        return (this.mCAG_Arena_SideSegments);
    }
    get CAG_Arena_RunningTracks() {
        return (this.mCAG_Arena_RunningsTracks);
    }
    get CAG_Arena_TrackLines() {
        return (this.mCAG_Arena_TrackLines);
    }
    get CAGoriginal() {
        return (this.mCAGoriginal);
    }
    get CAG_notVisible() {
        return (this.mCAG_notVisible);
    }
    get CAG_notVisible_Arena_PlayField() {
        return (this.mCAG_notVisible_Arena_InField);
    }
    get CAG_notVisible_Arena_SideSegments() {
        return (this.mCAG_notVisible_Arena_SideSegments);
    }
    get CAG_notVisible_Arena_RunningTracks() {
        return (this.mCAG_notVisible_Arena_RunningTracks);
    }
    get CAG_notVisible_Arena_TrackLines() {
        return (this.mCAG_notVisible_Arena_TrackLines);
    }
    get CAG_FS() {
        return (this.mCAG_freespace);
    }
    get CutCAG() {
        return (this.mCutCAG);
    }
    get DeletedCAG() {
        return (this.mDeletedCAG);
    }
    _getType() {
        let tType = this.mType.toString().toLocaleLowerCase();
        switch (this.mType) {
            case "Rectangle":
            case "Line":
            case "Line":
            case "Spline":
            case "Circle":
                tType = "2dshape";
                break;
            default:
                this.mType.toString().toLocaleLowerCase();
        }
        return (tType);
    }
    getMainElement() {
        if (this.mNode == null || this.mNode.parent == null)
            return (this);
        let tParent = SceneElement.FromNode(this.mNode.parent, true);
        if (tParent != null &&
            tParent instanceof MainArticleElement &&
            this.mBasketId.trim() === "" &&
            tParent.basketId != null) {
            this.mBasketId = tParent.basketId;
        }
        return (tParent == null ? this : tParent);
    }
    getMaterialCategoryValue(pList) {
        let tRes = pList[0];
        if (this.mMaterialSet.category.trim() === "")
            this.setMaterialCategory(StringProperty.ConvertValueToString(tRes.value), undefined, undefined, this.mMaterialSet);
        for (let tProp of pList) {
            if (tProp.value === this.mMaterialSet.category) {
                tRes = tProp;
                break;
            }
        }
        return (tRes);
    }
    disposeAudio() {
        if (this.mSound) {
            this.mSound.detachFromMesh();
            this.mSound.stop();
            setTimeout(() => {
                if (this.mSound != null) {
                    this.mSound.dispose();
                    this.mSound = null;
                }
            }, 50);
        }
        if (this.mSound_Preview) {
            this.mSound_Preview.stop();
            setTimeout(() => {
                if (this.mSound_Preview) {
                    this.mSound_Preview.dispose();
                    this.mSound_Preview = null;
                }
            }, 50);
        }
    }
    dispose2dGroup() {
        let t2dNodes = this.m2dGroup.getChildTransformNodes(true);
        for (let tNode of t2dNodes) {
            tNode.dispose();
        }
        let t2dMeshes = this.m2dGroup.getChildMeshes();
        for (let tMesh of t2dMeshes) {
            tMesh.dispose();
        }
    }
    dispose3dGroup() {
        let t3dNodes = this.m3dGroup.getChildTransformNodes(true);
        for (let tNode of t3dNodes) {
            tNode.dispose();
        }
        let t3dMeshes = this.m3dGroup.getChildMeshes();
        for (let tMesh of t3dMeshes) {
            tMesh.dispose();
        }
    }
    disposeSpecialGeometryGroup() {
        let tspecialNodes = this.mSpecialGeometryGroup.getChildTransformNodes(true);
        for (let tNode of tspecialNodes) {
            tNode.dispose();
        }
        let tspecialMeshes = this.mSpecialGeometryGroup.getChildMeshes();
        for (let tMesh of tspecialMeshes) {
            tMesh.dispose();
        }
    }
    disposeAdditionalGeometryGroup() {
        let tadditionalNodes = this.mAdditionalGeometryGroup.getChildTransformNodes(true);
        for (let tNode of tadditionalNodes) {
            tNode.dispose();
        }
        let tadditionalMeshes = this.mAdditionalGeometryGroup.getChildMeshes();
        for (let tMesh of tadditionalMeshes) {
            tMesh.dispose();
        }
    }
    dispose() {
        // if (this.mType === "CameraPath" && !this.isDisposed) {
        //     this.mRoomManager.eventCameraPathRemoved.trigger(this.mRoomManager);
        // }
        super.dispose();
        this.mRoomManager.app.model.eventBeginTransformElements.removeListener(this.onBeginTransform(this));
        this.mRoomManager.app.model.eventEndTransformElements.removeListener(this.onEndTransform(this));
        this.mRoomManager.app.viewer.scene.onBeforeCameraRenderObservable.removeCallback(this.onBeforeCameraChanged(this));
        this.dispose2dGroup();
        this.dispose3dGroup();
        this.disposeSpecialGeometryGroup();
        this.disposeAdditionalGeometryGroup();
        // dispose transform node array
        if (this.mType === "Line") {
            const tLine = this.mGeometry;
            if (tLine != null) {
                let tCornerAngleNodes = tLine.getTransformNodeArr();
                if (tCornerAngleNodes != null) {
                    for (let tTNode of tCornerAngleNodes) {
                        if (tTNode.node != null) {
                            tTNode.node.dispose();
                        }
                    }
                    tCornerAngleNodes = [];
                    tCornerAngleNodes = null;
                }
                tLine.hideCornerAngles(true);
            }
        }
        this.disposeAudio();
        if (this.mdrawnMesh2d != null)
            this.mdrawnMesh2d.dispose();
        if (this.mdrawnMesh3d != null)
            this.mdrawnMesh3d.dispose();
        this.mWall = null;
        if (this.type === "RoomAR") {
            this.geometry.clear2dGUI();
        }
        if (this.mInteractors != null) {
            this.mInteractors.dispose();
        }
        this.mCAG = null;
        this.mCAG_Arena_InField = null;
        this.mCAG_Arena_SideSegments = null;
        this.mCAG_Arena_RunningsTracks = null;
        this.mCAG_Arena_TrackLines = null;
        this.mCAG_freespace = [];
        this.mCAGoriginal = null;
        this.mCAGoriginal_Arena_InField = null;
        this.mCAGoriginal_Arena_SideSegments = null;
        this.mCAGoriginal_Arena_RunningsTracks = null;
        this.mCAGoriginal_Arena_TrackLines = null;
        this.mCutCAG = [];
    }
    canTranslate() {
        let tType = this.mGeometry.getType();
        let tRes = false;
        if (this.mRoomManager.ProjectId === "001") {
            switch (tType) {
                case "Spline":
                case "Line":
                case "Circle":
                case "Rectangle":
                case "Building":
                case "CustomBuilding":
                case "SafetyZone":
                case "Floor":
                case "Road":
                    tRes = true;
                    break;
                case "Sphere":
                case "CameraPath":
                    tRes = false;
                    break;
                default:
                    tRes = true;
            }
        }
        if (this.mRoomManager.ProjectId !== "001") {
            switch (tType) {
                case "Spline":
                case "Line":
                case "Circle":
                case "Rectangle":
                case "Building":
                case "CustomBuilding":
                    tRes = true;
                    break;
                case "SafetyZone":
                case "Floor":
                case "Road":
                case "Sphere":
                case "CameraPath":
                    tRes = false;
                    break;
                default:
                    tRes = true;
            }
        }
        if (this.isSelected && this.mRoomManager.app.model.selection.length > 1)
            tRes = true;
        return (tRes);
    }
    canDelete() {
        let tType = this.mGeometry.getType();
        let tRes = false;
        switch (tType) {
            case "Floor":
                tRes = false;
                break;
            default:
                tRes = true;
        }
        return (tRes);
    }
    canSelect() {
        let tRes = true;
        switch (this.mType) {
            case "WallSideDummy":
                tRes = false;
                break;
            default:
                tRes = true;
        }
        return (tRes);
    }
    canSelectSubElement() {
        if (this.mType === "Grid") {
            return (true);
        }
        return (false);
    }
    canRotate() {
        let tType = this.mGeometry.getType();
        let tRes = false;
        if (this.mRoomManager.ProjectId === "001") {
            switch (tType) {
                case "Floor":
                case "Building":
                case "CustomBuilding":
                case "SafetyZone":
                case "CameraPath":
                case "GeoMarker":
                    tRes = false;
                    break;
                case "Sphere":
                    if (this.mParams.audioSphere) {
                        tRes = false;
                    }
                    else {
                        tRes = true;
                    }
                    break;
                default:
                    tRes = true;
            }
        }
        if (this.mRoomManager.ProjectId !== "001") {
            switch (tType) {
                case "Floor":
                case "SafetyZone":
                case "PillarB":
                case "PillarC":
                case "CameraPath":
                    tRes = false;
                    break;
                case "Sphere":
                    if (this.mParams.audioSphere) {
                        tRes = false;
                    }
                    else {
                        tRes = true;
                    }
                    break;
                default:
                    tRes = true;
            }
        }
        return (tRes);
    }
    showInteractors(pShow, pOnlyLength = false) {
        if (this.mType === "Line" && this.mGridRE_REF != null) {
            this.mGridRE_REF.showInteractors(pShow, pOnlyLength);
        }
        if (this.mInteractors != null) {
            this.mInteractors.showInteractors(pShow, pOnlyLength);
            this.mInteractorsVisible = pShow;
            if (this.mType === "Line") {
                const tlineGEO = this.mGeometry;
                tlineGEO.setAnglesVisibility(true, pShow);
            }
        }
    }
    setTextPickPlane(pEnabled) {
        if (this.mType === "Text") {
            this.geometry.setPickPlane(pEnabled);
        }
    }
    setRefPoint(pPos) {
        this.mRefPoint = pPos;
    }
    setOnTriggerGeoAction(onTriggerAction) {
        this.monTriggerGeoAction = onTriggerAction;
    }
    setOriginalElementParams(pPosition, pHeight) {
        this.mParams.ElementOriginalPositionX = pPosition.x;
        this.mParams.ElementOriginalPositionY = pPosition.y;
        this.mParams.ElementOriginalPositionZ = pPosition.z;
        this.mParams.ElementOriginalHeight = pHeight;
    }
    setOriginalPosition(pPosition) {
        this.mParams.ElementOriginalPositionX = pPosition.x;
        this.mParams.ElementOriginalPositionY = pPosition.y;
        this.mParams.ElementOriginalPositionZ = pPosition.z;
    }
    setCrossHairVisibility(pValue) {
        this.mShowCrossHair = pValue;
        this.updateGeometry(false);
        this.notifyChange(ElementChangedFlags.PropertyValues);
        this.showInteractors(this.mInteractorsVisible);
    }
    setVisibility(pshow) {
        if (pshow) {
            this.mNode.setEnabled(true);
        }
        else {
            this.showInteractors(false);
            this.mRoomManager.app.model.removeFromSelection(this);
            this.mNode.setEnabled(false);
        }
        this.mRoomManager.app.viewer.requestRenderFrame();
    }
    moveMeshGroupsY(pPositionY) {
        this.m3dGroup.position.y = pPositionY;
        this.m2dGroup.position.y = pPositionY;
        this.updateMatrix();
    }
    play_preview(pSoundId, pDuration) {
        if (!(this.mType == "Sphere" && this.mParams.audioSphere)) {
            return;
        }
        const tsndSet = this.mRoomManager.getSoundEffectsById(pSoundId);
        if (tsndSet == null) {
            Log.error("SoundId not found! -> " + pSoundId + " <-", ERR_SOUNDID_NOT_FOUND);
            return;
        }
        this._clearTimeouts();
        this.stop_PreviewSound();
        try {
            let tFile = loadSoundFile(tsndSet.fileURL).then((pFile) => {
                if (pFile == null) {
                    Log.error("Failed to load sound file: " + tsndSet.fileURL, ERR_NO_FILE_VIA_URL);
                }
                else {
                    setTimeout(() => {
                        if (this.mSound_Preview != null) {
                            this.mSound_Preview.dispose();
                            this.mSound_Preview = null;
                        }
                        this.mSound_Preview = new Sound("sound_Preview", pFile, this.mRoomManager.app.scene, () => {
                            var _a, _b;
                            //
                            this.eventPreviewReadyToPlay.trigger(this);
                            (_a = this.mSound_Preview) === null || _a === void 0 ? void 0 : _a.onEndedObservable.add(this.preview_ended);
                            (_b = this.mSound_Preview) === null || _b === void 0 ? void 0 : _b.play();
                            if (pDuration) {
                                this.marr_TimeOut.push(setTimeout(() => {
                                    this.eventPreviewStopped.trigger(this);
                                    this.stop_PreviewSound();
                                }, pDuration * 1000));
                            }
                        }, { loop: false, autoplay: false, volume: this.mSoundVolume * 0.01 });
                    }, 10);
                }
            });
        }
        catch (e) {
            Log.error("Failed to load sound file: " + tsndSet.fileURL, e);
        }
        finally {
            //
        }
    }
    stop_preview() {
        this._clearTimeouts();
        this.stop_PreviewSound();
    }
    // tslint:disable-next-line:cyclomatic-complexity
    clone(pNewParent) {
        if (this.mType === "Floor") {
            return (null);
        }
        if (this.mType === "Grid") {
            const tclone = this.clone_grid(pNewParent);
            return (tclone);
        }
        let tClone = new RoomElement(this.mRoomManager, this.mGeometry.getType());
        tClone.beginGeometryUpdate();
        tClone.node.parent = pNewParent != null ? pNewParent : null;
        tClone.copyDataFrom(this);
        if (this.mType === "Arena") {
            this.mRoomManager.checkMaterial(tClone.mMaterialSet).then((pResult) => {
                if (pResult === 1 || pResult === 2) {
                    if (this.mType === "Arena") {
                        this.mRoomManager.checkMaterial(tClone.mMaterialSet_Arena_PlayField).then((pResult1) => {
                            if (pResult1 === 1 || pResult1 === 2) {
                                this.mRoomManager.checkMaterial(tClone.mMaterialSet_Arena_SideSegments).then((pResult2) => {
                                    if (pResult2 === 1 || pResult2 === 2) {
                                        this.mRoomManager.checkMaterial(tClone.mMaterialSet_Arena_TrackLines).then((pResult3) => {
                                            if (pResult3 === 1 || pResult3 === 2) {
                                                tClone.updateGeometry();
                                                tClone.notifyChange(ElementChangedFlags.PropertyValues);
                                                tClone.notifyChange(ElementChangedFlags.Properties);
                                            }
                                        });
                                    }
                                });
                            }
                        });
                    }
                    else {
                        tClone.updateGeometry();
                        tClone.notifyChange(ElementChangedFlags.PropertyValues);
                        tClone.notifyChange(ElementChangedFlags.Properties);
                    }
                }
            });
        }
        if (this.mType === "Sphere" && this.mParams.audioSphere) {
            tClone.m2dGroup.setEnabled(false);
        }
        if (this.mGeometry.getType() === "BackgroundImage")
            tClone.node.rotation.x = -Math.PI / 2;
        if (this.mGeometry.getType() === "CustomBuilding") {
            this.mRoomManager.addBuilding(tClone);
            let tBuilding_BottomTop = new RoomElement(this.mRoomManager, "Plane_BottomTop");
            this.mRoomManager.app.model.addElement(tBuilding_BottomTop);
            tBuilding_BottomTop.setRE_reference(tClone);
            tClone.setRE_reference(tBuilding_BottomTop);
            tBuilding_BottomTop.setHeight(tClone.height, false);
        }
        tClone.endGeometryUpdate();
        this.mInitialClone = true;
        tClone.eventAdded.addListener(() => this.afterClone());
        if (this.mType === "SafetyZone") {
            this.mRoomManager._addRoomElement(tClone);
        }
        else if (this.mType !== "Text") {
            tClone.setMaterialId(this.mMaterialId);
        }
        return (tClone);
    }
    saveUndoData(pOperation) {
        return (this._saveUndoData());
    }
    levelCameraPath() {
        const tsel = this.isSelected;
        this.mRoomManager.app.model.removeFromSelection(this);
        if (this.mType !== "CameraPath") {
            return;
        }
        const tREgeo = this.geometry;
        let tPoints = this.mParams.ElementPoints.slice();
        for (let tP of tPoints) {
            tP.y = 0;
        }
        tREgeo.setPoints(tPoints);
        tREgeo.computeCamPathData();
        this.updateGeometry(false);
        if (tsel) {
            this.mRoomManager.app.model.addToSelection(this);
        }
        this.mRoomManager.app.rendering.notifyModelInteraction();
        this.mRoomManager.app.viewer.requestRenderFrame();
    }
    loadUndoData(pOperation, pUndoData) {
        this._loadUndoData(pUndoData);
        this.mRoomManager.cutOverlappingShapes();
        if (pOperation === UndoOperation.Transform)
            this.mRoomManager._removeTransformedElement(this);
    }
    // tslint:disable-next-line:cyclomatic-complexity
    collectSafetyZoneMeshes(pMainArticle = null, pChanged = false) {
        if (this.node === null || pMainArticle === null)
            return;
        if (pChanged) {
            let tMainElement = this.getMainElement();
            if (tMainElement !== pMainArticle)
                return;
        }
        let tsafetyZoneMeshes = new Array();
        let tfallHeightMeshes = new Array();
        let tfreeSpaceMeshes = new Array();
        this.mSZ_SZmeshes = [];
        this.mSZ_SZmeshes = [];
        this.mSZ_FSmeshes = [];
        this.mSZ_FHmeshes = [];
        this.mCAG_freespace = [];
        let tMainArticle = this.getMainElement();
        if (tMainArticle === this)
            return;
        const tMAscaling = tMainArticle.node.scaling.clone();
        const tCSG_V3 = new CSG.Vector3D([1 / tMAscaling.x, 1 / tMAscaling.y, 1 / tMAscaling.z]);
        let tstandardList = new Array();
        tstandardList.push(this.mRoomManager.SZ_standard_selected);
        if (this.mRoomManager.SZ_standard_selected === "EN")
            tstandardList.push("EN16630");
        if (this.mRoomManager.SZ_standard_selected === "ASTM")
            tstandardList.push("ASTMF3101");
        for (let tselStandard of tstandardList) {
            for (let tSE of this.mRoomManager.ManufacturerSeriesList) {
                let tSZ_meshesA = this.mRoomManager.app.model.layers.getMeshesOnLayer("72_" + this.mRoomManager.ProjectManufacturer + "_" + tSE + "_D3_" +
                    tselStandard + "_SAFETYZONE_A", tMainArticle.node);
                let tSZ_meshes = this.mRoomManager.app.model.layers.getMeshesOnLayer("72_" + this.mRoomManager.ProjectManufacturer + "_" + tSE + "_D3_" +
                    tselStandard + "_SAFETYZONE", tMainArticle.node);
                let tSZ_meshes01A = this.mRoomManager.app.model.layers.getMeshesOnLayer("72_" + this.mRoomManager.ProjectManufacturer + "_" + tSE + "_D3_" +
                    tselStandard + "-SAFETYZONE_A", tMainArticle.node);
                let tSZ_meshes01 = this.mRoomManager.app.model.layers.getMeshesOnLayer("72_" + this.mRoomManager.ProjectManufacturer + "_" + tSE + "_D3_" +
                    tselStandard + "-SAFETYZONE", tMainArticle.node);
                let tFHmeshes = this.mRoomManager.app.model.layers.getMeshesOnLayer("72_" + this.mRoomManager.ProjectManufacturer + "_" + tSE + "_D3_DIMENSIONPOINT_FALLHEIGHT", tMainArticle.node);
                let tFSmeshes = this.mRoomManager.app.model.layers.getMeshesOnLayer("72_" + this.mRoomManager.ProjectManufacturer + "_" + tSE + "_D3_" +
                    tselStandard + "_FREE_SPACE", tMainArticle.node);
                let tFSmeshesNEZA = this.mRoomManager.app.model.layers.getMeshesOnLayer("72_" + this.mRoomManager.ProjectManufacturer + "_" + tSE + "_D3_" +
                    tselStandard + "_NOENCROACHMENTZONE_A", tMainArticle.node);
                let tFSmeshesNEZ = this.mRoomManager.app.model.layers.getMeshesOnLayer("72_" + this.mRoomManager.ProjectManufacturer + "_" + tSE + "_D3_" +
                    tselStandard + "_NOENCROACHMENTZONE", tMainArticle.node);
                let tFSmeshesCZA = this.mRoomManager.app.model.layers.getMeshesOnLayer("72_" + this.mRoomManager.ProjectManufacturer + "_" + tSE + "_D3_" +
                    tselStandard + "_CLEARANCEZONE_A", tMainArticle.node);
                let tFSmeshesCZ = this.mRoomManager.app.model.layers.getMeshesOnLayer("72_" + this.mRoomManager.ProjectManufacturer + "_" + tSE + "_D3_" +
                    tselStandard + "_CLEARANCEZONE", tMainArticle.node);
                let tFSmeshesTEA = this.mRoomManager.app.model.layers.getMeshesOnLayer("72_" + this.mRoomManager.ProjectManufacturer + "_" + tSE + "_D3_" +
                    tselStandard + "-TRAININGENVELOPE_A", tMainArticle.node);
                let tFSmeshesTE = this.mRoomManager.app.model.layers.getMeshesOnLayer("72_" + this.mRoomManager.ProjectManufacturer + "_" + tSE + "_D3_" +
                    tselStandard + "-TRAININGENVELOPE", tMainArticle.node);
                let tFSmeshesTEAu = this.mRoomManager.app.model.layers.getMeshesOnLayer("72_" + this.mRoomManager.ProjectManufacturer + "_" + tSE + "_D3_" +
                    tselStandard + "_TRAININGENVELOPE_A", tMainArticle.node);
                let tFSmeshesTEu = this.mRoomManager.app.model.layers.getMeshesOnLayer("72_" + this.mRoomManager.ProjectManufacturer + "_" + tSE + "_D3_" +
                    tselStandard + "_TRAININGENVELOPE", tMainArticle.node);
                let tFSmeshesTSA = this.mRoomManager.app.model.layers.getMeshesOnLayer("72_" + this.mRoomManager.ProjectManufacturer + "_" + tSE + "_D3_" +
                    tselStandard + "-TRAININGSPACE_A", tMainArticle.node);
                let tFSmeshesTS = this.mRoomManager.app.model.layers.getMeshesOnLayer("72_" + this.mRoomManager.ProjectManufacturer + "_" + tSE + "_D3_" +
                    tselStandard + "-TRAININGSPACE", tMainArticle.node);
                let tFSmeshesTSAu = this.mRoomManager.app.model.layers.getMeshesOnLayer("72_" + this.mRoomManager.ProjectManufacturer + "_" + tSE + "_D3_" +
                    tselStandard + "_TRAININGSPACE_A", tMainArticle.node);
                let tFSmeshesTSu = this.mRoomManager.app.model.layers.getMeshesOnLayer("72_" + this.mRoomManager.ProjectManufacturer + "_" + tSE + "_D3_" +
                    tselStandard + "_TRAININGSPACE", tMainArticle.node);
                this.mSZ_SZmeshes = this.mSZ_SZmeshes.concat(tSZ_meshesA);
                this.mSZ_SZmeshes = this.mSZ_SZmeshes.concat(tSZ_meshes);
                this.mSZ_SZmeshes = this.mSZ_SZmeshes.concat(tSZ_meshes01A);
                this.mSZ_SZmeshes = this.mSZ_SZmeshes.concat(tSZ_meshes01);
                this.mSZ_FHmeshes = this.mSZ_FHmeshes.concat(tFHmeshes);
                this.mSZ_FSmeshes = this.mSZ_FSmeshes.concat(tFSmeshes);
                this.mSZ_FSmeshes = this.mSZ_FSmeshes.concat(tFSmeshesNEZA);
                this.mSZ_FSmeshes = this.mSZ_FSmeshes.concat(tFSmeshesNEZ);
                this.mSZ_FSmeshes = this.mSZ_FSmeshes.concat(tFSmeshesCZA);
                this.mSZ_FSmeshes = this.mSZ_FSmeshes.concat(tFSmeshesCZ);
                this.mSZ_FSmeshes = this.mSZ_FSmeshes.concat(tFSmeshesTEA);
                this.mSZ_FSmeshes = this.mSZ_FSmeshes.concat(tFSmeshesTE);
                this.mSZ_FSmeshes = this.mSZ_FSmeshes.concat(tFSmeshesTEAu);
                this.mSZ_FSmeshes = this.mSZ_FSmeshes.concat(tFSmeshesTEu);
                this.mSZ_FSmeshes = this.mSZ_FSmeshes.concat(tFSmeshesTSA);
                this.mSZ_FSmeshes = this.mSZ_FSmeshes.concat(tFSmeshesTS);
                this.mSZ_FSmeshes = this.mSZ_FSmeshes.concat(tFSmeshesTSAu);
                this.mSZ_FSmeshes = this.mSZ_FSmeshes.concat(tFSmeshesTSu);
            }
        }
        let tMainNode = tMainArticle.node;
        if (tMainNode != null) {
            let tMAworldMatrix = tMainNode.getWorldMatrix();
            let tinvMAworldMatrix = tMAworldMatrix.invert();
            let tMAtranslation = tinvMAworldMatrix.getTranslation();
            let tinvertRotation = 0;
            if (tMainNode.rotationQuaternion != null) {
                let teulerRotation = tMainNode.rotationQuaternion.toEulerAngles();
                tinvertRotation = teulerRotation.y * (180 / Math.PI);
            }
            let tsafetyzoneCAGArr = new Array();
            for (let tMesh of this.mSZ_SZmeshes) {
                const tCAG = Util.convertMeshPlane2CAG(tMesh);
                if (tCAG != null) {
                    tsafetyzoneCAGArr.push(tCAG);
                }
            }
            for (let tMesh of this.mSZ_FSmeshes) {
                let tCAG = Util.convertMeshPlane2CAG(tMesh);
                if (tCAG != null) {
                    tCAG = tCAG.rotateZ(tinvertRotation);
                    tCAG = tCAG.translate([tMAtranslation.x, tMAtranslation.z, tMAtranslation.y]);
                    this.mCAG_freespace.push(tCAG);
                }
            }
            // based on CAG
            let tpolyFinal = new CAG();
            if (tsafetyzoneCAGArr.length === 1) {
                tpolyFinal = tsafetyzoneCAGArr[0];
            }
            else if (tsafetyzoneCAGArr.length > 0) {
                tpolyFinal = tsafetyzoneCAGArr[0];
                tpolyFinal = tpolyFinal.union(tsafetyzoneCAGArr);
            }
            tpolyFinal = tpolyFinal.rotateZ(tinvertRotation);
            tpolyFinal = tpolyFinal.scale(tCSG_V3);
            tpolyFinal = tpolyFinal.translate([tMAtranslation.x, tMAtranslation.z, tMAtranslation.y]);
            this.mCAG = tpolyFinal;
            this.mCAGoriginal = tpolyFinal;
            this.mCAG_notVisible = tpolyFinal;
            this.setREDimensionFromCAG();
        }
    }
    get_SafetyZone_Visibility() {
        return (this.mSZ_visible);
    }
    isSelectionHighlightEnabled() {
        if (this.mType === "WallSideDummy" ||
            this.mType === "CameraPath" ||
            this.mType === "GeoMarker") {
            return (false);
        }
        return (true);
    }
    // tslint:disable-next-line:cyclomatic-complexity
    getSelectionHighlightBoundingBox() {
        let tW = this.mWidth * 0.5;
        let tD = this.depth * 0.5;
        let tH = this.mHeight;
        if (tH < 0.01)
            tH = 0.01;
        let tBounds = new coreBoundingBox(new Vector3(0, 0, 0), new Vector3(0, 0, 0));
        switch (this.mType) {
            case "Spline":
                let tSpline = this.geometry;
                let tminPointS = tSpline.minPoint();
                let tmaxPointS = tSpline.maxPoint();
                if (this.mParams.terrainType !== "Flat") {
                    tH = this.mParams.hillHeight;
                }
                tBounds = new coreBoundingBox(new Vector3(tminPointS.x, 0, tminPointS.z), new Vector3(tmaxPointS.x, tH, tmaxPointS.z));
                break;
            case "Rectangle":
                let tRectangle = this.geometry;
                if (this.mParams.terrainType !== "Flat") {
                    tH = this.mParams.hillHeight;
                }
                tBounds = new coreBoundingBox(new Vector3(tRectangle.minPoint.x, 0, tRectangle.minPoint.z), new Vector3(tRectangle.maxPoint.x, tH, tRectangle.maxPoint.z));
                break;
            case "Line":
                let tLine = this.geometry;
                let tminPointL = tLine.minPoint();
                let tmaxPointL = tLine.maxPoint();
                if (this.mParams.terrainType !== "Flat") {
                    tH = this.mParams.hillHeight;
                }
                tBounds = new coreBoundingBox(new Vector3(tminPointL.x, 0, tminPointL.z), new Vector3(tmaxPointL.x, tH, tmaxPointL.z));
                break;
            case "Grid":
                tBounds = new coreBoundingBox(new Vector3(-tW, 0.0, -tD), new Vector3(tW, tH, tD));
                if (!this.m2dGroup.isEnabled() && this.mREforGrid_REF != null && this.mREforGrid_REF.length > 0) {
                    const tselRE4Grid = this.getSubElementSelection();
                    if (tselRE4Grid != null) {
                        tBounds = tselRE4Grid.boundingBoxVisible;
                    }
                }
                break;
            case "Circle":
                let tCircle = this.geometry;
                tW = tCircle.width() * 0.5;
                tD = tCircle.depth() * 0.5;
                if (this.mParams.terrainType !== "Flat") {
                    tH = this.mParams.hillHeight;
                }
                tBounds = new coreBoundingBox(new Vector3(-tW, 0, -tD), new Vector3(tW, tH, tW));
                break;
            case "BackgroundImage":
                let tbackImg = this.geometry;
                tBounds = new coreBoundingBox(tbackImg.minPoint, tbackImg.maxPoint);
                break;
            case "Building":
                let tBuilding = this.geometry;
                let tminPointB = tBuilding.minPoint();
                let tmaxPointB = tBuilding.maxPoint();
                tBounds = new coreBoundingBox(new Vector3(tminPointB.x, 0, tminPointB.z), new Vector3(tmaxPointB.x, tmaxPointB.y, tmaxPointB.z));
                break;
            case "CustomBuilding":
                let tCustomBuilding = this.geometry;
                let tminPointCB = tCustomBuilding.minPoint();
                let tmaxPointCB = tCustomBuilding.maxPoint();
                tBounds = new coreBoundingBox(new Vector3(tminPointCB.x, 0, tminPointCB.z), new Vector3(tmaxPointCB.x, this.height, tmaxPointCB.z));
                break;
            case "CameraPath":
            case "GeoMarker":
                // let tcamPath = this.geometry as CameraPath;
                // let tcp_minPointCB = tcamPath.minPoint();
                // let tcp_maxPointCB = tcamPath.maxPoint();
                // tBounds = new coreBoundingBox(
                // new Vector3(tcp_minPointCB.x, 0, tcp_minPointCB.z),
                // new Vector3(tcp_maxPointCB.x, this.height, tcp_maxPointCB.z));
                break;
            case "Arena":
                const ttmpBounds = Util.getBoundingBoxOfCAG(this.mCAG);
                if (ttmpBounds == null) {
                    tBounds = new coreBoundingBox(new Vector3(0, 0, 0), new Vector3(0, 0, 0));
                }
                else {
                    tBounds = ttmpBounds;
                    const tWidth = ttmpBounds.max.x - ttmpBounds.min.x;
                    const tDepth = ttmpBounds.max.z - ttmpBounds.min.z;
                    this.setWidth(tWidth, false, false, false);
                    this.setDepth(tDepth, false, false);
                }
                break;
            default:
                tBounds = new coreBoundingBox(new Vector3(-tW, 0.0, -tD), new Vector3(tW, tH, tD));
                break;
        }
        return (tBounds);
    }
    toggleAudio() {
        if (!this.mParams.audioSphere) {
            return;
        }
    }
    turnRefPointStart_Left() {
        if (this.mRefPointStartIndex === this.mParams.ElementPoints.length - 1)
            this.mRefPointStartIndex = 0;
        else
            this.mRefPointStartIndex++;
        this.updateGeometry(false);
        this.showInteractors(this.mInteractorsVisible);
        this.mRoomManager.app.viewer.requestRenderFrame();
    }
    turnRefPointStart_Right() {
        if (this.mRefPointStartIndex === 0)
            this.mRefPointStartIndex = this.mParams.ElementPoints.length - 1;
        else
            this.mRefPointStartIndex--;
        this.updateGeometry(false);
        this.showInteractors(this.mInteractorsVisible);
        this.mRoomManager.app.viewer.requestRenderFrame();
    }
    _serializeDummyElement(pContext) {
        let tIORoomElement = {};
        if (this.mRE_reference == null)
            return (null);
        tIORoomElement.classId = ROOM_ELEMENT_CLASSID;
        tIORoomElement.type = "DummyElement";
        tIORoomElement.description = this.mDescription;
        tIORoomElement.objectType = this.mObjectType;
        //material
        let tMatNo = pContext.addMaterial(this.materialId);
        tIORoomElement.materialId = this.materialId;
        //position
        let tREpos = this.mDummyMesh.absolutePosition.clone();
        tIORoomElement.position = serializeVector3(tREpos);
        //rotation
        let tRotation = getRotationQuaternion(this.mRE_reference.node).clone();
        tRotation.normalize();
        if (!eq(tRotation.w, 1))
            tIORoomElement.rotation = serializeQuaternion(tRotation);
        if (this.mDummyMesh != null) {
            let tmeshVertices = this.mDummyMesh.getVerticesData(VertexBuffer.PositionKind);
            let tmeshUV = this.mDummyMesh.getVerticesData(VertexBuffer.UVKind);
            let tmeshIndices = this.mDummyMesh.getIndices();
            this.mParams.MeshVertices = Array.prototype.slice.call(tmeshVertices);
            this.mParams.MeshIndices = Array.prototype.slice.call(tmeshIndices);
            this.mParams.MeshUVcoord = Array.prototype.slice.call(tmeshUV);
        }
        this.mParams.material = this._getMaterialIndex();
        this.mParams.materialColor = this.getColorValueOfMatId(this.mMaterialId);
        let tgeoData = this.mParams;
        if (tgeoData != null) {
            tIORoomElement.geometryData = tgeoData;
        }
        return (tIORoomElement);
    }
    // tslint:disable-next-line:cyclomatic-complexity
    serialize(pContext) {
        if (this.mType === "WallSideDummy" || this.mType === "Grid") {
            return (null);
        }
        if (this.mType === "DummySafetyZone" && this.mRE_reference != null) {
            if (contains(this.mRoomManager.RoomElements, this.mRE_reference))
                return (this.mRE_reference.serialize(pContext));
            return (null);
        }
        if (this.mType === "DummyElement") {
            let tDummyElement = this._serializeDummyElement(pContext);
            return (tDummyElement);
        }
        let tIORoomElement = {};
        tIORoomElement.classId = ROOM_ELEMENT_CLASSID;
        tIORoomElement.type = this.mGeometry.getType();
        tIORoomElement.description = this.mDescription;
        tIORoomElement.label = this.mLabel;
        tIORoomElement.color = this.mColor;
        if (tIORoomElement.color.indexOf("#") !== 0)
            tIORoomElement.color = "#999999";
        tIORoomElement.depth = this.mDepth;
        tIORoomElement.height = this.mHeight;
        tIORoomElement.width = this.mWidth;
        tIORoomElement.shapeLevel = this.mShapeLevel;
        tIORoomElement.objectType = this.mObjectType;
        if (this.mType === "SafetyZone") {
            tIORoomElement.SZvisible = this.mSZ_visible;
            if (!this.mSZ_visible) {
                tIORoomElement.objectType = "";
            }
        }
        if (this.mType === "GeoMarker") {
            let tinitPos = Vector3.Zero();
            if (this.mInitalPosition != null) {
                tinitPos = this.mInitalPosition.clone();
            }
            tIORoomElement.geo_initPosition = serializeVector3(tinitPos);
            tIORoomElement.geo_initLatitude = this.mInitGeo_Latitude;
            tIORoomElement.geo_initLongitude = this.mInitGeo_Longitude;
            tIORoomElement.geo_latitude = this.mgeo_Latitude;
            tIORoomElement.geo_longitude = this.mgeo_Longitude;
            tIORoomElement.geo_initMapRotation = this.mInitGeo_MapRotation;
        }
        if (this.mType === "Line" && this.GridRE_REF != null) {
            tIORoomElement.gridwidth = this.GridRE_REF.width;
            tIORoomElement.griddepth = this.GridRE_REF.depth;
            tIORoomElement.gridXInt = this.GridRE_REF.gridCellSize.x;
            tIORoomElement.gridZInt = this.GridRE_REF.gridCellSize.y;
            tIORoomElement.gridId = this.GridRE_REF.Id;
        }
        switch (tIORoomElement.type) {
            case "CustomBuilding":
                if (this.mBuildingOpacity != null)
                    tIORoomElement.opacity = this.mBuildingOpacity;
                break;
            case "Building":
                //tIORoomElement.floorOffset = -this.mHeight;
                break;
            default: //3
                tIORoomElement.floorOffset = this.mFloorOffset;
                break;
        }
        if (this.mText != null)
            tIORoomElement.text = this.mText;
        if (this.mTextColor != null)
            tIORoomElement.textcolor = this.mTextColor;
        if (this.mTextHeight != null)
            tIORoomElement.textscale = this.mTextHeight;
        let torderIndex = this.mRoomManager.Shapes2D.indexOf(this);
        tIORoomElement.orderIndex = torderIndex;
        if (this.mType === "SafetyZone") {
            let tME = this.getMainElement();
            if (tME instanceof MainArticleElement) {
                let tbasketId = tME.basketId;
                if (tbasketId !== null && tbasketId !== "") {
                    this.mBasketId = tbasketId;
                    tIORoomElement.basketId = this.mBasketId;
                }
            }
        }
        //material
        let tmatId = this.mRoomManager.getMaterialId(this.mMaterialSet);
        if (this.mMaterialSet.rotation !== 0 ||
            this.mMaterialSet.offsetU !== this.mMaterialSet.offsetU_init || this.mMaterialSet.offsetV !== this.mMaterialSet.offsetV_init ||
            this.mMaterialSet.scaleU !== this.mMaterialSet.scaleU_init || this.mMaterialSet.scaleV !== this.mMaterialSet.scaleV_init) {
            tmatId += this.materialSet.idAddOn;
        }
        tIORoomElement.materialId = tmatId;
        let tMatNo = pContext.addMaterial(tmatId);
        tIORoomElement.materialSet = this.mMaterialSet;
        if (this.mType === "Arena") {
            this._serialize_Arena_Material(pContext, tIORoomElement);
        }
        //position
        let tREpos = this.mNode.position.clone();
        if (this.mType === "SafetyZone") {
            tREpos = this.mNode.absolutePosition.clone();
        }
        if (this.mType === "Line" && this.mGridRE_REF != null) {
            tREpos = this.mGridRE_REF.node.absolutePosition.clone();
        }
        if (this.mType === "Floor") {
            tIORoomElement.wallIds = this.mWallIds;
        }
        if (this.mType === "BackgroundImage") {
            tIORoomElement.floorOffset = 0.002;
            tREpos.y += tIORoomElement.floorOffset;
        }
        tIORoomElement.position = serializeVector3(tREpos);
        //rotation
        let tRotation = getRotationQuaternion(this.mNode).clone();
        tRotation.normalize();
        if (!eq(tRotation.w, 1))
            tIORoomElement.rotation = serializeQuaternion(tRotation);
        if (this.mType === "SafetyZone" && this.mNode.parent != null) {
            tRotation = getRotationQuaternion(this.mNode.parent).clone();
            tRotation.normalize();
            if (!eq(tRotation.w, 1))
                tIORoomElement.rotation = serializeQuaternion(tRotation);
        }
        // grid-based line
        if (this.mType === "Line" && this.mGridRE_REF != null) {
            tRotation = getRotationQuaternion(this.mNode.parent).clone();
            tRotation.normalize();
            if (!eq(tRotation.w, 1))
                tIORoomElement.rotation = serializeQuaternion(tRotation);
        }
        this.mGeometry.getInitialParams();
        if (this.mType == "Sphere" && this.mParams.audioSphere) {
            tIORoomElement.soundId = this.mSoundSet.category + RoomManager.CATVALUE_SPLITTER + this.mSoundSet.id;
            tIORoomElement.soundVolume = this.mSoundVolume;
        }
        if (this.mType !== "Text" &&
            this.mType !== "Fence" &&
            this.mType !== "Arena" &&
            (this.mType !== "Sphere" && !this.mParams.audioSphere)) {
            this.getCAGVerticesData();
        }
        if (this.mType === "Arena") {
            this.getCAGVerticesData_Arena();
        }
        if (this.mType === "Plane_BottomTop" || (this.mType === "Sphere" && !this.mParams.audioSphere)) {
            this.getMeshVerticesDataOfMiscMesh();
        }
        if ((this.mType === "Circle" || this.mType === "Spline" || this.mType === "Line" || this.mType === "Rectangle")
            && this.mParams.terrainType !== "Flat") {
            this.getMeshVerticesDataOfMiscMesh_Hills();
        }
        if (this.mMaterialSet.category === "color" && isNotNullOrEmpty(this.mMaterialSet.value)) {
            this.mParams.materialColor = this.mMaterialSet.value;
        }
        this.mParams.material = this._getMaterialIndex();
        let tgeoData = this._serialize_Params(this.mParams);
        if (tgeoData != null) {
            tIORoomElement.geometryData = tgeoData;
        }
        return (tIORoomElement);
    }
    // tslint:disable-next-line:cyclomatic-complexity
    _serialize_Arena_Material(pContext, pIORoomElement) {
        // arena playfield
        let tmatId_arena_PlayField = this.mRoomManager.getMaterialId(this.mMaterialSet_Arena_PlayField);
        if (this.mMaterialSet_Arena_PlayField.rotation !== 0 ||
            this.mMaterialSet_Arena_PlayField.offsetU !== this.mMaterialSet_Arena_PlayField.offsetU_init ||
            this.mMaterialSet_Arena_PlayField.offsetV !== this.mMaterialSet_Arena_PlayField.offsetV_init ||
            this.mMaterialSet_Arena_PlayField.scaleU !== this.mMaterialSet_Arena_PlayField.scaleU_init ||
            this.mMaterialSet_Arena_PlayField.scaleV !== this.mMaterialSet_Arena_PlayField.scaleV_init) {
            tmatId_arena_PlayField += this.mMaterialSet_Arena_PlayField.idAddOn;
        }
        let tMatNo_arena_PlayField = pContext.addMaterial(tmatId_arena_PlayField);
        pIORoomElement.materialSet_Arena_PlayField = this.mMaterialSet_Arena_PlayField;
        // arena side segments
        let tmatId_arena_SideSegments = this.mRoomManager.getMaterialId(this.mMaterialSet_Arena_SideSegments);
        if (this.mMaterialSet_Arena_SideSegments.rotation !== 0 ||
            this.mMaterialSet_Arena_SideSegments.offsetU !== this.mMaterialSet_Arena_SideSegments.offsetU_init ||
            this.mMaterialSet_Arena_SideSegments.offsetV !== this.mMaterialSet_Arena_SideSegments.offsetV_init ||
            this.mMaterialSet_Arena_SideSegments.scaleU !== this.mMaterialSet_Arena_SideSegments.scaleU_init ||
            this.mMaterialSet_Arena_SideSegments.scaleV !== this.mMaterialSet_Arena_SideSegments.scaleV_init) {
            tmatId_arena_SideSegments += this.mMaterialSet_Arena_SideSegments.idAddOn;
        }
        let tMatNo_arena_SideSegments = pContext.addMaterial(tmatId_arena_SideSegments);
        pIORoomElement.materialSet_Arena_SideSegments = this.mMaterialSet_Arena_SideSegments;
        // arena track lines
        let tmatId_arena_TrackLines = this.mRoomManager.getMaterialId(this.mMaterialSet_Arena_TrackLines);
        if (this.mMaterialSet_Arena_TrackLines.rotation !== 0 ||
            this.mMaterialSet_Arena_TrackLines.offsetU !== this.mMaterialSet_Arena_TrackLines.offsetU_init ||
            this.mMaterialSet_Arena_TrackLines.offsetV !== this.mMaterialSet_Arena_TrackLines.offsetV_init ||
            this.mMaterialSet_Arena_TrackLines.scaleU !== this.mMaterialSet_Arena_TrackLines.scaleU_init ||
            this.mMaterialSet_Arena_TrackLines.scaleV !== this.mMaterialSet_Arena_TrackLines.scaleV_init) {
            tmatId_arena_TrackLines += this.mMaterialSet_Arena_TrackLines.idAddOn;
        }
        let tMatNo_arena_TrackLines = pContext.addMaterial(tmatId_arena_TrackLines);
        pIORoomElement.materialSet_Arena_TrackLines = this.mMaterialSet_Arena_TrackLines;
    }
    _deserialize_Arena_Material(pRoomManager, pRoomElement, pIORoomElement) {
        if (pIORoomElement.materialSet_Arena_PlayField !== undefined) {
            pRoomElement.mMaterialSet_Arena_PlayField = pIORoomElement.materialSet_Arena_PlayField;
            pRoomElement.mMaterialSet_Arena_PlayField.idAddOn = pRoomManager.materialSplitStr + pRoomElement.mIdStr;
            pRoomElement.mMaterialSet_Arena_PlayField.loaded = true;
        }
        if (pIORoomElement.materialSet_Arena_SideSegments !== undefined) {
            pRoomElement.mMaterialSet_Arena_SideSegments = pIORoomElement.materialSet_Arena_SideSegments;
            pRoomElement.mMaterialSet_Arena_SideSegments.idAddOn = pRoomManager.materialSplitStr + pRoomElement.mIdStr;
            pRoomElement.mMaterialSet_Arena_SideSegments.loaded = true;
        }
        if (pIORoomElement.materialSet_Arena_TrackLines !== undefined) {
            pRoomElement.mMaterialSet_Arena_TrackLines = pIORoomElement.materialSet_Arena_TrackLines;
            pRoomElement.mMaterialSet_Arena_TrackLines.idAddOn = pRoomManager.materialSplitStr + pRoomElement.mIdStr;
            pRoomElement.mMaterialSet_Arena_TrackLines.loaded = true;
        }
    }
    _getMaterialIndex() {
        let tRes = -1;
        let tmatId = this.mRoomManager.getMaterialId(this.mMaterialSet);
        for (let i = 0; i < this.mRoomManager.externalMaterialList.length; i++) {
            if (this.mRoomManager.externalMaterialList[i].materialValue === tmatId) {
                tRes = i;
                return (tRes);
            }
        }
        if (tRes === -1 && this.mMaterialSet.category !== "color") {
            tRes = 0;
        }
        return (tRes);
    }
    getPerimeterOfCAG(pCAG) {
        let toutLines = this.getOutlinesOfCAG(pCAG);
        let tRes = 0;
        for (let tLine of toutLines) {
            tRes += Vector3.Distance(tLine[0], tLine[1]);
        }
        this.mPerimeter = tRes;
        return (this.mPerimeter);
    }
    getPickPlane(isHill = false) {
        if (isHill && this.mParams.terrainType !== "Flat") {
            this.mPickPlane.d = -this.mParams.hillHeight;
        }
        // if (this.mchgPolyPointMode === tool.ChangePolygonPoints.Mode.changeHeight) {
        //     return (new Plane(0,1,0,0));
        // }
        return (this.mPickPlane);
    }
    getPropertyClasses() {
        return (Promise.resolve(this.mRoomManager.PropClasses));
    }
    getProperties() {
        // tslint:disable-next-line:cyclomatic-complexity
        let tPromise = super.getProperties().then((pProperties) => {
            let tupdateGeometry = true;
            let tType = this._getType();
            switch (this.mType) {
                case "Spline":
                case "Line":
                case "Rectangle":
                case "Building":
                case "CustomBuilding":
                case "SafetyZone":
                    tupdateGeometry = false;
                    break;
                default:
                    tupdateGeometry = true;
            }
            let tProperties = pProperties;
            if (tProperties == null)
                tProperties = new Array();
            let tYesNoChoices = new Array();
            for (let tYesNo of this.mRoomManager._YesNo)
                tYesNoChoices.push(this.mRoomManager._getYesNoPropValue(tYesNo));
            //area
            let tAreaProp = new StringProperty(this, RoomElement.PROP_KEY_AREA, this.mRoomManager.resources.propertyArea);
            tAreaProp.class = "base";
            let tareaConverted = this.mArea;
            switch (this.mRoomManager.app.i18n.lengthUnit) {
                case LengthUnit.Millimeter:
                    tareaConverted = tareaConverted * 1000000;
                    break;
                case LengthUnit.Centimeter:
                    tareaConverted = tareaConverted * 10000;
                    break;
                case LengthUnit.Inch:
                    tareaConverted = tareaConverted * 1550.003;
                    break;
                case LengthUnit.Feet:
                    tareaConverted = tareaConverted * 10.764;
                    break;
                case LengthUnit.Kilometer:
                    tareaConverted = tareaConverted * 0.000001;
                    break;
                default:
                    tareaConverted = tareaConverted;
            }
            let tareaVal = tareaConverted.toLocaleString(this.mRoomManager.localeString, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) +
                Util.getAreaUnitString(this.mRoomManager.app.i18n.lengthUnit);
            tAreaProp.getValue = () => new PropertyValue(tareaVal);
            if (this.mType === "BackgroundImage" || this.mType === "Floor" || this.mType === "PillarB" || this.mType === "PillarC" || this.mType === "Text"
                || this.mType === "Building" || this.mType === "CustomBuilding" || this.mType === "Fence") {
                tAreaProp.visible = false;
            }
            else {
                tAreaProp.visible = false;
                tAreaProp.editable = false;
            }
            if (tAreaProp.visible) {
                tProperties.push(tAreaProp);
            }
            //perimeter
            let tPerimeterProp = new LengthProperty(this, RoomElement.PROP_KEY_PERIMETER, this.mRoomManager.resources.propertyPerimeter);
            tPerimeterProp.class = "base";
            tPerimeterProp.getValue = () => new PropertyValue(this.mPerimeter);
            if (this.mType === "BackgroundImage" || this.mType === "Floor" || this.mType === "PillarB" || this.mType === "PillarC" || this.mType === "Text"
                || this.mType === "Building" || this.mType === "CustomBuilding" || this.mType === "Fence") {
                tPerimeterProp.visible = false;
            }
            else {
                tPerimeterProp.visible = false;
                tPerimeterProp.editable = false;
            }
            if (tPerimeterProp.visible) {
                tProperties.push(tPerimeterProp);
            }
            // label
            let tRELabelProp = new StringProperty(this, RoomElement.PROP_KEY_LABEL, this.mRoomManager.resources.propertyLabel);
            tRELabelProp.class = "base";
            tRELabelProp.getValue = () => new PropertyValue(this.mLabel);
            tRELabelProp.setValue = (pValue) => {
                let tValue = StringProperty.ConvertValueToString(pValue);
                if (tValue != null) {
                    this.setRELabel(tValue);
                    return (Promise.resolve(PropertyChangedResult.Value));
                }
                return (Promise.resolve(PropertyChangedResult.Nothing));
            };
            switch (this.mType) {
                case "Spline":
                case "Line":
                case "Rectangle":
                case "Circle":
                case "SafetyZone":
                    tRELabelProp.visible = true;
                    break;
                default:
                    tRELabelProp.visible = false;
                    break;
            }
            if (tRELabelProp.visible) {
                tProperties.push(tRELabelProp);
            }
            //length
            let tLengthProp = new LengthProperty(this, RoomElement.PROP_KEY_LENGTH, this.mRoomManager.resources.propertyLength);
            tLengthProp.class = "base";
            tLengthProp.getValue = () => new PropertyValue(this.mLength);
            if (this.mType === "Fence" || this.mType === "Hedge" || this.mType === "Road") {
                tLengthProp.visible = true;
                tLengthProp.editable = false;
            }
            else {
                tLengthProp.visible = false;
            }
            if (tLengthProp.visible) {
                tProperties.push(tLengthProp);
            }
            //width
            let tWidth = this.mWidth;
            let tWidthProp = new LengthProperty(this, RoomElement.PROP_KEY_WIDTH, this.mRoomManager.resources.propertyWidth);
            tWidthProp.class = "base";
            if (this.mType === "Circle") {
                tWidth = this.mWidth * 0.5;
                tWidthProp.key = RoomElement.PROP_KEY_RADIUS;
                tWidthProp.name = this.mRoomManager.resources.propertyRadius;
            }
            tWidthProp.getValue = () => new PropertyValue(tWidth);
            tWidthProp.setValue = (pValue) => {
                let tValue = LengthProperty.ConvertValueToNumber(pValue);
                if (tValue != null && this.setWidth(tValue, tupdateGeometry)) {
                    return (Promise.resolve(PropertyChangedResult.Value));
                }
                return (Promise.resolve(PropertyChangedResult.Nothing));
            };
            if (this.mType === "PillarB" || this.mType === "PillarC" || this.mType === "BackgroundImage" || this.type === "Rectangle" || this.mType === "Circle")
                tWidthProp.visible = true;
            else
                tWidthProp.visible = false;
            if (tWidthProp.visible) {
                tProperties.push(tWidthProp);
            }
            //depth
            let tDepth = this.mDepth;
            let tDepthProp = new LengthProperty(this, RoomElement.PROP_KEY_DEPTH, this.mRoomManager.resources.propertyDepth);
            tDepthProp.class = "base";
            tDepthProp.getValue = () => new PropertyValue(tDepth);
            tDepthProp.setValue = (pValue) => {
                let tValue = LengthProperty.ConvertValueToNumber(pValue);
                if (tValue != null && this.setDepth(tValue, tupdateGeometry)) {
                    return (Promise.resolve(PropertyChangedResult.Value));
                }
                return (Promise.resolve(PropertyChangedResult.Nothing));
            };
            if (this.mType === "Rectangle")
                tDepthProp.visible = true;
            else
                tDepthProp.visible = false;
            if (tDepthProp.visible) {
                tProperties.push(tDepthProp);
            }
            //set curved way
            let tcurvedWayProp = new BoolProperty(this, RoomElement.PROP_KEY_CURVED, this.mRoomManager.resources.propertyCurvedLine);
            tcurvedWayProp.choiceList = true;
            tcurvedWayProp.getChoices = () => Promise.resolve(tYesNoChoices);
            if (this.mType === "Road")
                tcurvedWayProp.getValue = () => this.mRoomManager._getYesNoPropValue(this.mcurvedRoad);
            if (this.mType === "Hedge")
                tcurvedWayProp.getValue = () => this.mRoomManager._getYesNoPropValue(this.mcurvedHedge);
            tcurvedWayProp.setValue = (pValue) => {
                if (pValue != null) {
                    let tval = BoolProperty.ConvertValueToBool(pValue);
                    if (this.mType === "Road")
                        this.setCurvedRoad(tval);
                    if (this.mType === "Hedge")
                        this.setCurvedHedge(tval);
                    return (Promise.resolve(PropertyChangedResult.Value));
                }
                return (Promise.resolve(PropertyChangedResult.Nothing));
            };
            if (this.mType === "Road" || this.mType === "Hedge") {
                tcurvedWayProp.visible = true;
            }
            else {
                tcurvedWayProp.visible = false;
            }
            if (this.mRoomManager.ProjectId !== "001")
                tcurvedWayProp.visible = false;
            if (tcurvedWayProp.visible) {
                tProperties.push(tcurvedWayProp);
            }
            //height
            let tHeightProp = new LengthProperty(this, RoomElement.PROP_KEY_Height, this.mRoomManager.resources.propertyHeight);
            tHeightProp.class = "base";
            tHeightProp.getValue = () => new PropertyValue(this.mHeight);
            tHeightProp.setValue = (pValue) => {
                let tValue = LengthProperty.ConvertValueToNumber(pValue);
                if (tValue != null && this.setHeight(tValue))
                    return (Promise.resolve(PropertyChangedResult.Value));
                return (Promise.resolve(PropertyChangedResult.Nothing));
            };
            if (this.mType === "PillarB" || this.mType === "PillarC" || this.mType === "BackgroundImage" ||
                this.mType === "CustomBuilding" || this.mType === "Fence") {
                tHeightProp.visible = true;
                tProperties.push(tHeightProp);
            }
            //mesh opacity
            const talphaProp = new NumberProperty(this, RoomElement.PROP_KEY_Opacity, this.mRoomManager.resources.propertyOpacity);
            talphaProp.class = "base";
            talphaProp.getValue = () => new PropertyValue(this.mBuildingOpacity);
            talphaProp.setValue = (pValue) => {
                let tValue = NumberProperty.ConvertValueToNumber(pValue);
                if (tValue != null) {
                    this.setBuildingOpacity(tValue);
                    return (Promise.resolve(PropertyChangedResult.Value));
                }
                return (Promise.resolve(PropertyChangedResult.Nothing));
            };
            if (this.mType === "CustomBuilding")
                talphaProp.visible = true;
            else
                talphaProp.visible = false;
            if (talphaProp.visible) {
                tProperties.push(talphaProp);
            }
            //add geometry properties
            let tGeomProps = this.mGeometry.getProperties();
            if (tGeomProps != null) {
                for (let tProp of tGeomProps)
                    tProperties.push(tProp);
            }
            if (this.mType === "Text" || this.mType === "BackgroundImage")
                return (tProperties);
            // terrain type
            let tTerrainTypeChoices = this.mRoomManager.TerrainTypePropList;
            const tterrainTypeProp = new StringProperty(this, RoomElement.PROP_KEY_TERRAIN_Type, this.mRoomManager.resources.propertyTerrain_Type);
            tterrainTypeProp.class = "terrain";
            tterrainTypeProp.getValue = () => {
                return new PropertyValue(this.mParams.terrainType);
            };
            tterrainTypeProp.setValue = (pval) => {
                if (!pval)
                    return Promise.resolve(PropertyChangedResult.Nothing);
                const tString = StringProperty.ConvertValueToString(pval);
                if (tString) {
                    this.setTerrainType(tString);
                    return Promise.resolve(PropertyChangedResult.Value);
                }
                return Promise.resolve(PropertyChangedResult.Nothing);
            };
            tterrainTypeProp.choiceList = true;
            // tterrainTypeProp.getChoices = () => Promise.resolve(["Flat", "Bottom", "Top"].map(x => new PropertyValue(x)));
            tterrainTypeProp.getChoices = () => Promise.resolve(tTerrainTypeChoices);
            tterrainTypeProp.visible = false;
            if (this.mType === "Spline" ||
                (this.mType === "Line" && this.mGridRE_REF == null)
                || this.mType === "Rectangle" || this.mType === "Circle") {
                tterrainTypeProp.visible = true;
            }
            if (tterrainTypeProp.visible) {
                tProperties.push(tterrainTypeProp);
            }
            // hill height
            const tHillHeightProp = new LengthProperty(this, RoomElement.PROP_KEY_TERRAIN_HillHeight, this.mRoomManager.resources.propertyTerrain_HillHeight);
            tHillHeightProp.class = "terrain";
            tHillHeightProp.getValue = () => {
                return new PropertyValue(this.mParams.hillHeight);
            };
            tHillHeightProp.setValue = (pval) => {
                if (!pval)
                    return Promise.resolve(PropertyChangedResult.Nothing);
                const thillHeight = LengthProperty.ConvertValueToNumber(pval);
                if (thillHeight) {
                    this.setHillHeight(thillHeight);
                    return Promise.resolve(PropertyChangedResult.Value);
                }
                return Promise.resolve(PropertyChangedResult.Nothing);
            };
            tHillHeightProp.visible = false;
            if ((this.mType === "Spline" || this.mType === "Line" || this.mType === "Rectangle" || this.mType === "Circle") &&
                (this.mParams.terrainType !== "Flat")) {
                tHillHeightProp.visible = true;
            }
            if (tHillHeightProp.visible) {
                tProperties.push(tHillHeightProp);
            }
            // hill offset
            const tHillOffsetProp = new LengthProperty(this, RoomElement.PROP_KEY_TERRAIN_HillOffset, this.mRoomManager.resources.propertyTerrain_HillOffset);
            tHillOffsetProp.class = "terrain";
            tHillOffsetProp.getValue = () => {
                return new PropertyValue(this.mParams.hillOffset);
            };
            tHillOffsetProp.setValue = (pValue) => {
                let tValue = LengthProperty.ConvertValueToNumber(pValue);
                if (tValue != null && this.setHillOffset(tValue)) {
                    return (Promise.resolve(PropertyChangedResult.Value));
                }
                return (Promise.resolve(PropertyChangedResult.Nothing));
            };
            tHillOffsetProp.visible = false;
            if ((this.mType === "Spline" || this.mType === "Line" || this.mType === "Rectangle" || this.mType === "Circle") &&
                (this.mParams.terrainType !== "Flat")) {
                tHillOffsetProp.visible = true;
            }
            if (tHillOffsetProp.visible) {
                tProperties.push(tHillOffsetProp);
            }
            //hill profile
            let tHillProfileChoices = this.mRoomManager.HillProfilePropList;
            const thillProfileProp = new StringProperty(this, RoomElement.PROP_KEY_TERRAIN_HillProfile, this.mRoomManager.resources.propertyTerrain_HillProfile);
            thillProfileProp.class = "terrain";
            thillProfileProp.getValue = () => this.mRoomManager.getHillProfilePropValue(this.mParams.hillProfile);
            thillProfileProp.setValue = (pval) => {
                if (!pval) {
                    return Promise.resolve(PropertyChangedResult.Nothing);
                }
                const tString = StringProperty.ConvertValueToString(pval);
                if (tString) {
                    this.setHillProfile(tString);
                    return Promise.resolve(PropertyChangedResult.Value);
                }
                return Promise.resolve(PropertyChangedResult.Nothing);
            };
            // thillProfileProp.getChoices = () => Promise.resolve(["Hard", "Medium", "Soft"].map(x => new PropertyValue(x)));
            thillProfileProp.getChoices = () => Promise.resolve(tHillProfileChoices);
            thillProfileProp.choiceList = true;
            thillProfileProp.visible = false;
            if ((this.mType === "Spline" || this.mType === "Line" || this.mType === "Rectangle" || this.mType === "Circle") &&
                (this.mParams.terrainType !== "Flat")) {
                thillProfileProp.visible = true;
            }
            if (thillProfileProp.visible) {
                tProperties.push(thillProfileProp);
            }
            //material category
            if (this.mType === "PillarB" || this.mType === "PillarC") {
                tType = "pillar";
            }
            if (this.mType === "Line" && this.mGridRE_REF != null) {
                tType = "grassmat";
            }
            let tMaterialCategory = this.mRoomManager.getMaterial_Categories(tType);
            let tMaterialCatProp = new NumberProperty(this, RoomElement.PROP_KEY_MATERIALCATEGORY, this.mRoomManager.resources.propertyMaterialCategory);
            tMaterialCatProp.class = "material";
            tMaterialCatProp.choiceList = true;
            tMaterialCatProp.getChoices = () => Promise.resolve(tMaterialCategory);
            tMaterialCatProp.getValue = () => this.getMaterialCategoryValue(tMaterialCategory);
            tMaterialCatProp.setValue = (pValue) => {
                if (pValue != null) {
                    this.setMaterialCategory(StringProperty.ConvertValueToString(pValue), undefined, undefined, this.mMaterialSet);
                    return (Promise.resolve(PropertyChangedResult.Value));
                }
                return (Promise.resolve(PropertyChangedResult.Nothing));
            };
            if (this.mType === "Spline" || this.mType === "Line" || this.mType === "Rectangle" || this.mType === "Circle" ||
                this.mType === "Road" || this.mType === "Floor" || this.mType === "PillarB" || this.mType === "PillarC" || this.mType === "Hedge" ||
                this.mType === "CustomBuilding" || this.mType === "SafetyZone")
                tMaterialCatProp.visible = true;
            else
                tMaterialCatProp.visible = false;
            if (tMaterialCatProp.visible) {
                tProperties.push(tMaterialCatProp);
            }
            //material value
            let tMaterialChoices = this.mRoomManager.getMaterialsOfCategory(this.mMaterialSet, tType);
            let tcatName = this.mMaterialSet.category === "color" ? this.mRoomManager.resources.propertyColor : this.mRoomManager.resources.propertyMaterial;
            let tMaterialProp;
            if (this.mMaterialSet.category === "color") {
                tMaterialProp = new Color3Property(this, RoomElement.PROP_KEY_MATERIAL, tcatName);
            }
            else {
                tMaterialProp = new StringProperty(this, RoomElement.PROP_KEY_MATERIAL, tcatName);
            }
            tMaterialProp.class = "material";
            tMaterialProp.choiceList = true;
            tMaterialProp.getChoices = () => Promise.resolve(tMaterialChoices);
            tMaterialProp.getValue = () => this.mRoomManager.getMaterialValue(this.mMaterialSet, tMaterialChoices);
            tMaterialProp.setValue = (pValue) => {
                let tValue = StringProperty.ConvertValueToString(pValue);
                if (tValue != null) {
                    this.setMaterialValue__(tValue, this.mMaterialSet, true);
                    return (Promise.resolve(PropertyChangedResult.Value));
                }
                return (Promise.resolve(PropertyChangedResult.Nothing));
            };
            if (this.mType === "Spline" || this.mType === "Line" || this.mType === "Rectangle" || this.mType === "Circle" ||
                this.mType === "Road" || this.mType === "Floor" || this.mType === "Hedge" ||
                this.mType === "PillarB" || this.mType === "PillarC" || this.mType === "CustomBuilding" ||
                this.mType === "SafetyZone" ||
                (this.mType === "Sphere" && this.mParams.audioSphere))
                tMaterialProp.visible = true;
            else
                tMaterialProp.visible = false;
            if (tMaterialProp.visible) {
                tProperties.push(tMaterialProp);
            }
            // grid visibility
            if (this.mType === "Line" && this.GridRE_REF != null) {
                let tgridVisibleProp = new BoolProperty(this, RoomElement.PROP_KEY_GRIDVISIBILITY, 'Grid visible');
                tgridVisibleProp.class = "additional";
                tgridVisibleProp.choiceList = true;
                tgridVisibleProp.getChoices = () => Promise.resolve(tYesNoChoices);
                tgridVisibleProp.getValue = () => this.mRoomManager._getYesNoPropValue(this.mGridRE_REF.m2dGroup.isEnabled());
                tgridVisibleProp.setValue = (pValue) => {
                    if (pValue != null) {
                        let tval = BoolProperty.ConvertValueToBool(pValue);
                        this.mGridRE_REF.m2dGroup.setEnabled(tval);
                        this.updateGeometry(true, false);
                        this.mRoomManager.cutOverlappingShapes();
                        this.mRoomManager.app.model.setSelection([this.mGridRE_REF]);
                        this.mRoomManager.app.model.setSubElementSelection(this.mGridRE_REF, this);
                        this.notifyChange(ElementChangedFlags.Properties);
                        this.notifyChange(ElementChangedFlags.PropertyValues);
                        return (Promise.resolve(PropertyChangedResult.Value));
                    }
                    return (Promise.resolve(PropertyChangedResult.Nothing));
                };
                tgridVisibleProp.visible = true;
                if (tgridVisibleProp.visible) {
                    tProperties.push(tgridVisibleProp);
                }
            }
            //set on site
            let tOnSiteProp = new BoolProperty(this, RoomElement.PROP_KEY_ONSITE, this.mRoomManager.resources.propertyOnSite);
            tOnSiteProp.class = "additional";
            tOnSiteProp.choiceList = true;
            tOnSiteProp.getChoices = () => Promise.resolve(tYesNoChoices);
            tOnSiteProp.getValue = () => this.mRoomManager._getYesNoPropValue(this.mOnSite);
            tOnSiteProp.setValue = (pValue) => {
                if (pValue != null) {
                    let tval = BoolProperty.ConvertValueToBool(pValue);
                    this.setOnSite(tval);
                    return (Promise.resolve(PropertyChangedResult.Value));
                }
                return (Promise.resolve(PropertyChangedResult.Nothing));
            };
            if (this.mType === "Spline" || this.mType === "Line" || this.mType === "Rectangle" || this.mType === "Circle" ||
                this.mType === "SafetyZone" || this.mType === "Road" || this.mType === "Arena")
                tOnSiteProp.visible = true;
            else
                tOnSiteProp.visible = false;
            if (this.mParams.terrainType !== "Flat") {
                tOnSiteProp.editable = false;
            }
            if (this.mRoomManager.ProjectId !== "001")
                tOnSiteProp.visible = false;
            if (tOnSiteProp.visible) {
                tProperties.push(tOnSiteProp);
            }
            //triangle ref point
            let tCrossHairProp = new BoolProperty(this, RoomElement.PROP_KEY_CROSSHAIR, this.mRoomManager.resources.propertyCrossHair);
            tCrossHairProp.choiceList = true;
            tCrossHairProp.getChoices = () => Promise.resolve(tYesNoChoices);
            tCrossHairProp.getValue = () => this.mRoomManager._getYesNoPropValue(this.mShowCrossHair);
            tCrossHairProp.setValue = (pValue) => {
                if (pValue != null) {
                    let tval = BoolProperty.ConvertValueToBool(pValue);
                    this.setCrossHairVisibility(tval);
                    this.mRoomManager.cutOverlappingShapes();
                    return (Promise.resolve(PropertyChangedResult.Value));
                }
                return (Promise.resolve(PropertyChangedResult.Nothing));
            };
            tCrossHairProp.visible = false;
            if ((this.mType === "Line" && this.mGridRE_REF == null) || this.mType === "Spline" || this.mType === "Rectangle") {
                tCrossHairProp.visible = true;
            }
            if (this.mRoomManager.ProjectId !== "001")
                tCrossHairProp.visible = false;
            if (tCrossHairProp.visible) {
                tProperties.push(tCrossHairProp);
            }
            //texture rotation
            let tTextureRotationRProp = new NumberProperty(this, RoomElement.PROP_KEY_ROTATE_TEXTURE, this.mRoomManager.resources.propertyRotationAngle);
            tTextureRotationRProp.class = "material";
            tTextureRotationRProp.minValue = 0;
            tTextureRotationRProp.maxValue = 360;
            tTextureRotationRProp.numDigits = 3;
            tTextureRotationRProp.numDecDigits = 0;
            tTextureRotationRProp.getValue = () => new PropertyValue(this.mMaterialSet.rotation);
            tTextureRotationRProp.setValue = (pValue) => {
                let tValue = NumberProperty.ConvertValueToNumber(pValue);
                if (tValue != null) {
                    this.changeTextureRotation(this.mMaterialSet, tValue);
                    return (Promise.resolve(PropertyChangedResult.Value));
                }
                return (Promise.resolve(PropertyChangedResult.Nothing));
            };
            tTextureRotationRProp.visible = false;
            if (this.mType === "CustomBuilding" && this.mMaterialSet.category !== "color") {
                tTextureRotationRProp.visible = true;
            }
            if (tTextureRotationRProp.visible) {
                tProperties.push(tTextureRotationRProp);
            }
            //texture offset u
            let tpropuOffset = new LengthProperty(this, RoomElement.PROP_KEY_TEXTURE_OFFSETU, this.mRoomManager.resources.propertyTextureOffsetU);
            tpropuOffset.class = "material";
            tpropuOffset.minValue = -5;
            tpropuOffset.maxValue = 5;
            tpropuOffset.numDigits = 5;
            tpropuOffset.numDecDigits = 3;
            tpropuOffset.getValue = () => this.getTextureOffsetU();
            tpropuOffset.setValue = (pValue) => {
                let tValue = LengthProperty.ConvertValueToNumber(pValue);
                if (tValue != null) {
                    this.changeTextureOffset_u(this.mMaterialSet, tValue);
                    return (Promise.resolve(PropertyChangedResult.Value));
                }
                return (Promise.resolve(PropertyChangedResult.Nothing));
            };
            tpropuOffset.visible = false;
            if (this.mType === "CustomBuilding" && this.mMaterialSet.category !== "color") {
                tpropuOffset.visible = true;
            }
            if (tpropuOffset.visible) {
                tProperties.push(tpropuOffset);
            }
            //texture offset v
            let toffV = this.mMaterialSet.offsetV;
            if (toffV !== 0) {
                toffV *= -1;
            }
            let tpropvOffset = new LengthProperty(this, RoomElement.PROP_KEY_TEXTURE_OFFSETV, this.mRoomManager.resources.propertyTextureOffsetV);
            tpropvOffset.class = "material";
            tpropvOffset.minValue = -5;
            tpropvOffset.maxValue = 5;
            tpropvOffset.numDigits = 5;
            tpropvOffset.numDecDigits = 3;
            tpropvOffset.getValue = () => this.getTextureOffsetV();
            tpropvOffset.setValue = (pValue) => {
                let tValue = LengthProperty.ConvertValueToNumber(pValue);
                if (tValue != null) {
                    this.changeTextureOffset_v(this.mMaterialSet, tValue);
                    return (Promise.resolve(PropertyChangedResult.Value));
                }
                return (Promise.resolve(PropertyChangedResult.Nothing));
            };
            tpropvOffset.visible = false;
            if (this.mType === "CustomBuilding" && this.mMaterialSet.category !== "color") {
                tpropvOffset.visible = true;
            }
            if (tpropvOffset.visible) {
                tProperties.push(tpropvOffset);
            }
            //texture scale u
            let tpropvScaleU = new NumberProperty(this, RoomElement.PROP_KEY_TEXTURE_SCALEU, this.mRoomManager.resources.propertyTextureScaleU);
            tpropvScaleU.class = "material";
            tpropvScaleU.minValue = -10;
            tpropvScaleU.maxValue = 10;
            tpropvScaleU.numDigits = 5;
            tpropvScaleU.numDecDigits = 3;
            tpropvScaleU.getValue = () => new PropertyValue(this.mMaterialSet.scaleU);
            tpropvScaleU.setValue = (pValue) => {
                let tValue = NumberProperty.ConvertValueToNumber(pValue);
                if (tValue != null) {
                    this.changeTextureScale_U(this.mMaterialSet, tValue);
                    return (Promise.resolve(PropertyChangedResult.Value));
                }
                return (Promise.resolve(PropertyChangedResult.Nothing));
            };
            tpropvScaleU.visible = false;
            if (this.mType === "CustomBuilding" && this.mMaterialSet.category !== "color") {
                tpropvScaleU.visible = true;
            }
            if (tpropvScaleU.visible) {
                tProperties.push(tpropvScaleU);
            }
            //texture scale v
            let tpropvScaleV = new NumberProperty(this, RoomElement.PROP_KEY_TEXTURE_SCALEV, this.mRoomManager.resources.propertyTextureScaleV);
            tpropvScaleV.class = "material";
            tpropvScaleV.minValue = -10;
            tpropvScaleV.maxValue = 10;
            tpropvScaleV.numDigits = 5;
            tpropvScaleV.numDecDigits = 3;
            tpropvScaleV.getValue = () => new PropertyValue(this.mMaterialSet.scaleV);
            tpropvScaleV.setValue = (pValue) => {
                let tValue = NumberProperty.ConvertValueToNumber(pValue);
                if (tValue != null) {
                    this.changeTextureScale_V(this.mMaterialSet, tValue);
                    return (Promise.resolve(PropertyChangedResult.Value));
                }
                return (Promise.resolve(PropertyChangedResult.Nothing));
            };
            tpropvScaleV.visible = false;
            if (this.mType === "CustomBuilding" && this.mMaterialSet.category !== "color") {
                tpropvScaleV.visible = true;
            }
            if (tpropvScaleV.visible) {
                tProperties.push(tpropvScaleV);
            }
            return (tProperties);
        });
        return (tPromise);
    }
    setRE_reference(pRE) {
        this.mRE_reference = pRE;
        if (this.mType === "Plane_BottomTop") {
            this.m3dGroup.parent = this.mRE_reference.node;
            this.m2dGroup.parent = this.mRE_reference.node;
        }
    }
    setInitialGeoPosition(pLatitude, pLongitude) {
        if (this.mType !== "GeoMarker") {
            return;
        }
        this.mInitGeo_Latitude = pLatitude;
        this.mInitGeo_Longitude = pLongitude;
        this.setGeoPosition(pLatitude, pLongitude);
    }
    setGeoMapRotation(pDegree) {
        if (this.mType !== "GeoMarker") {
            return;
        }
        this.mInitGeo_MapRotation = pDegree;
    }
    setGeoPosition(pLatitude, pLongitude) {
        if (this.mType !== "GeoMarker") {
            return;
        }
        this.mgeo_Latitude = pLatitude;
        this.mgeo_Longitude = pLongitude;
        this.updateGeometry();
    }
    setGridRE_REF(pGrid) {
        this.mGridRE_REF = pGrid;
    }
    setGridCellSize(pSize) {
        this.mgridCellSize = pSize;
        this.updateGeometry(true, false);
    }
    setREforGrid_REF(pRoomElements) {
        if (pRoomElements != null && pRoomElements.length > 0) {
            for (const tRE of pRoomElements) {
                if (!contains(this.mREforGrid_REF, tRE)) {
                    this.mREforGrid_REF.push(tRE);
                }
            }
        }
        else {
            this.mREforGrid_REF = [];
        }
    }
    removeREFromGrid_REF(pRoomElements) {
        if (pRoomElements != null && pRoomElements.length > 0) {
            const tREToRemove = [];
            for (const tRE of pRoomElements) {
                if (contains(this.mREforGrid_REF, tRE)) {
                    removeElement(this.mREforGrid_REF, tRE);
                }
            }
        }
        else {
            this.mREforGrid_REF = [];
        }
    }
    setRE_DummyMesh(pMesh) {
        this.mDummyMesh = pMesh;
    }
    setMainElement(pMainElement) {
        this.mMainElement = pMainElement;
    }
    setRE_orderIndex(pOrderIndex) {
        this.mOrderIndex = pOrderIndex;
    }
    setMesh2d(pMesh) {
        this.mdrawnMesh2d = pMesh;
    }
    setMesh3d(pMesh) {
        this.mdrawnMesh3d = pMesh;
    }
    // tslint:disable-next-line:cyclomatic-complexity
    setMeshVisibility(pOpacity, ponlySetValue = false) {
        let tOpacity = pOpacity;
        if (this.mType === "Sphere" && this.mParams.audioSphere) {
            tOpacity = 0.2;
        }
        if (this.mType === "Text") {
            return;
        }
        if (this.mType === "Grid") {
            this.setMeshVisibility_Grid(pOpacity, ponlySetValue);
            return;
        }
        if (this.mType === "CustomBuilding" && this.mRE_reference !== null) {
            this.mRE_reference.setMeshVisibility(tOpacity, ponlySetValue);
        }
        this.mGroupMeshOpacity = tOpacity;
        let trenderingGroupId = tOpacity === 1 ? 0 : 1;
        if (ponlySetValue)
            return;
        let t3dMeshes = this.m3dGroup.getChildMeshes(true);
        for (let tMesh of t3dMeshes) {
            if (tMesh.name.indexOf("pickingMesh") === 0)
                continue;
            if (this.mType === "SafetyZone") {
                tMesh.visibility = this.mSZ_visible ? 1 : 0;
            }
            else {
                tMesh.visibility = tOpacity;
            }
            if (this.mType === "Sphere" && this.mParams.audioSphere) {
                ShadowGenerator.disableMeshShadow(tMesh);
            }
            tMesh.renderingGroupId = trenderingGroupId;
        }
        let t2dMeshes = this.m2dGroup.getChildMeshes(true);
        for (let tMesh of t2dMeshes) {
            if (tMesh.name.indexOf("pickingMesh") === 0)
                continue;
            if (this.mType === "SafetyZone") {
                tMesh.visibility = this.mSZ_visible ? 1 : 0;
            }
            else {
                tMesh.visibility = tOpacity;
            }
            if (this.mType === "Sphere" && this.mParams.audioSphere) {
                ShadowGenerator.disableMeshShadow(tMesh);
            }
            tMesh.renderingGroupId = trenderingGroupId;
        }
    }
    setCSG(pCSG) {
        this.mCSG = pCSG;
    }
    setCAG(pCAG) {
        this.mCAG = pCAG;
    }
    setCAG_Arena_PlayField(pCAG) {
        this.mCAG_Arena_InField = pCAG;
    }
    setCAG_Arena_SideSegments(pCAG) {
        this.mCAG_Arena_SideSegments = pCAG;
    }
    setCAG_Arena_RunningTracks(pCAG) {
        this.mCAG_Arena_RunningsTracks = pCAG;
    }
    setCAG_Arena_TrackLines(pCAG) {
        this.mCAG_Arena_TrackLines = pCAG;
    }
    setCAG_notVisible(pCAG) {
        this.mCAG_notVisible = pCAG;
    }
    setCAG_notVisible_Arena_InField(pCAG) {
        this.mCAG_notVisible_Arena_InField = pCAG;
    }
    setCAG_notVisible_Arena_SideSegments(pCAG) {
        this.mCAG_notVisible_Arena_SideSegments = pCAG;
    }
    setCAG_notVisible_Arena_TrackLines(pCAG) {
        this.mCAG_notVisible_Arena_TrackLines = pCAG;
    }
    setCAG_notVisible_Arena_RunningTracks(pCAG) {
        this.mCAG_notVisible_Arena_RunningTracks = pCAG;
    }
    setCAG_FS(pCAG) {
        this.mCAG_freespace = pCAG;
    }
    clearCutCAG() {
        this.mCutCAG = [];
    }
    clearDeletedCAG() {
        this.mDeletedCAG = [];
    }
    setCAGoriginal(pCAG) {
        this.mCAGoriginal = pCAG;
        // this.setREDimensionFromCAG();
    }
    setCAGoriginal__Arena_InField(pCAG) {
        this.mCAGoriginal_Arena_InField = pCAG;
        // this.setREDimensionFromCAG();
    }
    setCAGoriginal__Arena_SideSegements(pCAG) {
        this.mCAGoriginal_Arena_SideSegments = pCAG;
        // this.setREDimensionFromCAG();
    }
    setCAGoriginal__Arena_RunningTracks(pCAG) {
        this.mCAGoriginal_Arena_RunningsTracks = pCAG;
        // this.setREDimensionFromCAG();
    }
    setCAGoriginal__Arena_TrackLines(pCAG) {
        this.mCAGoriginal_Arena_TrackLines = pCAG;
        // this.setREDimensionFromCAG();
    }
    setBasketId(pBasketId) {
        this.mBasketId = pBasketId;
    }
    setREId(pId) {
        this.mId = pId;
    }
    setOverlapped(pOverlapped) {
        this.mOverlapped = pOverlapped;
    }
    setInteractorPoints(pInteractors) {
        this.mInteractorPoints = [];
        this.mInteractorPoints = pInteractors;
    }
    setInteractorVisible(pVisible) {
        this.mInteractorsVisible = pVisible;
    }
    setChgPointMode(pMode) {
        this.mchgPolyPointMode = pMode;
    }
    getChgPointMode() {
        return (this.mchgPolyPointMode);
    }
    setPickPlane(pPickPlane) {
        this.mPickPlane = pPickPlane;
    }
    setARStartPoint(pPoint) {
        this.mARStartPoint = pPoint;
    }
    setARStartPointSet(pSet) {
        this.mARStartPointSet = pSet;
    }
    boundingBoxVisibility(pVisible) {
        if (this.mdrawnMesh2d != null)
            this.mdrawnMesh2d.showBoundingBox = pVisible;
        if (this.mdrawnMesh3d != null)
            this.mdrawnMesh3d.showBoundingBox = pVisible;
    }
    setArea(pArea) {
        switch (this.mType) {
            case "Circle":
                if (this.mCAG === this.mCAGoriginal) {
                    const tradius = Number((this.mWidth * 0.5).toFixed(2));
                    this.mArea = Math.PI * tradius * tradius;
                }
                else {
                    this.mArea = pArea;
                }
                break;
            case "Rectangle":
                if (this.mCAG === this.mCAGoriginal) {
                    this.mArea = Number(this.mWidth.toFixed(2)) * Number(this.mDepth.toFixed(2));
                }
                else {
                    this.mArea = pArea;
                }
                break;
            default:
                this.mArea = pArea;
                break;
        }
    }
    setClosedPolygon(pClosed) {
        this.mClosedPolygon = pClosed;
    }
    setCustomMin(pminVec) {
        this.mCustomMin = pminVec;
    }
    setCustomMax(pmaxVec) {
        this.mCustomMax = pmaxVec;
    }
    setPerimeter(pPerimeter) {
        this.mPerimeter = pPerimeter;
    }
    setMeshBoundingBox(pBounds) {
        this.mMeshBounds = pBounds;
    }
    /**
     * Set opacity of mesh to show.
     * Values: 0 - 1
     */
    setBuildingOpacity(pValue, pForce = false, pSetValue = false) {
        if (pSetValue) {
            this.mBuildingOpacity = pValue;
            return;
        }
        let tValue = clamp(pValue, 0.1, 1);
        if ((!pForce) && (eq(this.mBuildingOpacity, tValue)))
            return;
        this.mBuildingOpacity = tValue;
        this.beginChange();
        this.updateGeometry();
        this.endChange();
        this.notifyChange(ElementChangedFlags.PropertyValues);
        return;
    }
    setInitialPosition(pInitialPosition) {
        this.mInitalPosition = pInitialPosition.clone();
        this.setPosition(pInitialPosition);
        return (true);
    }
    setPosition(pNewPos, pNotifyWall = true) {
        if (pNewPos == null)
            return (false);
        this.mPosition = pNewPos;
        this.updateMatrix();
        this.updateGeoPosition();
        return (true);
    }
    setWallIds(pWallIds) {
        this.mWallIds = pWallIds;
    }
    setWallSidePolygon(pPolygon) {
        this.mWallSidePolygon = pPolygon;
    }
    setParentWall(pWall) {
        this.mParentWall = pWall;
    }
    updateFloorReference() {
        if (this.mType !== "Floor")
            return;
        let tfloorEntry = this.mRoomManager.FloorsPolygons.get(this.mWallIds);
        if (tfloorEntry !== undefined) {
            tfloorEntry.transparent = this.mParams.floorTransparent;
        }
    }
    attachToWall(pWall, pInsertPos) {
        if (pWall == null || pWall === this.mWall)
            return;
        if (this.mWall != null) {
            this.mWall._removeRoomElement(this);
            this.mWall = null;
        }
        this.mWall = pWall;
        if (pInsertPos != null)
            this.setPosition(Util.to3d(pInsertPos), false);
        this.mWall._addRoomElement(this);
    }
    updateAttachedPosition(pNotifyWall = true) {
        if (this.mNode == null || this.mWall == null || this.mWallAttachData == null)
            return;
        let tWallLength = Vector2.Distance(this.mWall.startPoint, this.mWall.endPoint);
        let tWallDir = this.mWall.endPoint.subtract(this.mWall.startPoint).normalize();
        if (!this.mWall.startPoint.equalsWithEpsilon(this.mWallAttachData.start, EPSILON)) {
            if (this.mWallAttachData.distanceEnd > tWallLength)
                this.mWallAttachData.distanceEnd = tWallLength;
            tWallDir.scaleInPlace(this.mWallAttachData.distanceEnd);
            this.mPosition = Util.to3d(this.mWall.endPoint.subtract(tWallDir));
        }
        else {
            if (this.mWallAttachData.distanceStart > tWallLength)
                this.mWallAttachData.distanceStart = tWallLength;
            tWallDir.scaleInPlace(this.mWallAttachData.distanceStart);
            this.mPosition = Util.to3d(this.mWall.startPoint.add(tWallDir));
        }
        this.updateWallAttachData();
        this.updateMatrix();
        if (pNotifyWall)
            this.mWall.updateGeometry();
    }
    setMirrorVertical(pMirror) {
        if (pMirror === this.mMirrorVertical)
            return;
        this.mMirrorVertical = pMirror;
        this.updateGeometry();
    }
    setMirrorHorizontal(pMirror) {
        if (pMirror === this.mMirrorHorizontal)
            return;
        this.mMirrorHorizontal = pMirror;
        this.updateGeometry();
    }
    setAudioSphere(pisAudiSphere = false) {
        this.Params.audioSphere = pisAudiSphere;
        if (pisAudiSphere) {
            if (this.m2dGroup) {
                this.m2dGroup.setEnabled(false);
            }
            this.Params.soundEffectId = '';
            const tseList = this.mRoomManager.externalSoundEffectList;
            if (tseList.length > 0) {
                this.Params.soundEffectId = tseList[0].id;
                // this.setAudioFile(this.Params.soundEffectId);
            }
        }
        else {
            if (this.m2dGroup) {
                this.m2dGroup.setEnabled(true);
            }
        }
    }
    setAudioFile(pnewId) {
        const tnewSoundSet = this.mRoomManager.getSoundEffectsById(pnewId);
        if (tnewSoundSet == null ||
            tnewSoundSet.id == 'none') {
            if (tnewSoundSet != null) {
                this.mSoundSet = tnewSoundSet;
            }
            this.removeAudio();
            this.notifyChange(ElementChangedFlags.PropertyValues);
            return;
        }
        this.mSoundSet = tnewSoundSet;
        const tfileURL = this.mSoundSet.fileURL;
        if (tfileURL == null) {
            this.mAudioURL = "";
        }
        else {
            this.mAudioURL = tfileURL;
        }
        if (this.mAudioURL === '') {
            return;
        }
        const tmesh3d = this.m3dGroup.getChildMeshes();
        if (tmesh3d.length == 0) {
            return;
        }
        if (this.mSound != null) {
            this.mSound.detachFromMesh();
            this.mSound.stop();
            setTimeout(() => {
                if (this.mSound != null) {
                    this.mSound.dispose();
                    this.mSound = null;
                }
                this.createSound(tmesh3d[0]);
            }, 50);
        }
        else {
            this.createSound(tmesh3d[0]);
        }
    }
    removeAudio() {
        if (this.mSound != null) {
            this.mSound.detachFromMesh();
            this.mSound.stop();
            setTimeout(() => {
                if (this.mSound != null) {
                    this.mSound.dispose();
                    this.mSound = null;
                }
            }, 50);
        }
    }
    setAudioVolume(pVolume, pMuteUnMute = false) {
        var _a, _b, _c, _d;
        let tValue = clamp(pVolume, 0, 100);
        if (pMuteUnMute) {
            (_a = this.mSound) === null || _a === void 0 ? void 0 : _a.setVolume(pVolume * 0.01);
            return;
        }
        this.mSoundVolume = tValue;
        if (this.mRoomManager.audioSpheresMute) {
            (_b = this.mSound) === null || _b === void 0 ? void 0 : _b.setVolume(0);
        }
        else {
            (_c = this.mSound) === null || _c === void 0 ? void 0 : _c.setVolume(tValue * 0.01);
            (_d = this.mSound_Preview) === null || _d === void 0 ? void 0 : _d.setVolume(tValue * 0.01);
        }
        this.notifyChange(ElementChangedFlags.PropertyValues);
    }
    setStatus(pStatus) {
        if (this.mElementStatus === pStatus)
            return;
        this.mElementStatus = pStatus;
        if (this.type === "RoomAR") {
            this.geometry.setStatus(pStatus);
        }
        this.mRoomManager.app.viewer.requestRenderFrame();
    }
    set_SafetyZone_Visibility(pVisible) {
        this.mSZ_visible = pVisible;
        if (this.mSZ_visible) {
            this.showMeshes();
        }
        else {
            this.hideMeshes();
        }
    }
    setTransformStatus(pStatus) {
        if (this.mTransformStatus === pStatus)
            return;
        this.mTransformStatus = pStatus;
        this.mRoomManager.app.viewer.requestRenderFrame();
    }
    setToolRunning(pRunning) {
        this.mToolRunning = pRunning;
    }
    setObjectType(pType) {
        this.mObjectType = pType;
    }
    changeMeshMaterial() {
        let t2dNodes = this.m2dGroup.getChildMeshes(true);
        for (let tNode of t2dNodes) {
            tNode.material = null;
        }
    }
    // tslint:disable-next-line:cyclomatic-complexity
    countGridFields(pCAG) {
        this.mGridFieldData.full = 0;
        this.mGridFieldData.diagonal = 0;
        this.mGridFieldData.vertical = 0;
        this.mGridFieldData.horizontal = 0;
        this.mGridFieldData.freeform = 0;
        if (this.mType !== "Line" ||
            (this.mType === "Line" && this.mGridRE_REF == null)) {
            return (null);
        }
        const tGridFieldCnt = Util.getGridFieldCount(this.mGridRE_REF);
        const tREmesh = this.m3dGroup.getChildMeshes();
        if (tREmesh.length === 0) {
            return (null);
        }
        const tlineElemMesh = tREmesh[0];
        const tWM = this.node.getWorldMatrix();
        for (let i = 1; i <= tGridFieldCnt; i++) {
            const tgCell = Util.getGridFieldCell(this.mGridRE_REF, i);
            if (tgCell == null) {
                // grid index out of range!
                return (null);
            }
            const tgAllPoints = Util.getGridAllFieldPoints(this.mGridRE_REF, tgCell.x, tgCell.y);
            if (tgAllPoints == null) {
                // got not all grid points
                return (null);
            }
            // const tgAll = this.checkGridPoints_All(tlineElemMesh as Mesh, tWM, tgAllPoints);
            // if (tgAll == null) {
            //     return (null);
            // }
            // if (tgAll === PointOnPolygons.none) {
            //     continue;
            // }
            const tgFull = this.checkGridPoints_Full_CAG(tgAllPoints, pCAG);
            if (tgFull) {
                this.mGridFieldData.full++;
                continue;
            }
            // diagonal top left
            const tgDiagTopLeft = this.checkGridPoints_Opponents_CAG(tgAllPoints, GridFieldPointMode.diagonalTopLeft, GridFieldPointMode.diagnoalBottomRight, pCAG);
            if (tgDiagTopLeft) {
                this.mGridFieldData.diagonal++;
                continue;
            }
            // diagonal top right
            const tgDiagTopRight = this.checkGridPoints_Opponents_CAG(tgAllPoints, GridFieldPointMode.diagonalTopRight, GridFieldPointMode.diagnoalBottomLeft, pCAG);
            if (tgDiagTopRight == null) {
                return (null);
            }
            if (tgDiagTopRight) {
                this.mGridFieldData.diagonal++;
                continue;
            }
            // diagonal bottom left
            const tgDiagBottomLeft = this.checkGridPoints_Opponents_CAG(tgAllPoints, GridFieldPointMode.diagnoalBottomLeft, GridFieldPointMode.diagonalTopRight, pCAG);
            if (tgDiagBottomLeft == null) {
                return (null);
            }
            if (tgDiagBottomLeft) {
                this.mGridFieldData.diagonal++;
                continue;
            }
            // diagonal bottom right
            const tgDiagBottomRight = this.checkGridPoints_Opponents_CAG(tgAllPoints, GridFieldPointMode.diagnoalBottomRight, GridFieldPointMode.diagonalTopLeft, pCAG);
            if (tgDiagBottomRight == null) {
                return (null);
            }
            if (tgDiagBottomRight) {
                this.mGridFieldData.diagonal++;
                continue;
            }
            // horizontal top
            const tgHoriTop = this.checkGridPoints_Opponents_CAG(tgAllPoints, GridFieldPointMode.horizontalTop, GridFieldPointMode.horizontalBottom, pCAG);
            if (tgHoriTop == null) {
                return (null);
            }
            if (tgHoriTop) {
                this.mGridFieldData.horizontal++;
                continue;
            }
            // horizontal bottom
            const tgHoriBottom = this.checkGridPoints_Opponents_CAG(tgAllPoints, GridFieldPointMode.horizontalBottom, GridFieldPointMode.horizontalTop, pCAG);
            if (tgHoriBottom == null) {
                return (null);
            }
            if (tgHoriBottom) {
                this.mGridFieldData.horizontal++;
                continue;
            }
            // vertical left
            const tgVerticalLeft = this.checkGridPoints_Opponents_CAG(tgAllPoints, GridFieldPointMode.verticalLeft, GridFieldPointMode.verticalRight, pCAG);
            if (tgVerticalLeft == null) {
                return (null);
            }
            if (tgVerticalLeft) {
                this.mGridFieldData.vertical++;
                continue;
            }
            // vertical right
            const tgVerticalRight = this.checkGridPoints_Opponents_CAG(tgAllPoints, GridFieldPointMode.verticalRight, GridFieldPointMode.verticalLeft, pCAG);
            if (tgVerticalRight == null) {
                return (null);
            }
            if (tgVerticalRight) {
                this.mGridFieldData.vertical++;
                continue;
            }
            const tgAllCount = this.checkGridPoints_rest_CAG(tgAllPoints, pCAG);
            if (tgAllCount) {
                this.mGridFieldData.freeform++;
                continue;
            }
        }
        this.mGridFieldData.totalfull = this.mGridFieldData.full;
        this.mGridFieldData.totalfull += this.mGridFieldData.freeform;
        this.mGridFieldData.totalfull += Math.round(this.mGridFieldData.diagonal / 2);
        this.mGridFieldData.totalfull += Math.round(this.mGridFieldData.vertical / 2);
        this.mGridFieldData.totalfull += Math.round(this.mGridFieldData.horizontal / 2);
        this.notifyChange(ElementChangedFlags.PropertyValues);
        this.notifyChange(ElementChangedFlags.Properties);
        return (this.mGridFieldData);
    }
    drawOutLines() {
        let t2dNodes = this.m2dGroup.getChildTransformNodes(true);
        let pMesh = t2dNodes[0];
        this.mOutlines = Util.getPerimeterLines(pMesh);
        let tLS = CreateLineSystem("linesystem", { lines: this.mOutlines, updatable: true }, this.mScene);
        tLS.color = Color3.Red();
    }
    drawVertices(pDelta) {
        let tMat = new PbrMaterial("room element color " + this.color, this.mModel.materials);
        tMat.metallic = 0;
        tMat.roughness = 1;
        tMat.albedoColor = Color3.FromHexString("#0000FF").toLinearSpace();
        tMat.backFaceCulling = false;
        tMat.freeze();
        let t2dNodes = this.m2dGroup.getChildMeshes(true);
        let pMesh = t2dNodes[0];
        let tMV = Util.getMeshVertices(pMesh, pDelta);
        for (let tP of tMV) {
            let tLines = CreateLines("", { points: tP }, this.mScene);
            tLines.color = Color3.FromHexString("#FF0000").toLinearSpace();
            tLines.parent = pMesh.parent;
        }
    }
    setCustomParent(pParent) {
        this.mCustomParent = pParent;
    }
    beginGeometryUpdate() {
        this.mGeometryUpdateDepth++;
    }
    endGeometryUpdate() {
        this.mGeometryUpdateDepth--;
        if (this.mGeometryUpdateDepth <= 0) {
            this.mGeometryUpdateDepth = 0;
            if (this.mPendingGeometryUpdate)
                this.updateGeometry();
        }
    }
    // tslint:disable-next-line:cyclomatic-complexity
    updateGeometry(pNotify = true, pDisplayOnly = false, pcutComplete = false) {
        if (this.mType === "DummyElement" || this.mNode === undefined)
            return;
        if (this.mGeometryUpdateDepth > 0) {
            this.mPendingGeometryUpdate = true;
            return;
        }
        let tdisplayOnly = pDisplayOnly;
        this.mPendingGeometryUpdate = false;
        this.updateMaterial(this.mMaterialSet);
        let tactTool = this.mRoomManager.app.tools.activeTool;
        if ((this.interactorsVisible && this.mRoomManager.interactorState === 1) ||
            ((tactTool instanceof MoveLine || tactTool instanceof MoveSpline ||
                tactTool instanceof Area2D || tactTool instanceof ChangePolygonPoints) &&
                this.mRoomManager.interactorState === 1)) {
            this.mAdditionalGeometryGroup.setEnabled(true);
        }
        else {
            this.mAdditionalGeometryGroup.setEnabled(false);
        }
        if (this.mType === "SafetyZone") {
            let tgeom = this.geometry;
            tgeom.clearCutPoints();
        }
        if (this.mType === "Sphere" && this.mParams.audioSphere) {
            if (this.mSound != null) {
                this.mSound.detachFromMesh();
            }
        }
        this.update2dGeometry(tdisplayOnly, pcutComplete);
        this.update3dGeometry(tdisplayOnly, pcutComplete);
        if (this.mType === "CustomBuilding" && this.mRE_reference !== null) {
            this.mRE_reference.update2dGeometry(tdisplayOnly);
            this.mRE_reference.update3dGeometry(tdisplayOnly);
        }
        if (!(this.mType === "Line" && this.mGridRE_REF != null)) {
            this.updateAdditionalGeometry();
            this.updateSpecialGeometry();
        }
        if (this.mType === "Sphere" && this.mParams.audioSphere) {
            this.updateAudio();
        }
        if (pNotify) {
            this.notifyChange(ElementChangedFlags.BoundingBox);
            this.notifyChange(ElementChangedFlags.PropertyValues);
        }
        if (this.mType === "BackgroundImage")
            this.createPickPlane();
        else
            this.calcNormalVector();
    }
    updateGeometry_GEO(pNotify = true) {
        this.beginChange();
        this.updateGeometry();
        if (pNotify) {
            this.notifyChange(ElementChangedFlags.PropertyValues);
            this.notifyChange(ElementChangedFlags.Properties);
            this.notifyChange(ElementChangedFlags.BoundingBox);
        }
        this.endChange();
    }
    getEdge(pEdge, pLocalCoords) {
        let tV;
        switch (pEdge) {
            case 0:
                tV = new Vector2(-this.mWidth * 0.5, -this.depth * 0.5);
                break;
            case 1:
                tV = new Vector2(this.mWidth * 0.5, -this.depth * 0.5);
                break;
            case 2:
                tV = new Vector2(this.mWidth * 0.5, this.depth * 0.5);
                break;
            default: //3
                tV = new Vector2(-this.mWidth * 0.5, this.depth * 0.5);
                break;
        }
        if (!pLocalCoords) {
            let tV3d = Util.to3d(tV);
            Vector3.TransformCoordinatesToRef(tV3d, this.transform, tV3d);
            tV = Util.to2d(tV3d);
        }
        return (tV);
    }
    //// internal //////////////////////////////////////////////////////////////////////////////
    /** @internal */
    static _load(pRoomManager, pRoomElement, pIOElement) {
        //
    }
    /** @internal */
    // tslint:disable-next-line:cyclomatic-complexity
    _saveUndoData() {
        let tData = {};
        tData.type = this.mType;
        tData.elementParams = {};
        if (this.mType === "Line" || this.mType === "Spline" || this.mType === "CustomBuilding" || this.mType === "Road" ||
            this.mType === "Rectangle" || this.mType === "Circle" || this.mType === "Hedge" || this.mType === "Fence" ||
            this.mType === "CameraPath" || this.mType === "Grid") {
            tData.elementParams.ElementPoints = new Array();
            // clone point array !
            for (let tP of this.mParams.ElementPoints) {
                let tnP = tP.clone();
                tData.elementParams.ElementPoints.push(tnP);
            }
            tData.refPointPos = this.mRefPoint;
        }
        if (this.mType === "Text") {
            tData.textHeight = this.mTextHeight;
        }
        tData.position = this.mPosition.clone();
        tData.node = this.node;
        return (tData);
    }
    /** @internal */
    // tslint:disable-next-line:cyclomatic-complexity
    _loadUndoData(pData) {
        if (pData == null)
            return;
        let tElement = null;
        if (this.mType === "Line" || this.mType === "Spline" || this.mType === "CustomBuilding" || this.mType === "Road" ||
            this.mType === "Rectangle" || this.mType === "Circle" || this.mType === "Hedge" || this.mType === "Fence" ||
            this.mType === "Grid") {
            tElement = this.mGeometry;
            if (tElement !== null) {
                tElement.setPoints(pData.elementParams.ElementPoints, false);
            }
            this.mRefPoint = pData.refPointPos;
        }
        if (this.mType === "CameraPath") {
            tElement = this.mGeometry;
            if (tElement !== null) {
                tElement.setPoints(pData.elementParams.ElementPoints, false);
            }
            this.mRefPoint = pData.refPointPos;
        }
        if (this.mType === "Text") {
            this.setTextHeight(pData.textHeight);
        }
        this.beginGeometryUpdate();
        this.setPosition(pData.position);
        if (this.mType === "CameraPath" && tElement != null) {
            tElement.computeCamPathData();
        }
        this.updateGeometry(false);
        this.endGeometryUpdate();
        // if (this.mType === "Road" || this.mType === "Hedge") {
        //     this.updateGeometry(false);
        //     this.convertREMesh();
        // }
    }
    /** @internal */
    // tslint:disable-next-line:cyclomatic-complexity
    _getDistanceEdge(pEdge) {
        if (this.mWall == null || pEdge > 3)
            return (0);
        let tV1 = Vector2.Zero();
        let tV2 = Vector2.Zero();
        let tNRes = this.getNeighbour(pEdge, pEdge === 0 || pEdge === 3); //left/right
        let tNeighbour = tNRes.opening;
        let tWall = tNRes.wall;
        let tDist = 0;
        //use other opening as neighbour
        if (tNeighbour != null && tNeighbour.mWall != null && tWall == null) {
            let tW = this.mWidth;
            if (pEdge === 0 || pEdge === 3) {
                tDist = Vector2.Distance(Util.to2d(this.mPosition), this.mWall.startPoint) - tW / 2;
                tDist -= Vector2.Distance(Util.to2d(tNeighbour.mPosition), tNeighbour.mWall.startPoint);
                tDist -= tNeighbour.mWidth / 2;
            }
            else {
                tDist = Vector2.Distance(Util.to2d(tNeighbour.mPosition), tNeighbour.mWall.startPoint);
                tDist -= tNeighbour.mWidth / 2;
                tDist -= Vector2.Distance(Util.to2d(this.mPosition), this.mWall.startPoint) + tW / 2;
            }
        }
        else {
            tV1 = this.getEdge(pEdge, false);
            if (tWall == null) {
                if (tNeighbour == null)
                    tV2 = this.mWall._edges[pEdge];
                else {
                    switch (pEdge) {
                        case 0:
                            tV2 = tNeighbour.getEdge(1, false);
                            break;
                        case 1:
                            tV2 = tNeighbour.getEdge(0, false);
                            break;
                        case 2:
                            tV2 = tNeighbour.getEdge(3, false);
                            break;
                        case 3:
                            tV2 = tNeighbour.getEdge(2, false);
                            break;
                        default:
                            break;
                    }
                }
            }
            else if (pEdge === 0 || pEdge === 2) {
                if (tWall.startCxn.wall === this.mWall) {
                    tV2 = tWall._edges[3];
                }
                else {
                    tV2 = tWall._edges[1];
                }
            }
            else if (tWall.startCxn.wall === this.mWall) {
                tV2 = tWall._edges[0];
            }
            else {
                tV2 = tWall._edges[2];
            }
            tDist = Vector2.Distance(tV1, tV2);
            //negate distance if necessary
            if (pEdge === 0 || pEdge === 3) {
                if (!Util.vectorEquals2(this.mWall.endPoint.subtract(this.mWall.startPoint).normalize(), tV1.subtract(tV2).normalize()))
                    tDist *= -1;
            }
            else if (!Util.vectorEquals2(this.mWall.startPoint.subtract(this.mWall.endPoint).normalize(), tV1.subtract(tV2).normalize())) {
                tDist *= -1;
            }
        }
        if (eq(tDist, 0.0))
            tDist = Math.abs(tDist);
        return (tDist);
    }
    /** @internal */
    _update2dVisibility() {
        let tNodes = this.m2dGroup.getChildTransformNodes(false);
        for (let tNode of tNodes) {
            if (!(tNode instanceof AbstractMesh))
                continue;
            if (!this.mRoomManager.isInteracting && this.mRoomManager.visibilityMode === VisibilityMode.Show3d) {
                //only visible in top and bottom view
                CameraControl.SetMeshCameraModeMask(tNode, CameraModeMask.Top | CameraModeMask.Bottom);
            }
            else {
                //visible in all views
                CameraControl.SetMeshCameraModeMask(tNode, null);
            }
        }
    }
    /** @internal */
    _update3dVisibility() {
        if (this.mRoomManager.visibilityMode === VisibilityMode.Show3d) {
            if (this.mType === "SafetyZone") {
                this.m3dGroup.setEnabled(true);
            }
            else {
                this.m3dGroup.setEnabled(true);
            }
        }
        else {
            this.m3dGroup.setEnabled(true);
        }
    }
    /** @internal */
    _updateSpecialGeometryVisibility() {
        let tNodes = this.mSpecialGeometryGroup.getChildTransformNodes(false);
        for (let tNode of tNodes) {
            if (!(tNode instanceof AbstractMesh))
                continue;
            if (!this.mRoomManager.isInteracting && this.mRoomManager.visibilityMode === VisibilityMode.Show3d) {
                //only visible in top and bottom view
                CameraControl.SetMeshCameraModeMask(tNode, CameraModeMask.Top | CameraModeMask.Bottom);
            }
            else {
                CameraControl.SetMeshCameraModeMask(tNode, CameraModeMask.Top | CameraModeMask.Bottom);
            }
        }
        if (this.mType === "SafetyZone") {
            this.mSpecialGeometryGroup.setEnabled(this.mSZ_visible);
        }
    }
    /** @internal */
    _updateAdditionalGeometryVisibility() {
        let tNodes = this.mAdditionalGeometryGroup.getChildTransformNodes(false);
        for (let tNode of tNodes) {
            if (!(tNode instanceof AbstractMesh))
                continue;
            switch (this.mType) {
                case "CustomBuilding":
                    //only visible in top and bottom view
                    CameraControl.SetMeshCameraModeMask(tNode, null);
                    break;
                default:
                    //only visible in top and bottom view
                    CameraControl.SetMeshCameraModeMask(tNode, CameraModeMask.Top | CameraModeMask.Bottom);
            }
        }
        if (this.mType === "SafetyZone") {
            this.mAdditionalGeometryGroup.setEnabled(this.mSZ_visible);
        }
    }
    /** @internal */
    _finishTransformation() {
        if (this.type === "Floor")
            return;
        if (this.mPendingTransformMatrix == null)
            return;
        if (this.mWall == null || !this.mRoomManager._wasElementTransformed(this.mWall)) {
            let tNewPos = Vector3.TransformCoordinates(VECTOR3_ZERO, this.mPendingTransformMatrix);
            this.setPosition(tNewPos, true);
        }
        this.boundingBoxVisibility(false);
        const tREgeo = this.geometry;
        if (tREgeo != null) {
            let tType = tREgeo.getType();
            if (tType === "BackgroundImage") {
                this.createPickPlane();
            }
            if (tType === "CameraPath") {
                this.mHeight = this.mNode.position.y;
                this.notifyChange(ElementChangedFlags.PropertyValues);
                this.notifyChange(ElementChangedFlags.BoundingBox);
                tREgeo.computeCamPathData();
                this.updateGeometry();
            }
            if (tType === "Circle") {
                this.mNode.rotationQuaternion = Quaternion.Identity();
                this.updateTransformation();
            }
        }
        this.mPendingTransformMatrix = null;
    }
    setDepth(pValue, pUpdateGeometry = true, pNotify = true) {
        let tValue = clamp(pValue, 0.01, 500.0);
        if (eq(this.mDepth, tValue))
            return (false);
        this.mDepth = tValue;
        if (this.mType === "Rectangle") {
            let tRectangle = this.geometry;
            tRectangle.setNewDepth(tValue);
            this.mRoomManager.cutOverlappingShapes();
        }
        if (pUpdateGeometry) {
            this.beginChange();
            if (this.mType === "SafetyZone") {
                this.updateGeometry(true, true);
            }
            else {
                this.updateGeometry();
            }
            this.endChange();
        }
        if (pNotify) {
            this.notifyChange(ElementChangedFlags.PropertyValues);
            this.notifyChange(ElementChangedFlags.Properties);
            this.notifyChange(ElementChangedFlags.BoundingBox);
        }
        if (this.mWall != null)
            this.mWall.updateGeometry();
        this.mRoomManager.contentChanged();
        return (true);
    }
    setDrawingNew(pIsDrawingNew) {
        this.mIsDrawingNew = pIsDrawingNew;
        this.updateGeometry();
    }
    setRELabel(pValue) {
        if (this.mType === "Line" && this.mGridRE_REF != null) {
            if (pValue === undefined) {
                this.mLabel = "Grass mat [" + this.mRoomManager._getNextREGridId().toString() + "]";
            }
            else {
                this.mLabel = pValue;
            }
        }
        else if (pValue === undefined) {
            this.mLabel = "Area [" + this.mRoomManager._getNextREId().toString() + "]";
        }
        else {
            this.mLabel = pValue;
        }
        this.notifyChange(ElementChangedFlags.PropertyValues);
    }
    setArenaLaneCount(pValue, pUpdateGeometry = true) {
        let tValue = clamp(pValue, 1, 8);
        if (eq(this.Params.arenaLaneCount, tValue)) {
            return (false);
        }
        this.Params.arenaLaneCount = tValue;
        if (pUpdateGeometry) {
            this.beginChange();
            this.updateGeometry();
            this.notifyChange(ElementChangedFlags.PropertyValues);
            this.notifyChange(ElementChangedFlags.Properties);
            this.endChange();
        }
        return (true);
    }
    setArenaShapeType(pValue) {
        let tValue = pValue.toLocaleLowerCase().trim() === "circle";
        if (tValue === this.Params.arenaCircle) {
            return (false);
        }
        this.Params.arenaCircle = tValue;
        this.Params.arenaSideSegmentsVisible = !this.Params.arenaCircle;
        this.beginChange();
        this.updateGeometry();
        this.notifyChange(ElementChangedFlags.PropertyValues);
        this.notifyChange(ElementChangedFlags.Properties);
        this.endChange();
        return (true);
    }
    setArenaLaneLength(pValue, pUpdateGeometry = true) {
        let tValue = clamp(pValue, 10, 400);
        const tnewScale = tValue / 400; // reference value for scale
        if (eq(this.Params.arenaLaneScale, tnewScale)) {
            return (false);
        }
        this.Params.arenaLaneScale = tnewScale;
        if (pUpdateGeometry) {
            this.beginChange();
            this.updateGeometry();
            this.notifyChange(ElementChangedFlags.PropertyValues);
            this.notifyChange(ElementChangedFlags.Properties);
            this.endChange();
        }
        return (true);
    }
    setArenaScale(pValue, pUpdateGeometry = true) {
        let tValue = clamp(pValue, 0.025, 1);
        if (eq(this.Params.arenaLaneScale, tValue)) {
            return (false);
        }
        this.Params.arenaLaneScale = tValue;
        if (pUpdateGeometry) {
            this.beginChange();
            this.updateGeometry();
            this.notifyChange(ElementChangedFlags.PropertyValues);
            this.endChange();
        }
        return (true);
    }
    getArenaFieldScaleOffset() {
        const tfieldLength = this.Params.arenaFieldLength * this.mParams.arenaLaneScale;
        const tfieldLengthScaled = this.Params.arenaFieldLength * this.mParams.arenaLaneScale * this.mParams.arenaFieldScale;
        const tDiff = tfieldLengthScaled - tfieldLength;
        let tValue = new PropertyValue(tDiff);
        return (tValue);
    }
    setArenaFieldScaleOffset(pValue, pUpdateGeometry = true) {
        if (this.mType !== "Arena") {
            return (false);
        }
        const tfieldLength = this.Params.arenaFieldLength * this.mParams.arenaLaneScale;
        let tnewFieldLength = clamp(tfieldLength + pValue, 1, 500);
        const tnewFieldLengthScale = tnewFieldLength / tfieldLength;
        let tValue = clamp(tnewFieldLengthScale, -5, 5);
        if (eq(this.Params.arenaFieldScale, tValue)) {
            return (false);
        }
        this.Params.arenaFieldScale = tValue;
        if (pUpdateGeometry) {
            this.beginChange();
            this.updateGeometry();
            this.notifyChange(ElementChangedFlags.PropertyValues);
            this.endChange();
        }
        return (true);
    }
    setArenaFieldScale(pValue, pUpdateGeometry = true) {
        let tValue = clamp(pValue, 0.1, 10);
        if (eq(this.Params.arenaFieldScale, tValue)) {
            return (false);
        }
        this.Params.arenaFieldScale = tValue;
        if (pUpdateGeometry) {
            this.beginChange();
            this.updateGeometry();
            this.notifyChange(ElementChangedFlags.PropertyValues);
            this.endChange();
        }
        return (true);
    }
    setArenaPlayFieldVisible(pValue, pUpdateGeometry = true) {
        if (this.Params.arenaPlayFieldVisible === pValue) {
            return (false);
        }
        this.Params.arenaPlayFieldVisible = pValue;
        if (pUpdateGeometry) {
            this.beginChange();
            this.updateGeometry();
            this.notifyChange(ElementChangedFlags.PropertyValues);
            this.endChange();
            this.mRoomManager.cutOverlappingShapes();
        }
        return (true);
    }
    setArenaSideSegmentsVisible(pValue, pUpdateGeometry = true) {
        if (this.Params.arenaSideSegmentsVisible === pValue) {
            return (false);
        }
        this.Params.arenaSideSegmentsVisible = pValue;
        if (pUpdateGeometry) {
            this.beginChange();
            this.updateGeometry();
            this.notifyChange(ElementChangedFlags.PropertyValues);
            this.endChange();
            this.mRoomManager.cutOverlappingShapes();
        }
        return (true);
    }
    setLength(pValue) {
        this.mLength = pValue;
        this.notifyChange(ElementChangedFlags.PropertyValues);
        this.notifyChange(ElementChangedFlags.Properties);
        return (true);
    }
    setRoadWidth(pValue, pUpdateGeometry = true) {
        let tValue = clamp(pValue, 0.01, 10.0);
        if (eq(this.Params.roadWidth, tValue))
            return (false);
        this.Params.roadWidth = tValue;
        if (pUpdateGeometry) {
            this.beginChange();
            this.updateGeometry();
            this.notifyChange(ElementChangedFlags.PropertyValues);
            this.endChange();
        }
        return (true);
    }
    setHedgeWidth(pValue, pUpdateGeometry = true) {
        let tValue = clamp(pValue, 0.01, 10.0);
        if (eq(this.Params.HedgeDepth, tValue))
            return (false);
        this.Params.HedgeDepth = tValue;
        if (pUpdateGeometry) {
            this.beginChange();
            this.updateGeometry();
            this.notifyChange(ElementChangedFlags.PropertyValues);
            this.endChange();
        }
        return (true);
    }
    setHedgeHeight(pValue, pUpdateGeometry = true) {
        let tValue = clamp(pValue, 0.01, 2.0);
        if (eq(this.mHeight, tValue))
            return (false);
        this.mHeight = tValue;
        if (pUpdateGeometry) {
            this.beginChange();
            this.updateGeometry();
            this.notifyChange(ElementChangedFlags.PropertyValues);
            this.endChange();
        }
        return (true);
    }
    setDiameter(pValue) {
        let tValue = clamp(pValue, 0.01, 500.0);
        if (eq(this.mDiameter, tValue))
            return (false);
        this.mDiameter = tValue;
        this.beginChange();
        this.updateGeometry();
        this.notifyChange(ElementChangedFlags.PropertyValues);
        this.endChange();
        return (true);
    }
    setImageURL(pURL, pUpdateGeometry = true) {
        let tValue = pURL.trim();
        if (tValue === "")
            return (false);
        this.mImageURL = tValue;
        if (pUpdateGeometry) {
            this.beginChange();
            this.updateGeometry();
            this.notifyChange(ElementChangedFlags.PropertyValues);
            this.endChange();
        }
        return (true);
    }
    setImageBase64(pURL, pUpdateGeometry = false) {
        let tValue = pURL.trim();
        if (tValue === "")
            return (false);
        this.mImageBase64 = tValue;
        if (pUpdateGeometry) {
            this.beginChange();
            this.updateGeometry();
            this.notifyChange(ElementChangedFlags.PropertyValues);
            this.endChange();
        }
        return (true);
    }
    setImageProportional(pIsProportional) {
        if (pIsProportional === this.mImageProportional)
            return (false);
        this.mImageProportional = pIsProportional;
        this.beginChange();
        this.updateGeometry();
        this.notifyChange(ElementChangedFlags.PropertyValues);
        this.endChange();
        return (true);
    }
    set_MaterialSet(pMaterialSet) {
        this.mMaterialSet.category = pMaterialSet.category;
        this.mMaterialSet.value = pMaterialSet.value;
    }
    setMaterialId(pId) {
        if (pId != null && pId.trim().toLocaleLowerCase() === "")
            return;
        let tId = pId;
        if (tId === undefined) {
            tId = "color" + this.mRoomManager.materialSplitStr + "#999999";
        }
        this.mMaterialId = tId;
        let tmatId = tId.split(this.mRoomManager.materialSplitStr);
        let tmatValue = tmatId.pop();
        let tmatCategory = tmatId.pop();
        if (tmatCategory !== undefined)
            this.mMaterialSet.category = tmatCategory;
        if (tmatValue !== undefined) {
            if (this.mMaterialSet.category === "color")
                this.mColor = tmatValue;
            this.mMaterialSet.value = tmatValue;
        }
        this.mMaterial = this._getMaterialIndex();
    }
    _setMaterialCategory(pCategory) {
        this.mMaterialSet.category = pCategory;
    }
    generateMaterialId() {
        this.mMaterialId = this.mMaterialSet.category + this.mRoomManager.materialSplitStr + this.mColor;
        this.storeSafetyZoneData();
    }
    setMaterialIndex(pValue) {
        let tValue = clamp(pValue, -2, 100);
        this.mMaterial = tValue;
        this.mRoomManager.checkMaterial(this.mMaterialSet);
        if (this.mType !== "Text") {
            this.beginChange();
            let t3dNodes = this.m3dGroup.getChildTransformNodes(true);
            for (let tNode of t3dNodes) {
                let tN = tNode;
                if (tN != null) {
                    tN.material = this.localMaterial;
                }
            }
            let t2dNodes = this.m2dGroup.getChildTransformNodes(true);
            for (let tNode of t2dNodes) {
                let tN = tNode;
                if (tN != null) {
                    tN.material = this.localMaterial;
                }
            }
            this.endChange();
        }
        this.mRoomManager.setSafetyZoneData(this.mBasketId, this.mMaterial, this.mColor, this.mMaterialSet.category, this.mMaterialSet.value, this.mMaterialId);
        this.notifyChange(ElementChangedFlags.PropertyValues);
        this.notifyChange(ElementChangedFlags.Properties);
        return (true);
    }
    // tslint:disable-next-line:cyclomatic-complexity
    async setMaterialValue() {
        await this.setMeshMaterial1();
        if (this.mType === "Floor")
            this.mRoomManager.setFloorMaterialId(this.mWallIds, this.mMaterialSet);
        if (this.mType !== "Text") {
            this.beginChange();
            let t3dNodes = this.m3dGroup.getChildTransformNodes(true);
            for (let tNode of t3dNodes) {
                let tN = tNode;
                if (tN != null && tN.material != null) {
                    tN.material = this.localMaterial;
                }
                if (this.mType === "Floor") {
                    tN.visibility = this.Params.floorTransparent ? RoomManager.ROOM_FLOOR_OPACITY_VALUE : 1;
                }
            }
            let t2dNodes = this.m2dGroup.getChildTransformNodes(true);
            for (let tNode of t2dNodes) {
                let tN = tNode;
                if (tN != null && tN.material != null) {
                    tN.material = this.localMaterial;
                }
                if (this.mType === "Floor") {
                    tN.visibility = this.Params.floorTransparent ? RoomManager.ROOM_FLOOR_OPACITY_VALUE : 1;
                }
            }
            this.endChange();
        }
        this.mRoomManager.setSafetyZoneData(this.mBasketId, this.mMaterial, this.mColor, this.mMaterialSet.category, this.mMaterialSet.value, this.mMaterialId);
        this.notifyChange(ElementChangedFlags.PropertyValues);
        this.notifyChange(ElementChangedFlags.Properties);
        this.mRoomManager.contentChanged();
        return (true);
    }
    setMaterialSet(pMaterialSet) {
        this.mMaterialSet = pMaterialSet;
    }
    setMaterialCategory(pValue, pMatOfCat = "", pforceSet = false, pMaterialSet, pdeserialize = false) {
        if ((!pforceSet) && (pValue === pMaterialSet.category)) {
            return;
        }
        pMaterialSet.category = pValue;
        let tMatofCat = pMatOfCat;
        if (tMatofCat === "") {
            tMatofCat = this.mRoomManager._getFirstextMaterialOfCategory(pValue);
        }
        if (tMatofCat === "" && pValue === "color") {
            this.mMaterial = -1;
            tMatofCat = this.mColor;
        }
        pMaterialSet.value = tMatofCat;
        this.mRoomManager.checkMaterial(pMaterialSet).then((pResult) => {
            if (pResult === 1 || pResult === 2) {
                //this.updateGeometry();
                this.getTextureScaling(pdeserialize);
                this.mMaterialId = this.mRoomManager.getMaterialId(this.mMaterialSet);
                this.updateGeometry_GEO();
                if (!pdeserialize) {
                    this.mRoomManager.cutOverlappingShapes();
                }
                if (this.mType === "SafetyZone") {
                    this.mRoomManager.cutOverlappingSZShapes();
                }
                this.notifyChange(ElementChangedFlags.PropertyValues);
                this.notifyChange(ElementChangedFlags.Properties);
            }
        });
    }
    setextMaterial(pValue, pUpdateGeometry = true, pFindOverlapping = true) {
        let tmatId = pValue.split(this.mRoomManager.materialSplitStr);
        let textValue = tmatId.pop();
        this.setMaterialId(pValue);
        this.setMaterialValue();
        this.notifyChange(ElementChangedFlags.PropertyValues);
        this.notifyChange(ElementChangedFlags.Properties);
        return (true);
    }
    changeMaterial(pIdx) {
        this.mMaterial = pIdx;
        //this.setMeshMaterial();
        let t3dNodes = this.m3dGroup.getChildTransformNodes(true);
        for (let tNode of t3dNodes) {
            let tN = tNode;
            if ((tN != null) && (tN.material != null)) {
                tN.material = this.mRoomManager.MatManager.getMaterial(this.mMaterialId);
            }
        }
        this.mRoomManager.app.viewer.requestRenderFrame();
    }
    setMaterialValue__(pValue, pMaterial, pRightWall) {
        let tmatValue = StringProperty.ConvertValueToString(pValue);
        if (tmatValue.trim() === "") {
            pMaterial.value = "";
            // clear loaded material id
            pMaterial.loadMatId = "";
            return;
        }
        pMaterial.setInit = false;
        // clear loaded material id
        pMaterial.loadMatId = "";
        let tmatGroup = tmatValue.split(this.mRoomManager.materialSplitStr);
        if (tmatGroup !== undefined) {
            if (tmatGroup.length === 1) {
                tmatValue = tmatGroup[0];
                this.mColor = tmatValue;
            }
            if (tmatGroup.length > 1) {
                tmatValue = tmatGroup[1];
            }
        }
        else {
            tmatValue = "";
        }
        pMaterial.value = tmatValue;
        if (this.mType !== "Arena") {
            this.mMaterialId = this.mRoomManager.getMaterialId(pMaterial);
        }
        this.mRoomManager.checkMaterial(pMaterial).then((pResult) => {
            if (pResult === 1 || pResult === 2) {
                this.getTextureScaling();
                this.updateGeometry(true, true);
                this.mRoomManager.cutOverlappingShapes();
                if (this.mType === "SafetyZone") {
                    this.mRoomManager.cutOverlappingSZShapes();
                }
                this.notifyChange(ElementChangedFlags.PropertyValues);
            }
        });
    }
    // tslint:disable-next-line:cyclomatic-complexity
    setWidth(pValue, pUpdateGeometry = true, pNotify = true, pcutShapes = true) {
        let tValue = clamp(pValue, 0.01, 500.0);
        let tWidth = this.mWidth;
        if (this.mType === "Circle" || this.mType === "Sphere")
            tWidth = this.mWidth * 0.5;
        if (eq(tWidth, tValue))
            return (false);
        this.mWidth = tValue;
        switch (this.mType) {
            case "BackgroundImage":
                if (!this.mImageProportional) {
                    this.mWidth = tValue;
                }
                else {
                    let tRatio = this.mWidth / this.mHeight;
                    this.mWidth = tValue;
                    this.mHeight = this.mWidth / tRatio;
                }
                break;
            case "PillarC":
                this.mDepth = tValue;
                break;
            case "Circle":
                let tCircle = this.geometry;
                if (pUpdateGeometry) {
                    tCircle.setNewRadius(tValue);
                    this.mRoomManager.cutOverlappingShapes();
                }
                break;
            case "Rectangle":
                let tRectangle = this.geometry;
                tRectangle.setNewWidth(tValue);
                this.mRoomManager.cutOverlappingShapes();
                break;
            default:
                if (pcutShapes) {
                    this.mRoomManager.cutOverlappingShapes();
                }
                break;
        }
        if (pUpdateGeometry) {
            this.beginChange();
            this.mSizeChanging = true;
            if (this.mType === "SafetyZone") {
                this.updateGeometry(true, true);
            }
            else {
                this.updateGeometry();
            }
            this.mSizeChanging = false;
            this.endChange();
        }
        if (pNotify) {
            this.notifyChange(ElementChangedFlags.PropertyValues);
            this.notifyChange(ElementChangedFlags.Properties);
            this.notifyChange(ElementChangedFlags.BoundingBox);
        }
        if (this.mWall != null)
            this.mWall.updateGeometry();
        this.mRoomManager.contentChanged();
        return (true);
    }
    // tslint:disable-next-line:cyclomatic-complexity
    setHeight(pValue, pUpdateGeometry = true) {
        let tmin = 0.01;
        if (this.mType === "Rectangle" || this.mType === "Circle" ||
            this.mType === "Line" || this.mType === "Spline") {
            tmin = 0;
        }
        let tValue = clamp(pValue, tmin, 500.0);
        if (eq(this.mHeight, tValue))
            return (false);
        this.mHeight = tValue;
        if (this.mType === "BackgroundImage") {
            if (!this.mImageProportional) {
                this.mHeight = tValue;
            }
            else {
                let tRatio = this.mWidth / this.mHeight;
                this.mHeight = tValue;
                this.mWidth = this.mHeight * tRatio;
            }
        }
        else {
            this.mHeight = tValue;
        }
        if (this.mType === "Rectangle" || this.mType === "Circle" ||
            this.mType === "Line" || this.mType === "Spline") {
            if (this.mHeight > 0) {
                this.m3dGroup.position.y = 0;
                this.m2dGroup.position.y = 0;
            }
            else {
                this.m3dGroup.position.y = RoomElement.INITIAL_2D_SHAPE_HEIGHT;
                this.m2dGroup.position.y = RoomElement.INITIAL_2D_SHAPE_HEIGHT;
            }
            this.updateTransformation();
        }
        if (this.mType === "CameraPath") {
            const tPos = this.mNode.position.clone();
            tPos.y = this.mHeight;
            this.setPosition(tPos);
            if (this.mGeometry != null) {
                const tcamGeo = this.mGeometry;
                tcamGeo.computeCamPathData();
            }
        }
        if (pUpdateGeometry) {
            this.beginChange();
            this.mSizeChanging = true;
            if (this.mType === "SafetyZone") {
                this.updateGeometry(true, true);
            }
            else {
                this.updateGeometry();
            }
            this.mSizeChanging = false;
            this.endChange();
        }
        this.notifyChange(ElementChangedFlags.PropertyValues);
        if (this.mWall != null)
            this.mWall.updateGeometry();
        this.mRoomManager.contentChanged();
        return (true);
    }
    HeightSet(pValue) {
        this.mHeightSet = pValue;
        return;
    }
    setImageSize(pWidth, pHeight, pUpdateGeometry = true) {
        if (pWidth === 0 || pHeight === 0)
            return (false);
        let tValue = this.mWidth * pHeight / pWidth;
        if (eq(this.mHeight, tValue))
            return (false);
        this.mHeight = tValue;
        this.mHeightImage_original = this.mHeight;
        this.mWidthImage_original = this.mWidth;
        if (pUpdateGeometry) {
            this.beginChange();
            this.updateGeometry();
            this.notifyChange(ElementChangedFlags.PropertyValues);
            this.endChange();
        }
        return (true);
    }
    addDummy_REF() {
        if (this.mDummy_REF != null) {
            return;
        }
        const tdummy_REF = new DummyElement(this.mRoomManager, this);
        this.mDummy_REF = tdummy_REF;
        this.mRoomManager.app.model.addElement(tdummy_REF);
    }
    removeDummy_REF(pDispose = false) {
        if (this.mDummy_REF != null) {
            this.mRoomManager.app.model.removeElement(this.mDummy_REF, true);
            if (pDispose) {
                this.mDummy_REF.dispose();
            }
        }
        this.mDummy_REF = null;
    }
    resetImageSize() {
        if (this.mType === "BackgroundImage") {
            let tgeom = this.geometry;
            if (tgeom != null) {
                tgeom.resetPoints();
                this.mHeight = this.mHeightImage_original;
                this.mWidth = this.mWidthImage_original;
                this.beginChange();
                this.updateGeometry();
                this.notifyChange(ElementChangedFlags.PropertyValues);
                this.endChange();
                this.mRoomManager.contentChanged();
            }
        }
    }
    resetCustomBuilding() {
        if (this.mType !== "CustomBuilding")
            return;
        let tgeom = this.geometry;
        if (tgeom != null &&
            (this.mParams.ElementOriginalHeight !== undefined && this.mParams.ElementOriginalHeight > 0)) {
            this.beginChange();
            this.mHeight = this.mParams.ElementOriginalHeight;
            let toriPosX = this.mParams.ElementOriginalPositionX;
            let toriPosY = this.mParams.ElementOriginalPositionY;
            let toriPosZ = this.mParams.ElementOriginalPositionZ;
            tgeom.resetPoints();
            this.setPosition(new Vector3(toriPosX, toriPosY, toriPosZ));
            this.updateGeometry(true, false);
            this.notifyChange(ElementChangedFlags.PropertyValues);
            this.endChange();
            this.mRoomManager.contentChanged();
            this.mRoomManager.app.viewer.requestRenderFrame();
        }
    }
    resetTextureSize() {
        if (this.mType !== "CustomBuilding" || this.mMaterialSet.category === "color")
            return;
        this.mMaterialSet.offsetU = this.mMaterialSet.offsetU_init;
        this.mMaterialSet.offsetV = this.mMaterialSet.offsetV_init;
        this.mMaterialSet.scaleU = this.mMaterialSet.scaleU_init;
        this.mMaterialSet.scaleV = this.mMaterialSet.scaleV_init;
        this.updateMaterial(this.mMaterialSet);
        this.updateGeometry();
        this.notifyChange(ElementChangedFlags.PropertyValues);
    }
    setShapeHeight(shapeLevel) {
        if (this.mType !== "Rectangle" && this.mType !== "Circle" &&
            this.mType !== "Line" && this.mType !== "Spline" && this.mType !== "BackgroundImage") {
            return;
        }
        switch (shapeLevel) {
            case ShapeLevel.default:
                this.mShapeLevel = ShapeLevel.default;
                // this.m2dGroup.position.y = RoomManager.ROOM_2dSHAPE_POSITION_DEFAULT_Y;
                // this.m3dGroup.position.y = RoomManager.ROOM_2dSHAPE_POSITION_DEFAULT_Y;
                this.mNode.position.y = RoomManager.ROOM_2dSHAPE_POSITION_DEFAULT_Y;
                break;
            case ShapeLevel.OnTop:
                this.mShapeLevel = ShapeLevel.OnTop;
                // this.m2dGroup.position.y = RoomManager.ROOM_2dSHAPE_POSITION_ONTOP_Y;
                // this.m3dGroup.position.y = RoomManager.ROOM_2dSHAPE_POSITION_ONTOP_Y;
                this.mNode.position.y = RoomManager.ROOM_2dSHAPE_POSITION_ONTOP_Y;
                break;
            default:
                break;
        }
        this.updateTransformation();
    }
    // tslint:disable-next-line:cyclomatic-complexity
    async setColor(pColor) {
        if (this.mColor === pColor)
            return (false);
        this.mColor = pColor;
        if (this.mMaterialSet.category === "color") {
            this.generateMaterialId();
        }
        await this.setMeshMaterial1();
        if (this.mType === "Floor")
            this.mRoomManager.setFloorMaterialId(this.mWallIds, this.mMaterialSet);
        this.beginChange();
        let t3dNodes = this.m3dGroup.getChildTransformNodes(true);
        for (let tNode of t3dNodes) {
            let tN = tNode;
            if (tN != null) {
                tN.material = this.localMaterial;
            }
            if (this.mType === "Floor") {
                tN.visibility = this.Params.floorTransparent ? RoomManager.ROOM_FLOOR_OPACITY_VALUE : 1;
            }
        }
        let t2dNodes = this.m2dGroup.getChildTransformNodes(true);
        for (let tNode of t2dNodes) {
            let tN = tNode;
            if (tN != null) {
                tN.material = this.localMaterial;
            }
            if (this.mType === "Floor") {
                tN.visibility = this.Params.floorTransparent ? RoomManager.ROOM_FLOOR_OPACITY_VALUE : 1;
            }
        }
        this.endChange();
        if (this.mType === "SafetyZone")
            this.mRoomManager.setSafetyZoneData(this.mBasketId, this.mMaterial, this.mColor, this.mMaterialSet.category, this.mMaterialSet.value, this.mMaterialId);
        this.notifyChange(ElementChangedFlags.PropertyValues);
        this.notifyChange(ElementChangedFlags.Properties);
        this.mRoomManager.contentChanged();
        return (true);
    }
    setText(pText, pUpdateGeometry = true) {
        if (this.mText === pText)
            return (false);
        this.mText = pText;
        this.beginChange();
        if (pUpdateGeometry)
            this.updateGeometry();
        this.notifyChange(ElementChangedFlags.PropertyValues);
        this.endChange();
        this.mRoomManager.contentChanged();
        return (true);
    }
    setTextColor(pTextColor) {
        if (this.mTextColor === pTextColor)
            return (false);
        this.mTextColor = pTextColor;
        this.beginChange();
        this.updateGeometry();
        this.notifyChange(ElementChangedFlags.PropertyValues);
        this.endChange();
        this.mRoomManager.contentChanged();
        return (true);
    }
    setTextHeight(pMeter, pUpdateGeometry = true) {
        let tValue = pMeter;
        tValue = clamp(tValue, 0.01, 10);
        if (eq(this.mTextHeight, tValue))
            return (false);
        this.mTextHeight = tValue;
        this.beginChange();
        if (pUpdateGeometry)
            this.updateGeometry();
        this.notifyChange(ElementChangedFlags.PropertyValues);
        this.endChange();
        this.mRoomManager.contentChanged();
        return (true);
    }
    getEOXDescription(pDocument, pParentNode) {
        if (this.geometry != null)
            this.geometry.getEOXDescription(pDocument, pParentNode);
    }
    addCuttedRE(pRoomElement) {
        if (!contains(this.mCuttedRoomElements, pRoomElement)) {
            this.mCuttedRoomElements.push(pRoomElement);
        }
    }
    addPoint(pIndex) {
        let tElement = null;
        if (this.mType === "Line") {
            tElement = this.geometry;
        }
        if (this.mType === "Spline") {
            tElement = this.geometry;
        }
        if (this.mType === "Road") {
            tElement = this.geometry;
        }
        if (this.mType === "Hedge") {
            tElement = this.geometry;
        }
        if (this.mType === "Fence") {
            tElement = this.geometry;
        }
        if (this.mType === "CameraPath") {
            tElement = this.geometry;
        }
        if (tElement == null) {
            return;
        }
        tElement.addPoint(pIndex);
        if (this.mType === "CameraPath") {
            tElement.computeCamPathData();
        }
        this.updateGeometry(true, false);
        this.mRoomManager.app.model.setSelection([this]);
    }
    deletePoint(pIndex) {
        let tElement = null;
        if (this.mType === "Line") {
            tElement = this.geometry;
        }
        if (this.mType === "Spline") {
            tElement = this.geometry;
        }
        if (this.mType === "Road") {
            tElement = this.geometry;
        }
        if (this.mType === "Hedge") {
            tElement = this.geometry;
        }
        if (this.mType === "Fence") {
            tElement = this.geometry;
        }
        if (this.mType === "CameraPath") {
            tElement = this.geometry;
        }
        if (tElement == null) {
            return;
        }
        tElement.deletePoint(pIndex);
        if (this.mType === "CameraPath") {
            tElement.computeCamPathData();
        }
        this.updateGeometry(true, false);
        this.mRoomManager.app.model.setSelection([this]);
    }
    sort3DLines() {
        let tnewOut = new Array();
        tnewOut.push(this.mOutlines[0][0]);
        let tcheckPoint = this.mOutlines[0][1];
        for (let i = 1; i < this.mOutlines.length; i++) {
            for (let j = 1; j < this.mOutlines.length; j++) {
                let tLine = this.mOutlines[j];
                let tP1 = tLine[0];
                let tP2 = tLine[1];
                let tsP1 = Util.samePointsV3(tcheckPoint, tP1, 0);
                let tsP2 = Util.samePointsV3(tcheckPoint, tP2, 0);
                if (tsP1 || tsP2) {
                    if (tsP1) {
                        tnewOut.push(tP1);
                        tcheckPoint = tP2;
                        break;
                    }
                    if (tsP2) {
                        tnewOut.push(tP2);
                        tcheckPoint = tP1;
                        break;
                    }
                }
            }
        }
    }
    getOutlines() {
        let tRes = new Array();
        let tCAG = this.mCAG;
        if (tCAG == null)
            return (tRes);
        let tLines = new Array();
        for (let tS of tCAG.sides) {
            let tLine = new Array();
            let tSide = tS;
            let tP1 = new Vector3(tSide.vertex0.pos.x, 0, tSide.vertex0.pos.y);
            let tP2 = new Vector3(tSide.vertex1.pos.x, 0, tSide.vertex1.pos.y);
            tLine.push(tP1, tP2);
            tLines.push(tLine);
        }
        let tPerimeter = 0;
        for (let tLine of tLines) {
            tPerimeter += Vector3.Distance(tLine[0], tLine[1]);
        }
        this.mPerimeter = tPerimeter;
        this.notifyChange(ElementChangedFlags.PropertyValues);
        return (tLines);
    }
    getOutlinesOfCAG(pCAG) {
        let tRes = new Array();
        if (pCAG == null)
            return (tRes);
        let tLines = new Array();
        for (let tS of pCAG.sides) {
            let tLine = new Array();
            let tSide = tS;
            let tP1 = new Vector3(tSide.vertex0.pos.x, 0, tSide.vertex0.pos.y);
            let tP2 = new Vector3(tSide.vertex1.pos.x, 0, tSide.vertex1.pos.y);
            tLine.push(tP1, tP2);
            tLines.push(tLine);
        }
        return (tLines);
    }
    getFence_FieldMeshes() {
        let tRes = new Array();
        let tMeshes = this.m3dGroup.getChildMeshes();
        if (tMeshes.length === 0)
            return (tRes);
        for (let tMesh of tMeshes) {
            if (tMesh == null)
                continue;
            if (tMesh.name === "fence_field")
                tRes.push(tMesh);
        }
        return (tRes);
    }
    getFence_PostMeshes() {
        let tRes = new Array();
        let tMeshes = this.m3dGroup.getChildMeshes();
        if (tMeshes.length === 0)
            return (tRes);
        for (let tMesh of tMeshes) {
            if (tMesh == null)
                continue;
            if (tMesh.name === "fence_post")
                tRes.push(tMesh);
        }
        return (tRes);
    }
    /** @internal */
    static _registerClass(pRoomManager) {
        pRoomManager.app.model.registerElementLoader(ROOM_ELEMENT_CLASSID, (pApp, pContext, pIOElement) => {
            return (RoomElement.deserialize(pRoomManager, pContext, pIOElement));
        });
    }
    //// protected /////////////////////////////////////////////////////////////////////////////
    handleImmediateChanges() {
        super.handleImmediateChanges();
        if (this.wasFlagChanged(ElementChangedFlags.Added))
            this.mRoomManager._addRoomElement(this);
        if (this.wasFlagChanged(ElementChangedFlags.Removed)) {
            const tREToUpdate = this.mRoomManager.addOverlappingShapes([this], true);
            this.mRoomManager._removeRoomElement(this);
            if (tREToUpdate.length > 0) {
                for (let tRE of tREToUpdate) {
                    tRE.updateGeometry();
                    tRE.setMeshVisibility(1);
                }
                this.mRoomManager.cutOverlappingShapes();
            }
        }
    }
    handleDelayedChanges() {
        super.handleDelayedChanges();
        if (this.wasFlagChanged(ElementChangedFlags.Transformation) && this.mHandleTansformationChange) {
            if (this.mRoomManager._transformationStarted()) {
                this.mPendingTransformMatrix = this.transform.clone();
                this.mRoomManager._addTransformedElement(this);
            }
            else
                this.updateMatrix(); // reset transform
        }
    }
    // tslint:disable-next-line:cyclomatic-complexity
    copyDataFrom(pElement) {
        super.copyDataFrom(pElement);
        if (pElement == null || !(pElement instanceof RoomElement)) {
            return;
        }
        let tSourceElement = pElement;
        this.mWidth = tSourceElement.mWidth;
        this.mHeight = tSourceElement.mHeight;
        this.mDepth = tSourceElement.mDepth;
        this.mColor = tSourceElement.mColor;
        if (tSourceElement.node.rotationQuaternion != null) {
            this.mNode.rotationQuaternion = tSourceElement.node.rotationQuaternion.clone();
        }
        if (this.mType === "Road") {
            this.mcurvedRoad = tSourceElement.curvedRoad();
            this.mParams.curvedRoad = tSourceElement.Params.curvedRoad;
            this.mParams.roadWidth = tSourceElement.Params.roadWidth;
        }
        if (this.mType === "Hedge") {
            this.mcurvedHedge = tSourceElement.curvedHedge();
            this.mParams.curvedHedge = tSourceElement.Params.curvedHedge;
            this.mParams.HedgeDepth = tSourceElement.Params.HedgeDepth;
        }
        if (this.mType !== "Text" && this.mType !== "Arena") {
            //this.mMeshMaterial = tSourceElement.mMeshMaterial.clone("");
            this.mMaterialId = tSourceElement.mMaterialId;
            this.setMaterialId(this.mMaterialId);
        }
        if (this.mType == "Sphere" && tSourceElement.mParams.audioSphere != null) {
            this.mParams.audioSphere = tSourceElement.mParams.audioSphere;
            if (tSourceElement.mParams.soundEffectId != null) {
                this.mParams.soundEffectId = tSourceElement.mParams.soundEffectId;
            }
        }
        this.mParams.terrainType = tSourceElement.mParams.terrainType;
        this.mParams.hillHeight = tSourceElement.mParams.hillHeight;
        this.mParams.hillOffset = tSourceElement.mParams.hillOffset;
        this.mParams.hillProfile = tSourceElement.mParams.hillProfile;
        this.mParams.hillTopOffset = tSourceElement.mParams.hillTopOffset.clone();
        this.mMaterial = tSourceElement.mMaterial;
        this.mMaterialSet.category = tSourceElement.mMaterialSet.category;
        this.mMaterialSet.loadMatId = "";
        this.mMaterialSet.rotation = tSourceElement.mMaterialSet.rotation;
        this.mMaterialSet.offsetV = tSourceElement.mMaterialSet.offsetV;
        this.mMaterialSet.offsetV_init = tSourceElement.mMaterialSet.offsetV_init;
        this.mMaterialSet.offsetU = tSourceElement.mMaterialSet.offsetU;
        this.mMaterialSet.offsetU_init = tSourceElement.mMaterialSet.offsetU_init;
        this.mMaterialSet.scaleU = tSourceElement.mMaterialSet.scaleU;
        this.mMaterialSet.scaleU_init = tSourceElement.mMaterialSet.scaleU_init;
        this.mMaterialSet.scaleV = tSourceElement.mMaterialSet.scaleV;
        this.mMaterialSet.scaleV_init = tSourceElement.mMaterialSet.scaleV_init;
        this.mMaterialSet.setInit = tSourceElement.mMaterialSet.setInit;
        this.mMaterialSet.value = tSourceElement.mMaterialSet.value;
        if (this.mType === "Arena") {
            this.mMaterialSet_Arena_PlayField.category = tSourceElement.mMaterialSet_Arena_PlayField.category;
            this.mMaterialSet_Arena_PlayField.loadMatId = "";
            this.mMaterialSet_Arena_PlayField.rotation = tSourceElement.mMaterialSet_Arena_PlayField.rotation;
            this.mMaterialSet_Arena_PlayField.offsetV = tSourceElement.mMaterialSet_Arena_PlayField.offsetV;
            this.mMaterialSet_Arena_PlayField.offsetV_init = tSourceElement.mMaterialSet_Arena_PlayField.offsetV_init;
            this.mMaterialSet_Arena_PlayField.offsetU = tSourceElement.mMaterialSet_Arena_PlayField.offsetU;
            this.mMaterialSet_Arena_PlayField.offsetU_init = tSourceElement.mMaterialSet_Arena_PlayField.offsetU_init;
            this.mMaterialSet_Arena_PlayField.scaleU = tSourceElement.mMaterialSet_Arena_PlayField.scaleU;
            this.mMaterialSet_Arena_PlayField.scaleU_init = tSourceElement.mMaterialSet_Arena_PlayField.scaleU_init;
            this.mMaterialSet_Arena_PlayField.scaleV = tSourceElement.mMaterialSet_Arena_PlayField.scaleV;
            this.mMaterialSet_Arena_PlayField.scaleV_init = tSourceElement.mMaterialSet_Arena_PlayField.scaleV_init;
            this.mMaterialSet_Arena_PlayField.value = tSourceElement.mMaterialSet_Arena_PlayField.value;
            this.mMaterialSet_Arena_SideSegments.category = tSourceElement.mMaterialSet_Arena_SideSegments.category;
            this.mMaterialSet_Arena_SideSegments.loadMatId = "";
            this.mMaterialSet_Arena_SideSegments.rotation = tSourceElement.mMaterialSet_Arena_SideSegments.rotation;
            this.mMaterialSet_Arena_SideSegments.offsetV = tSourceElement.mMaterialSet_Arena_SideSegments.offsetV;
            this.mMaterialSet_Arena_SideSegments.offsetV_init = tSourceElement.mMaterialSet_Arena_SideSegments.offsetV_init;
            this.mMaterialSet_Arena_SideSegments.offsetU = tSourceElement.mMaterialSet_Arena_SideSegments.offsetU;
            this.mMaterialSet_Arena_SideSegments.offsetU_init = tSourceElement.mMaterialSet_Arena_SideSegments.offsetU_init;
            this.mMaterialSet_Arena_SideSegments.scaleU = tSourceElement.mMaterialSet_Arena_SideSegments.scaleU;
            this.mMaterialSet_Arena_SideSegments.scaleU_init = tSourceElement.mMaterialSet_Arena_SideSegments.scaleU_init;
            this.mMaterialSet_Arena_SideSegments.scaleV = tSourceElement.mMaterialSet_Arena_SideSegments.scaleV;
            this.mMaterialSet_Arena_SideSegments.scaleV_init = tSourceElement.mMaterialSet_Arena_SideSegments.scaleV_init;
            this.mMaterialSet_Arena_SideSegments.value = tSourceElement.mMaterialSet_Arena_SideSegments.value;
            this.mMaterialSet_Arena_TrackLines.category = tSourceElement.mMaterialSet_Arena_TrackLines.category;
            this.mMaterialSet_Arena_TrackLines.loadMatId = "";
            this.mMaterialSet_Arena_TrackLines.rotation = tSourceElement.mMaterialSet_Arena_TrackLines.rotation;
            this.mMaterialSet_Arena_TrackLines.offsetV = tSourceElement.mMaterialSet_Arena_TrackLines.offsetV;
            this.mMaterialSet_Arena_TrackLines.offsetV_init = tSourceElement.mMaterialSet_Arena_TrackLines.offsetV_init;
            this.mMaterialSet_Arena_TrackLines.offsetU = tSourceElement.mMaterialSet_Arena_TrackLines.offsetU;
            this.mMaterialSet_Arena_TrackLines.offsetU_init = tSourceElement.mMaterialSet_Arena_TrackLines.offsetU_init;
            this.mMaterialSet_Arena_TrackLines.scaleU = tSourceElement.mMaterialSet_Arena_TrackLines.scaleU;
            this.mMaterialSet_Arena_TrackLines.scaleU_init = tSourceElement.mMaterialSet_Arena_TrackLines.scaleU_init;
            this.mMaterialSet_Arena_TrackLines.scaleV = tSourceElement.mMaterialSet_Arena_TrackLines.scaleV;
            this.mMaterialSet_Arena_TrackLines.scaleV_init = tSourceElement.mMaterialSet_Arena_TrackLines.scaleV_init;
            this.mMaterialSet_Arena_TrackLines.value = tSourceElement.mMaterialSet_Arena_TrackLines.value;
            this.mMaterialId = tSourceElement.mMaterialId;
            this.mMaterialId_Arena_PlayField = tSourceElement.mMaterialId_Arena_PlayField;
            this.mMaterialId_Arena_SideSegments = tSourceElement.mMaterialId_Arena_SideSegments;
            this.mMaterialId_Arena_TrackLines = tSourceElement.mMaterialId_Arena_TrackLines;
        }
        this.m3dGroup.position.y = tSourceElement.m3dGroup.position.y;
        this.m2dGroup.position.y = tSourceElement.m2dGroup.position.y;
        if (this.mText != null)
            this.mText = tSourceElement.mText;
        if (this.mTextColor != null)
            this.mTextColor = tSourceElement.mTextColor;
        if (this.mTextHeight != null)
            this.mTextHeight = tSourceElement.mTextHeight;
        if (tSourceElement.mImageURL !== "")
            this.mImageURL = tSourceElement.mImageURL;
        if (tSourceElement.mGeometry != null) {
            if (tSourceElement.mGeometry.getType() === "Spline") {
                let tPoints = tSourceElement.mGeometry.getPoints();
                let tnewPoints = [];
                for (let tP of tPoints) {
                    tnewPoints.push(tP.clone());
                }
                this.mGeometry.setPoints(tnewPoints);
            }
            if (tSourceElement.mGeometry.getType() === "Line") {
                let tPoints = tSourceElement.mGeometry.getPoints();
                this.mGeometry.setPoints(tPoints);
            }
            if (tSourceElement.mGeometry.getType() === "SafetyZone") {
                let tCAG = tSourceElement.CAG;
                let tCAGoriginal = tSourceElement.CAGoriginal;
                let tCAG_FS = tSourceElement.CAG_FS;
                let tCAG_notVisible = tSourceElement.CAG_notVisible;
                if (tCAG != null)
                    this.setCAG(tCAG);
                if (tCAGoriginal != null)
                    this.setCAGoriginal(tCAGoriginal);
                if (tCAG_FS != null)
                    this.setCAG_FS(tCAG_FS);
                if (tCAG_notVisible != null)
                    this.setCAG_notVisible(tCAG_notVisible);
                let tFSPoints = tSourceElement.mGeometry.getSZFreeSpacePoints();
                this.mGeometry.setFreeSpacePoints(tFSPoints);
            }
            if (tSourceElement.mGeometry.getType() === "Rectangle") {
                let tPoints = tSourceElement.mGeometry.getPoints();
                let tnewPoints = [];
                for (let tP of tPoints) {
                    tnewPoints.push(tP.clone());
                }
                this.mGeometry.setPoints(tnewPoints);
            }
            if (tSourceElement.mGeometry.getType() === "Circle") {
                let tPoints = tSourceElement.mGeometry.getPoints();
                this.mGeometry.setPoints(tPoints);
            }
            if (tSourceElement.mGeometry.getType() === "Sphere") {
                let tPoints = tSourceElement.mGeometry.getPoints();
                this.mGeometry.setPoints(tPoints);
            }
            if (tSourceElement.mGeometry.getType() === "CustomBuilding") {
                let tPoints = tSourceElement.mGeometry.getPoints();
                this.mGeometry.setPoints(tPoints);
                let tCAG = tSourceElement.CAG;
                let tCAGoriginal = tSourceElement.CAGoriginal;
                let tCAG_notVisible = tSourceElement.CAG_notVisible;
                if (tCAG != null)
                    this.setCAG(tCAG);
                if (tCAGoriginal != null)
                    this.setCAGoriginal(tCAGoriginal);
                if (tCAG_notVisible != null)
                    this.setCAG_notVisible(tCAG_notVisible);
            }
            if (tSourceElement.mGeometry.getType() === "Road") {
                let tPoints = tSourceElement.mGeometry.getPoints();
                this.mGeometry.setPoints(tPoints);
                let tCAG = tSourceElement.CAG;
                let tCAGoriginal = tSourceElement.CAGoriginal;
                let tCAG_notVisible = tSourceElement.CAG_notVisible;
                if (tCAG != null)
                    this.setCAG(tCAG);
                if (tCAGoriginal != null)
                    this.setCAGoriginal(tCAGoriginal);
                if (tCAG_notVisible != null)
                    this.setCAG_notVisible(tCAG_notVisible);
            }
            if (tSourceElement.mGeometry.getType() === "Hedge") {
                let tPoints = tSourceElement.mGeometry.getPoints();
                this.mGeometry.setPoints(tPoints);
                let tCAG = tSourceElement.CAG;
                let tCAGoriginal = tSourceElement.CAGoriginal;
                let tCAG_notVisible = tSourceElement.CAG_notVisible;
                if (tCAG != null)
                    this.setCAG(tCAG);
                if (tCAGoriginal != null)
                    this.setCAGoriginal(tCAGoriginal);
                if (tCAG_notVisible != null)
                    this.setCAG_notVisible(tCAG_notVisible);
            }
            if (tSourceElement.mGeometry.getType() === "Fence") {
                let tPoints = tSourceElement.mGeometry.getPoints();
                this.mGeometry.setPoints(tPoints);
                let tCAG = tSourceElement.CAG;
                let tCAGoriginal = tSourceElement.CAGoriginal;
                let tCAG_notVisible = tSourceElement.CAG_notVisible;
                if (tCAG != null)
                    this.setCAG(tCAG);
                if (tCAGoriginal != null)
                    this.setCAGoriginal(tCAGoriginal);
                if (tCAG_notVisible != null)
                    this.setCAG_notVisible(tCAG_notVisible);
            }
            if (tSourceElement.mGeometry.getType() === "Arena") {
                this.mParams.arenaCircle = tSourceElement.mParams.arenaCircle;
                this.mParams.arenaFieldScale = tSourceElement.mParams.arenaFieldScale;
                this.mParams.arenaLaneCount = tSourceElement.mParams.arenaLaneCount;
                this.mParams.arenaLaneScale = tSourceElement.mParams.arenaLaneScale;
                this.mParams.arenaPlayFieldVisible = tSourceElement.mParams.arenaPlayFieldVisible;
                this.mParams.arenaSideSegmentsVisible = tSourceElement.mParams.arenaSideSegmentsVisible;
                let tCAG = tSourceElement.CAG;
                let tCAGoriginal = tSourceElement.CAGoriginal;
                let tCAG_notVisible = tSourceElement.CAG_notVisible;
                let tCAG_InField = tSourceElement.CAG_Arena_PlayField;
                let tCAG_RunningTracks = tSourceElement.CAG_Arena_RunningTracks;
                let tCAG_SideSegments = tSourceElement.CAG_Arena_SideSegments;
                let tCAG_TrackLines = tSourceElement.CAG_Arena_TrackLines;
                if (tCAG != null) {
                    this.setCAG(tCAG);
                }
                if (tCAGoriginal != null) {
                    this.setCAGoriginal(tCAGoriginal);
                }
                if (tCAG_notVisible != null) {
                    this.setCAG_notVisible(tCAG_notVisible);
                }
                if (tCAG_InField != null) {
                    this.setCAG_Arena_PlayField(tCAG_InField);
                }
                if (tCAG_RunningTracks != null) {
                    this.setCAG_Arena_RunningTracks(tCAG_RunningTracks);
                }
                if (tCAG_SideSegments != null) {
                    this.setCAG_Arena_SideSegments(tCAG_SideSegments);
                }
                if (tCAG_TrackLines != null) {
                    this.setCAG_Arena_TrackLines(tCAG_TrackLines);
                }
            }
        }
        this.mFloorOffset = tSourceElement.mFloorOffset;
        let tData = tSourceElement._saveUndoData();
        this._loadUndoData(tData);
    }
    _clearTimeouts() {
        for (const tTO of this.marr_TimeOut) {
            clearTimeout(tTO);
        }
    }
    afterClone() {
        if (this.mInitialClone) {
            this.mInitialClone = false;
            this.mRoomManager.cutOverlappingShapes();
            this.mRoomManager.contentChanged();
        }
    }
    onBeginTransform(pRoomElement) {
        return () => {
            if (pRoomElement.node == null) {
                return;
            }
            this.misTransforming = true;
            let tMainElement = pRoomElement.getMainElement();
            if (tMainElement.isSelected) {
                if (!(this.mType === "Fence")) {
                    this.setMeshVisibility(0.5);
                }
                if (this.mType === "Line") {
                    const tgeo = this.mGeometry;
                    if (tgeo != null) {
                        tgeo.hideCornerAngles();
                    }
                }
                if (this.mType === "SafetyZone" && this.mRoomManager.app.model.layers.isLayerVisible("room_safetyzones")) {
                    this.mRoomManager.app.model.layers.setLayerVisible("room_safetyzones", false);
                    this.mRoomManager.app.model.layers.setLayerVisible("room:PerimeterLine_Layer", false);
                    this.mRoomManager.app.model.layers.setLayerVisible("room:FreeSpaceLine_Layer", true);
                    this.mRoomManager.app.model.layers.setLayerVisible("room:PerimeterLineOriginal_Layer", true);
                    this.mRoomManager.TransformSafetyZone();
                }
                else {
                    if (this.mType !== "GeoMarker") {
                        this.mRoomManager.TransformShape();
                    }
                }
            }
        };
    }
    onEndTransform(pRoomElement) {
        return () => {
            if (pRoomElement.node == null) {
                return;
            }
            this.misTransforming = false;
            let tMainElement = pRoomElement.getMainElement();
            const tposY = pRoomElement.mNode.position.y;
            if (pRoomElement.type !== "SafetyZone" &&
                (!Util.numbersEqual(tposY, RoomManager.ROOM_2dSHAPE_POSITION_DEFAULT_Y) &&
                    !Util.numbersEqual(tposY, RoomManager.ROOM_2dSHAPE_POSITION_ONTOP_Y))) {
                this.mShapeLevel = ShapeLevel.manual;
            }
            if (tMainElement.isSelected) {
                this.setMeshVisibility(1);
                if (this.mType === "CustomBuilding")
                    this.setBuildingOpacity(this.mBuildingOpacity, true);
                this.mRoomManager.app.model.layers.setLayerVisible("room_safetyzones", this.mRoomManager.SafetyZonesVisible);
                this.mRoomManager.app.model.layers.setLayerVisible("room:PerimeterLine_Layer", this.mRoomManager.SafetyZonesVisible);
                this.mRoomManager.app.model.layers.setLayerVisible("room:FreeSpaceLine_Layer", this.mRoomManager.SafetyZonesVisible);
                this.mRoomManager.app.model.layers.setLayerVisible("room:PerimeterLineOriginal_Layer", false);
            }
        };
    }
    onLengthUnitChanged(pRoomElement) {
        return () => {
            pRoomElement.notifyChange(ElementChangedFlags.PropertyValues);
            pRoomElement.notifyChange(ElementChangedFlags.Properties);
        };
    }
    onBeforeCameraChanged(pRoomElement) {
        return () => {
            this.getScaleFactor();
            if (pRoomElement.type === "CameraPath") {
                const tcamGeo = pRoomElement.geometry;
                if (tcamGeo == null) {
                    return;
                }
                tcamGeo.compute2dProj();
            }
            if (pRoomElement.type === "Line") {
                const tlineGeo = pRoomElement.geometry;
                if (tlineGeo != null) {
                    tlineGeo.updateCornerAngles();
                }
            }
            if (pRoomElement.type === "GeoMarker" &&
                pRoomElement.geometry != null) {
                pRoomElement.geometry.updateMesh();
            }
        };
    }
    getScaleFactor() {
        if (this.mRoomManager.hoverView == null)
            return;
        let tScaleFactor = Util.getViewScaleFactorOfNode(this.mRoomManager.hoverView, this.mNode, 0.1, RoomElement.NODESCALE);
        if (tScaleFactor == null)
            return;
        this.mScaleFactor = tScaleFactor;
    }
    updateGeoPosition() {
        if (this.mType !== "GeoMarker" || this.mInitalPosition == null) {
            return;
        }
        // avoid NaN
        let tcurrPos = Util.to2d(this.mPosition.clone());
        if (Util.vectorEquals2(tcurrPos, Vector2.Zero())) {
            tcurrPos.x = 0.0001;
        }
        // angle clockwise (0 - 360)
        let tdistAngle = radToDeg(signedAngleBetweenVectors2(VECTOR2_Y, tcurrPos));
        if (isNaN(tdistAngle)) {
            tdistAngle = 0;
        }
        if (tdistAngle < 0) {
            tdistAngle *= -1;
        }
        else {
            tdistAngle = 180 + (180 - tdistAngle);
        }
        tdistAngle += (-1) * this.mInitGeo_MapRotation;
        const tdist = Vector2.Distance(tcurrPos, Util.to2d(this.mInitalPosition));
        const tnewGeoPos = this.destinationPoint(this.mInitGeo_Latitude, this.mInitGeo_Longitude, tdist, tdistAngle);
        this.mgeo_Latitude = tnewGeoPos.lat;
        this.mgeo_Longitude = tnewGeoPos.lon;
        this.mRoomManager.eventGeoMarkerMoved.trigger({});
        this.updateGeometry();
    }
    destinationPoint(pLat0, pLon0, pDist, pdistAngle) {
        const tradiusEarth = 6371e3;
        const tDist = pDist / tradiusEarth;
        const tdistAngle = degToRad(pdistAngle);
        const tLat0 = degToRad(pLat0);
        const tLon0 = degToRad(pLon0);
        const tsinLat2 = Math.sin(tLat0) * Math.cos(tDist) + Math.cos(tLat0) * Math.sin(tDist) * Math.cos(tdistAngle);
        const tLat2 = Math.asin(tsinLat2);
        const ty = Math.sin(tdistAngle) * Math.sin(tDist) * Math.cos(tLat0);
        const tx = Math.cos(tDist) - Math.sin(tLat0) * tsinLat2;
        const tLon2 = tLon0 + Math.atan2(ty, tx);
        const tLat2Deg = radToDeg(tLat2);
        const tLon2Deg = radToDeg(tLon2);
        const tRes = { lat: tLat2Deg, lon: tLon2Deg };
        return (tRes);
    }
    updateWallAttachData() {
        if (this.mWall == null) {
            this.mWallAttachData = null;
            return;
        }
        this.mWallAttachData = {};
        this.mWallAttachData.start = this.mWall.startPoint.clone();
        this.mWallAttachData.end = this.mWall.endPoint.clone();
        this.mWallAttachData.distanceStart = Vector2.Distance(this.mWallAttachData.start, Util.to2d(this.mPosition));
        this.mWallAttachData.distanceEnd = Vector2.Distance(this.mWallAttachData.end, Util.to2d(this.mPosition));
    }
    updateMatrix() {
        if (this.mNode === undefined)
            return;
        //let tWallAngle = this.mNode.rotation.z;
        //this.mNode.rotationQuaternion = Quaternion.RotationAxis(VECTOR3_Y, -tWallAngle);
        this.mNode.position = this.mPosition;
        this.mHandleTansformationChange = false;
        this.updateTransformation();
        this.mHandleTansformationChange = true;
    }
    setFloorOffset(pValue, pNotifyChange) {
        let tValue = clamp(pValue, 0.0, 100.0);
        if (eq(this.mFloorOffset, tValue))
            return;
        this.mFloorOffset = tValue;
        this.m3dGroup.position.y = this.mFloorOffset;
        this.beginChange();
        this.updateGeometry();
        this.notifyChange(ElementChangedFlags.PropertyValues);
        this.endChange();
        if (this.mWall != null)
            this.mWall.updateGeometry();
        this.mRoomManager.contentChanged();
    }
    setDefaultMATCategory(pValue) {
        this.mMaterialSet.category = pValue;
        let tfirstDefaultMat = this.mRoomManager.firstextMaterialofCategory(this.mMaterialSet.category);
        if (tfirstDefaultMat !== "")
            this.setMaterialId(this.mMaterialSet.category + this.mRoomManager.materialSplitStr + tfirstDefaultMat);
    }
    setInitialMaterial(pMatCat, pMatValue) {
        let textMat = this.mRoomManager.findextMaterial(pMatCat, pMatValue);
        if (textMat != null)
            this.setMaterialId(pMatCat + this.mRoomManager.materialSplitStr + pMatValue);
    }
    // tslint:disable-next-line:cyclomatic-complexity
    setMeshVisibility_Grid(pOpacity, ponlySetValue = false) {
        let tOpacity = pOpacity;
        this.mGroupMeshOpacity = tOpacity;
        let trenderingGroupId = tOpacity === 1 ? 0 : 1;
        if (ponlySetValue || this.REforGrid_REF == null) {
            return;
        }
        for (let tLine of this.REforGrid_REF) {
            if (tLine.group3d != null) {
                let t3dMeshes = tLine.group3d.getChildMeshes(true);
                for (let tMesh of t3dMeshes) {
                    if (tMesh.name.indexOf("pickingMesh") === 0)
                        continue;
                    if (this.mType === "SafetyZone") {
                        tMesh.visibility = this.mSZ_visible ? 1 : 0;
                    }
                    else {
                        tMesh.visibility = tOpacity;
                    }
                    tMesh.renderingGroupId = trenderingGroupId;
                }
            }
            if (tLine.group2d != null) {
                let t2dMeshes = tLine.group2d.getChildMeshes(true);
                for (let tMesh of t2dMeshes) {
                    if (tMesh.name.indexOf("pickingMesh") === 0)
                        continue;
                    if (this.mType === "SafetyZone") {
                        tMesh.visibility = this.mSZ_visible ? 1 : 0;
                    }
                    else {
                        tMesh.visibility = tOpacity;
                    }
                    tMesh.renderingGroupId = trenderingGroupId;
                }
            }
        }
    }
    setREDimensionFromCAG() {
        if (this.mCAGoriginal != null) {
            let tCAGdimensions = Util.getCAGMeasure(this.mCAGoriginal);
            this.mWidth = tCAGdimensions.x;
            this.mDepth = tCAGdimensions.z;
        }
        else if (this.mCAG != null) {
            let tCAGdimensions = Util.getCAGMeasure(this.mCAG);
            this.mWidth = tCAGdimensions.x;
            this.mDepth = tCAGdimensions.z;
        }
    }
    setSoundSet_init() {
        const tfirstEntry = this.mRoomManager.firstextSoundofCategory(RoomManager.AUDIO_LOCAL_EFFECT);
        this.mSoundSet = {};
        this.mSoundSet.category = 'local_effect';
        this.mSoundSet.default = false;
        this.mSoundSet.description = '';
        this.mSoundSet.fileURL = '';
        this.mSoundSet.filter_exclude = '';
        this.mSoundSet.filter_include = '';
        this.mSoundSet.id = '';
        if (tfirstEntry != null) {
            this.mSoundSet.default = false;
            this.mSoundSet.description = tfirstEntry.description;
            this.mSoundSet.fileURL = tfirstEntry.fileURL;
            this.mSoundSet.filter_exclude = tfirstEntry.filter_exclude;
            this.mSoundSet.filter_include = tfirstEntry.filter_include;
            this.mSoundSet.id = tfirstEntry.id;
        }
    }
    initElementParams() {
        this.mParams.ElementOriginalPositionX = 0;
        this.mParams.ElementOriginalPositionY = 0;
        this.mParams.ElementOriginalPositionZ = 0;
        this.mParams.ElementPoints = new Array();
        this.mParams.ElementOriginalPoints = new Array();
    }
    // tslint:disable-next-line:cyclomatic-complexity
    getNeighbour(pEdge, pLeft) {
        let tRes = {
            wall: null,
            opening: null
        };
        if (this.mWall == null)
            return (tRes);
        let tRoomElements = arrClone(this.mWall.roomElements);
        //remove openings that are not on the same height level
        let tLow1 = this.mFloorOffset;
        let tHigh1 = this.mFloorOffset + this.mHeight;
        let i = 0;
        while (i < tRoomElements.length) {
            let tLow2 = tRoomElements[i].mFloorOffset;
            let tHigh2 = tRoomElements[i].mFloorOffset + tRoomElements[i].mHeight;
            if (tRoomElements[i] !== this && (tHigh2 < tLow1 || tLow2 > tHigh1)) {
                tRoomElements.splice(i, 1); //remove
            }
            else
                ++i;
        }
        // this can only happen if this is not yet added to the opening list of mWall
        if (tRoomElements.length === 0)
            return (tRes);
        tRoomElements.sort(this.cmpWallElements);
        //find neighbour openings
        if (pLeft) {
            if (tRoomElements[0] !== this) {
                for (let x = 0; x < tRoomElements.length; ++x) {
                    if (tRoomElements[x] === this) {
                        tRes.opening = tRoomElements[x - 1];
                        break;
                    }
                }
            }
        }
        else if (tRoomElements[tRoomElements.length - 1] !== this) {
            for (let j = 0; j < tRoomElements.length; ++j) {
                if (tRoomElements[j] === this) {
                    tRes.opening = tRoomElements[j + 1];
                    break;
                }
            }
        }
        //find neighbour wall
        let tWalls = new Array();
        let tTempWalls = new Array();
        if (pEdge === 0 || pEdge === 1) {
            this.mWall.getAllSideWalls(tTempWalls, tWalls, true, true);
        }
        else
            this.mWall.getAllSideWalls(tWalls, tTempWalls, true, true);
        if (pLeft)
            tWalls.reverse();
        let tVS = this.mWall.startPoint;
        let tV;
        let tDist = Vector2.Distance(Util.to2d(this.mPosition), tVS);
        let tDist1 = 0;
        let tDist2 = 0;
        if (tRes.opening != null)
            tDist1 = Vector2.Distance(Util.to2d(tRes.opening.mPosition), tVS);
        for (let tWall of tWalls) {
            if (tWall.startCxn.wall === this.mWall)
                tV = tWall.startPoint;
            else
                tV = tWall.endPoint;
            tDist2 = Vector2.Distance(tV, tVS);
            if (pLeft) {
                if ((tDist2 < tDist) && (tRes.opening == null || tDist1 < tDist2)) {
                    tRes.wall = tWall;
                    break;
                }
            }
            else if ((tDist2 > tDist) && (tRes.opening == null || tDist1 > tDist2)) {
                tRes.wall = tWall;
                break;
            }
        }
        return (tRes);
    }
    getSubElementSelection() {
        const tselection = this.mRoomManager.app.model.selection;
        if (tselection.length !== 1 || !this.mRoomManager.app.model.hasSubElementSelection()) {
            return (null);
        }
        let tRes = null;
        for (let telem of tselection) {
            if (telem === this) {
                const tsubsel = this.mRoomManager.app.model.getSubElementSelection(telem);
                if (tsubsel == null) {
                    break;
                }
                if (tsubsel instanceof RoomElement && tsubsel.type === "Line") {
                    tRes = tsubsel;
                    break;
                }
            }
        }
        return (tRes);
    }
    getextMaterialValue(pList) {
        let tRes = null;
        if (pList.length === 0)
            return (tRes);
        if (this.mMaterialId.trim() === "") {
            tRes = pList[0];
        }
        else {
            for (let tProp of pList) {
                if (tProp.value === this.mMaterialId) {
                    tRes = tProp;
                    break;
                }
            }
        }
        if (tRes == null)
            tRes = pList[0];
        return (tRes);
    }
    async changeTextureRotation(pMaterialSet, pValue) {
        let tValue = clamp(pValue, 0, 360);
        pMaterialSet.rotation = tValue;
        // clone texture if rotation is != 0
        if (pMaterialSet.rotation !== 0) {
            await this.mRoomManager.checkMaterial(pMaterialSet);
        }
        this.updateMaterial(pMaterialSet);
        this.updateGeometry();
        this.notifyChange(ElementChangedFlags.PropertyValues);
        return (Promise.resolve(true));
    }
    async changeTextureOffset_u(pMaterialSet, pValue) {
        let tValue = clamp(pValue, -10, 10);
        pMaterialSet.offsetU = -tValue;
        // clone texture if offsetU is != 0
        if (pMaterialSet.offsetU !== pMaterialSet.offsetU_init) {
            await this.mRoomManager.checkMaterial(pMaterialSet);
        }
        this.updateMaterial(pMaterialSet);
        this.updateGeometry();
        this.notifyChange(ElementChangedFlags.PropertyValues);
        return (Promise.resolve(true));
    }
    async changeTextureOffset_v(pMaterialSet, pValue) {
        let tValue = clamp(pValue, -10, 10);
        pMaterialSet.offsetV = -tValue;
        // clone texture if offsetV is != 0
        if (pMaterialSet.offsetV !== pMaterialSet.offsetV_init) {
            await this.mRoomManager.checkMaterial(pMaterialSet);
        }
        this.updateMaterial(pMaterialSet);
        this.updateGeometry();
        this.notifyChange(ElementChangedFlags.PropertyValues);
        return (Promise.resolve(true));
    }
    // tslint:disable-next-line:cyclomatic-complexity
    getTextureScaling(pdeserialize = false) {
        // if (this.mMaterialSet.loaded) {
        //     return;
        // }
        let tmatId = this.mRoomManager.getMaterialId(this.mMaterialSet);
        let tmatIdAddOn = tmatId + this.mMaterialSet.idAddOn;
        let tMat = this.mRoomManager.MatManager.getMaterial(tmatIdAddOn);
        if (tMat == null) {
            tMat = this.mRoomManager.MatManager.getMaterial(tmatId);
        }
        if (tMat != null && tMat.albedoTexture != null) {
            this.mMaterialSet.offsetU = tMat.albedoTexture.uOffset;
            this.mMaterialSet.offsetV = tMat.albedoTexture.vOffset;
            this.mMaterialSet.scaleU = tMat.albedoTexture.uScale;
            this.mMaterialSet.scaleV = tMat.albedoTexture.vScale;
            if (this.mMaterialSet.setInit === false && pdeserialize === false) {
                this.mMaterialSet.offsetU_init = this.mMaterialSet.offsetU;
                this.mMaterialSet.offsetV_init = this.mMaterialSet.offsetU;
                this.mMaterialSet.scaleU_init = this.mMaterialSet.scaleU;
                this.mMaterialSet.scaleV_init = this.mMaterialSet.scaleV;
                this.mMaterialSet.setInit = true;
            }
            // // if (this.mMaterialSet.offsetU_init == -999) {
            //     this.mMaterialSet.offsetU_init = this.mMaterialSet.offsetU;
            // // }
            // // if (this.mMaterialSet.offsetV_init == -999) {
            //     this.mMaterialSet.offsetV_init = this.mMaterialSet.offsetU;
            // // }
            // // if (this.mMaterialSet.scaleU_init == -999) {
            //     this.mMaterialSet.scaleU_init = this.mMaterialSet.scaleU;
            // // }
            // // if (this.mMaterialSet.scaleV_init == -999) {
            //     this.mMaterialSet.scaleV_init = this.mMaterialSet.scaleV;
            // // }
        }
        if (tMat != null && this.mMaterialSet.category === "color") {
            this.mMaterialSet.offsetU = 0;
            this.mMaterialSet.offsetU_init = 0;
            this.mMaterialSet.offsetV = 0;
            this.mMaterialSet.offsetV_init = 0;
            this.mMaterialSet.scaleU = 1;
            this.mMaterialSet.scaleU_init = 1;
            this.mMaterialSet.scaleV = 1;
            this.mMaterialSet.scaleV_init = 1;
        }
    }
    getTextureOffsetU() {
        let toffU = this.mMaterialSet.offsetU;
        if (toffU !== 0) {
            toffU *= -1;
        }
        const tValue = new PropertyValue(toffU);
        return (tValue);
    }
    getTextureOffsetV() {
        let toffV = this.mMaterialSet.offsetV;
        if (toffV !== 0) {
            toffV *= -1;
        }
        const tValue = new PropertyValue(toffV);
        return (tValue);
    }
    async changeTextureScale_U(pMaterialSet, pValue) {
        let tValue = clamp(pValue, -10, 10);
        pMaterialSet.scaleU = tValue;
        // clone texture if scaleU is != 0
        if (pMaterialSet.scaleU !== pMaterialSet.scaleU_init) {
            await this.mRoomManager.checkMaterial(pMaterialSet);
        }
        this.updateMaterial(pMaterialSet);
        this.updateGeometry();
        this.notifyChange(ElementChangedFlags.PropertyValues);
        return (Promise.resolve(true));
    }
    async changeTextureScale_V(pMaterialSet, pValue) {
        let tValue = clamp(pValue, -10, 10);
        pMaterialSet.scaleV = tValue;
        // clone texture if scaleU is != 0
        if (pMaterialSet.scaleV !== pMaterialSet.scaleV_init) {
            await this.mRoomManager.checkMaterial(pMaterialSet);
        }
        this.updateMaterial(pMaterialSet);
        this.updateGeometry();
        this.notifyChange(ElementChangedFlags.PropertyValues);
        return (Promise.resolve(true));
    }
    updateMaterial(pMaterialSet) {
        let tmatId = this.mRoomManager.getMaterialId(pMaterialSet);
        tmatId = tmatId + pMaterialSet.idAddOn;
        let tMat = this.mRoomManager.MatManager.getMaterial(tmatId);
        if (tMat != null) {
            this.updateTexture(tMat, pMaterialSet);
            return;
        }
    }
    // tslint:disable-next-line:cyclomatic-complexity
    updateTexture(pMaterial, pMatSet) {
        let trotAngle = degToRad(pMatSet.rotation);
        if (pMaterial.isFrozen) {
            pMaterial.unfreeze();
        }
        if (pMaterial.albedoTexture != null) {
            pMaterial.albedoTexture.wAng = trotAngle;
            pMaterial.albedoTexture.uOffset = pMatSet.offsetU;
            pMaterial.albedoTexture.vOffset = pMatSet.offsetV;
            if (pMatSet.scaleU != 0) {
                pMaterial.albedoTexture.uScale = pMatSet.scaleU;
            }
            if (pMatSet.scaleV != 0) {
                pMaterial.albedoTexture.vScale = pMatSet.scaleV;
            }
            pMaterial.albedoTexture.notifyTextureMatrixChanged();
        }
        if (pMaterial.bumpTexture != null) {
            pMaterial.bumpTexture.wAng = trotAngle;
            pMaterial.bumpTexture.uOffset = pMatSet.offsetU;
            pMaterial.bumpTexture.vOffset = pMatSet.offsetV;
            if (pMatSet.scaleU != 0) {
                pMaterial.bumpTexture.uScale = pMatSet.scaleU;
            }
            if (pMatSet.scaleV != 0) {
                pMaterial.bumpTexture.vScale = pMatSet.scaleV;
            }
            pMaterial.bumpTexture.notifyTextureMatrixChanged();
        }
        if (pMaterial.metallicTexture != null) {
            pMaterial.metallicTexture.wAng = trotAngle;
            pMaterial.metallicTexture.uOffset = pMatSet.offsetU;
            pMaterial.metallicTexture.vOffset = pMatSet.offsetV;
            if (pMatSet.scaleU != 0) {
                pMaterial.metallicTexture.uScale = pMatSet.scaleU;
            }
            if (pMatSet.scaleV != 0) {
                pMaterial.metallicTexture.vScale = pMatSet.scaleV;
            }
            pMaterial.metallicTexture.notifyTextureMatrixChanged();
        }
        if (pMaterial.microSurfaceTexture != null) {
            pMaterial.microSurfaceTexture.wAng = trotAngle;
            pMaterial.microSurfaceTexture.uOffset = pMatSet.offsetU;
            pMaterial.microSurfaceTexture.vOffset = pMatSet.offsetV;
            if (pMatSet.scaleU != 0) {
                pMaterial.microSurfaceTexture.uScale = pMatSet.scaleU;
            }
            if (pMatSet.scaleV != 0) {
                pMaterial.microSurfaceTexture.vScale = pMatSet.scaleV;
            }
            pMaterial.microSurfaceTexture.notifyTextureMatrixChanged();
        }
        // pMaterial.freeze();
    }
    getColorValueOfMatId(pmatId) {
        let tRes = "#999999";
        let tmatGroup = pmatId.split(this.mRoomManager.materialSplitStr);
        if (tmatGroup == null)
            return (tRes);
        if (tmatGroup.length === 1)
            tRes = "#999999";
        if ((tmatGroup.length === 2) && (tmatGroup[0] === "color")) {
            tRes = tmatGroup[1];
        }
        return (tRes);
    }
    hideMeshes() {
        this.hideGroupMeshes(this.m2dGroup);
        this.hideGroupMeshes(this.m3dGroup);
        this.hideGroupMeshes(this.mSpecialGeometryGroup);
        this.hideGroupMeshes(this.mAdditionalGeometryGroup);
        this.notifyChange(ElementChangedFlags.BoundingBox, true);
    }
    showMeshes() {
        this.showGroupMeshes(this.m2dGroup);
        this.showGroupMeshes(this.m3dGroup);
        this.showGroupMeshes(this.mSpecialGeometryGroup);
        this.showGroupMeshes(this.mAdditionalGeometryGroup);
        this.notifyChange(ElementChangedFlags.BoundingBox, true);
    }
    hideGroupMeshes(pGroup) {
        let tmeshes = pGroup.getChildMeshes();
        for (let tmesh of tmeshes) {
            tmesh.visibility = 0;
        }
    }
    showGroupMeshes(pGroup) {
        let tmeshes = pGroup.getChildMeshes();
        for (let tmesh of tmeshes) {
            tmesh.visibility = 1;
        }
    }
    setOnSite(pValue) {
        this.mOnSite = pValue;
        this.notifyChange(ElementChangedFlags.PropertyValues);
    }
    setHillProfile(pValue) {
        this.mParams.hillProfile = pValue;
        this.mParams.hillTopOffset = Vector3.Zero();
        this.notifyChange(ElementChangedFlags.PropertyValues);
        this.updateGeometry_GEO(true);
    }
    setHillHeight(pValue) {
        this.mParams.hillHeight = pValue;
        this.notifyChange(ElementChangedFlags.PropertyValues);
        this.updateGeometry_GEO(true);
    }
    setHillOffset(pValue) {
        let tValue = clamp(pValue, 0, 1000);
        this.mParams.hillOffset = tValue;
        this.notifyChange(ElementChangedFlags.PropertyValues);
        this.updateGeometry_GEO(true);
        this.mRoomManager.cutOverlappingShapes();
        return (true);
    }
    setTerrainType(pValue) {
        this.mParams.terrainType = pValue;
        if (pValue !== "Flat") {
            this.setOnSite(true);
        }
        else {
            this.setOnSite(false);
        }
        this.notifyChange(ElementChangedFlags.PropertyValues);
        this.notifyChange(ElementChangedFlags.Properties);
        this.updateGeometry_GEO(true);
        this.mRoomManager.cutOverlappingShapes();
    }
    setCurvedRoad(pValue, pUpdateGeometry = true, pNotify = true) {
        this.mcurvedRoad = pValue;
        this.mParams.curvedRoad = pValue;
        if (pUpdateGeometry) {
            this.beginChange();
            this.updateGeometry();
            this.endChange();
        }
        if (pNotify) {
            this.notifyChange(ElementChangedFlags.PropertyValues);
            this.notifyChange(ElementChangedFlags.Properties);
            this.notifyChange(ElementChangedFlags.BoundingBox);
        }
        this.mRoomManager.contentChanged();
    }
    setCurvedHedge(pValue, pUpdateGeometry = true, pNotify = true) {
        this.mcurvedHedge = pValue;
        this.mParams.curvedHedge = pValue;
        if (pUpdateGeometry) {
            this.beginChange();
            this.updateGeometry();
            this.endChange();
        }
        if (pNotify) {
            this.notifyChange(ElementChangedFlags.PropertyValues);
            this.notifyChange(ElementChangedFlags.Properties);
            this.notifyChange(ElementChangedFlags.BoundingBox);
        }
        this.mRoomManager.contentChanged();
    }
    stop_PreviewSound() {
        var _a;
        (_a = this.mSound_Preview) === null || _a === void 0 ? void 0 : _a.stop();
        setTimeout(() => {
            if (this.mSound_Preview != null) {
                this.mSound_Preview.dispose();
                this.mSound_Preview = null;
            }
        }, 10);
    }
    storeSafetyZoneData() {
        if (this.mType === "SafetyZone") {
            let tME = this.getMainElement();
            if (tME instanceof MainArticleElement) {
                let tbasketId = tME.basketId;
                if (tbasketId === null)
                    return;
                this.mRoomManager.setSafetyZoneData(tbasketId, this.mMaterial, this.mColor, this.mMaterialSet.category, this.mMaterialSet.value, this.mMaterialId);
            }
        }
    }
    // tslint:disable-next-line:cyclomatic-complexity
    _buildCSGMesh() {
        if (this.mType === "Arena") {
            this._buildCSGMesh_Arena();
            return;
        }
        if (this.mCAG == null)
            return;
        this.mRoomManager.checkMaterial(this.mMaterialSet).then(() => {
            this.updateMaterial(this.mMaterialSet);
        });
        //dispose current geometries
        let t3dNodes = this.m3dGroup.getChildTransformNodes(true);
        for (let tNode of t3dNodes) {
            tNode.dispose();
        }
        //dispose current geometries
        let t2dNodes = this.m2dGroup.getChildTransformNodes(true);
        for (let tNode of t2dNodes) {
            tNode.dispose();
        }
        let tPos = this.position.clone();
        let tRErotEuler = new Vector3(0, 0, 0);
        let tRErotQuat = new Quaternion(0, 0, 0, 1);
        let tRErotateY = 0;
        if (this.mType === "SafetyZone") {
            let tME = this.getMainElement();
            if (tME != null && tME.node.rotationQuaternion != null) {
                tRErotQuat = tME.node.rotationQuaternion;
                tRErotEuler = tME.node.rotationQuaternion.toEulerAngles().clone();
                tRErotateY = tRErotEuler.y * (180 / Math.PI);
                tRErotEuler = tRErotEuler.negate();
            }
            if (this.node.parent != null) {
                tPos = this.node.parent.position;
            }
        }
        let tCSG = Util.CAG2CSG(this.mCAG, this.mHeight);
        this.mArea = this.mCAG.area();
        this.mPerimeter = this.perimeter;
        let tmeshName = RoomManager.ROOM_SHAPE2D_PREFIX + this.mType.toString();
        let tMeshes2d = CsgUtils.buildMeshes(tCSG, this.mScene);
        if (tMeshes2d != null) {
            for (let tMesh of tMeshes2d) {
                //tMesh.addRotation(0, tRErotEuler.y, 0);
                tMesh.rotate(new Vector3(0, 1, 0), tRErotateY); // addRotation(0, tRErotEuler.y, 0);
                tMesh.parent = this.m2dGroup;
                tMesh.material = this.localMaterial;
                tMesh.receiveShadows = true;
                if (this.mType === "SafetyZone") {
                    this.mRoomManager.app.model.layers.setMeshLayer(tMesh, "room_safetyzones");
                }
                if (this.mType === "SafetyZone" || this.mType === "Rectangle" || this.mType === "Spline" ||
                    this.mType === "Line" || this.mType === "Circle" || this.mType === "Road") {
                    ShadowGenerator.disableMeshShadow(tMesh);
                }
                tMesh.name = tmeshName;
            }
        }
        let tMeshes3d = CsgUtils.buildMeshes(tCSG, this.mScene);
        if (tMeshes3d != null) {
            for (let tMesh of tMeshes3d) {
                //tMesh.addRotation(0, tRErotEuler.y, 0);
                tMesh.rotate(new Vector3(0, 1, 0), tRErotateY); // addRotation(0, tRErotEuler.y, 0);
                tMesh.parent = this.m3dGroup;
                tMesh.material = this.localMaterial;
                tMesh.receiveShadows = true;
                if (this.mType === "SafetyZone") {
                    this.mRoomManager.app.model.layers.setMeshLayer(tMesh, "room_safetyzones");
                }
                if (this.mType === "SafetyZone" || this.mType === "Rectangle" || this.mType === "Spline" ||
                    this.mType === "Line" || this.mType === "Circle" || this.mType === "Road") {
                    ShadowGenerator.disableMeshShadow(tMesh);
                }
                tMesh.name = tmeshName;
            }
        }
        this.mRoomManager.app.viewer.requestRenderFrame();
    }
    // tslint:disable-next-line:cyclomatic-complexity
    getCAGVerticesData() {
        if (this.mCAG_notVisible == null) {
            return;
        }
        if (this.mType === "SafetyZone" && !this.mRoomManager.app.model.layers.isLayerVisible("room_safetyzones")) {
            return;
        }
        let tPos = this.position.clone();
        let tRErotEuler = new Vector3(0, 0, 0);
        let tRErotQuat = new Quaternion(0, 0, 0, 1);
        let tRErotateY = 0;
        if (this.mType === "SafetyZone" ||
            (this.mType === "Line" && this.mGridRE_REF != null)) {
            let tME = this.getMainElement();
            if (tME != null && (tME.node.rotationQuaternion != null)) {
                tRErotQuat = tME.node.rotationQuaternion;
                tRErotEuler = tME.node.rotationQuaternion.toEulerAngles().clone();
                tRErotateY = tRErotEuler.y * (180 / Math.PI);
                tRErotEuler = tRErotEuler.negate();
            }
            if (this.node.parent != null) {
                tPos = this.node.parent.position;
            }
        }
        let tCSG = new CSG();
        if (this.mType === "CustomBuilding") {
            tCSG = this.mCAG_notVisible.extrude({ offset: [0, 0, this.mHeight] });
            tCSG = tCSG.rotate(new CSG.Vector3D([0, 0, 0]), new CSG.Vector3D([1, 0, 0]), 90);
            tCSG = tCSG.translate(new CSG.Vector3D([0, this.mHeight, 0]));
            tCSG = Util.removeBottomTopOfPolygon(tCSG, this.mHeight);
        }
        else {
            tCSG = Util.CAG2CSG(this.mCAG_notVisible, this.mHeight);
            if (Util.numbersEqual(this.mNode.position.y, RoomManager.ROOM_2dSHAPE_POSITION_ONTOP_Y, 0.0001)) {
                tCSG = tCSG.translate(new CSG.Vector3D([0, -RoomManager.ROOM_2dSHAPE_POSITION_ONTOP_Y, 0]));
            }
        }
        let tmeshName = RoomManager.ROOM_SHAPE2D_PREFIX + this.mType.toString();
        let tMeshes2d = CsgUtils.buildMeshes(tCSG, this.mScene);
        if (tMeshes2d != null) {
            for (let tMesh of tMeshes2d) {
                tMesh.rotate(new Vector3(0, 1, 0), tRErotateY);
                // ShadowGenerator.disableMeshShadow(tMesh);
                //tMesh.parent = this.m2dGroup;
                tMesh.material = this.mRoomManager.MatManager.getMaterial(this.mMaterialId);
                if (this.mType === "SafetyZone")
                    this.mRoomManager.app.model.layers.setMeshLayer(tMesh, "room_safetyzones");
                tMesh.name = tmeshName;
            }
            if (tMeshes2d[0] != null) {
                let tmeshVertices = tMeshes2d[0].getVerticesData(VertexBuffer.PositionKind);
                let tmeshIndices = tMeshes2d[0].getIndices();
                this.mParams.MeshVertices = Array.prototype.slice.call(tmeshVertices);
                this.mParams.MeshIndices = Array.prototype.slice.call(tmeshIndices);
            }
            else {
                this.mParams.MeshVertices = undefined;
                this.mParams.MeshIndices = undefined;
            }
            for (let tMesh of tMeshes2d)
                tMesh.dispose();
        }
    }
    getCAGVerticesData_Arena() {
        if (this.mCAG_notVisible_Arena_InField == null ||
            this.mCAG_notVisible_Arena_SideSegments == null ||
            this.mCAG_notVisible_Arena_RunningTracks == null ||
            this.mCAG_notVisible_Arena_TrackLines == null) {
            return;
        }
        const tmatId_PlayField = this.mRoomManager.getMaterialId(this.mMaterialSet_Arena_PlayField);
        const tmatId_SideSegments = this.mRoomManager.getMaterialId(this.mMaterialSet_Arena_SideSegments);
        const tmatId_TrackLines = this.mRoomManager.getMaterialId(this.mMaterialSet_Arena_TrackLines);
        if (this.mParams.arenaPlayFieldVisible) {
            this._getArenaCAGVerticesData(this.mCAG_notVisible_Arena_InField, tmatId_PlayField, ArenaPart.PlayField);
        }
        if (this.mParams.arenaSideSegmentsVisible) {
            this._getArenaCAGVerticesData(this.mCAG_notVisible_Arena_SideSegments, tmatId_SideSegments, ArenaPart.SideSegments);
        }
        this._getArenaCAGVerticesData(this.mCAG_notVisible_Arena_RunningTracks, this.mMaterialId, ArenaPart.RunningTracks);
        this._getArenaCAGVerticesData(this.mCAG_notVisible_Arena_TrackLines, tmatId_TrackLines, ArenaPart.TrackLines);
    }
    getMeshVerticesDataOfMiscMesh() {
        try {
            let t3dMeshes = this.m3dGroup.getChildMeshes();
            let tmergedMesh = Mesh.MergeMeshes(t3dMeshes, false);
            if (tmergedMesh != null) {
                let tmeshVertices = tmergedMesh.getVerticesData(VertexBuffer.PositionKind);
                let tmeshIndices = tmergedMesh.getIndices();
                this.mParams.MeshVertices = Array.prototype.slice.call(tmeshVertices);
                this.mParams.MeshIndices = Array.prototype.slice.call(tmeshIndices);
                tmergedMesh.dispose();
            }
        }
        catch (e) {
            console.log('(serialize) error while plane bottom_top generating');
            console.log(e);
        }
    }
    getMeshVerticesDataOfMiscMesh_Hills() {
        try {
            let t3dmeshes = this.m3dGroup.getChildMeshes();
            for (let tmesh of t3dmeshes) {
                tmesh.parent = null;
            }
            let tmergedMesh = Mesh.MergeMeshes(t3dmeshes, false);
            if (tmergedMesh != null) {
                tmergedMesh.parent = this.m3dGroup;
                let tmeshVertices = tmergedMesh.getVerticesData(VertexBuffer.PositionKind);
                let tmeshIndices = tmergedMesh.getIndices();
                this.mParams.MeshVertices = Array.prototype.slice.call(tmeshVertices);
                this.mParams.MeshIndices = Array.prototype.slice.call(tmeshIndices);
                tmergedMesh.parent = null;
                tmergedMesh.dispose();
            }
            for (let tmesh of t3dmeshes) {
                tmesh.parent = this.m3dGroup;
            }
            this.updateTransformation();
        }
        catch (e) {
            console.log('(serialize) error while plane bottom_top generating');
            console.log(e);
        }
    }
    getMeshVerticesDataOfMiscMesh_Hills1() {
        try {
            let tPos = this.position.negate().clone();
            let trotation = 0;
            if (this.node.rotationQuaternion != null) {
                let trotEuler = this.node.rotationQuaternion.toEulerAngles();
                trotation = trotEuler.y * (180 / Math.PI);
            }
            let t3dMeshes = this.m3dGroup.getChildMeshes();
            let tmergedMesh = Mesh.MergeMeshes(t3dMeshes, false);
            if (tmergedMesh != null) {
                tmergedMesh.position = tPos;
                //tmergedMesh.rotate(Vector3.Up(), -trotation);
                tmergedMesh.parent = this.m3dGroup;
            }
        }
        catch (e) {
            console.log('(serialize) error while plane bottom_top generating');
            console.log(e);
        }
    }
    // tslint:disable-next-line:cyclomatic-complexity
    _updateGeometrie_Arena(pParent, pMeshNamePrefix) {
        let tOH = 0;
        let tCSG_RT = new CSG();
        let tCSG_TL = new CSG();
        let tCSG_PF = new CSG();
        let tCSG_SS = new CSG();
        tCSG_RT = Util.CAG2CSG(this.mCAG_Arena_RunningsTracks, tOH);
        tCSG_TL = Util.CAG2CSG(this.mCAG_Arena_TrackLines, tOH);
        tCSG_PF = Util.CAG2CSG(this.mCAG_Arena_InField, tOH);
        tCSG_SS = Util.CAG2CSG(this.mCAG_Arena_SideSegments, tOH);
        let tMeshes_RT = CsgUtils.buildMeshes(tCSG_RT, this.mScene);
        if (tMeshes_RT != null) {
            for (let tMesh of tMeshes_RT) {
                // core.rendering.ShadowGenerator.disableMeshShadow(tMesh);
                tMesh.parent = pParent;
                tMesh.material = this.localMaterial;
                tMesh.name = pMeshNamePrefix + this.mType.toString();
                if (this.mGroupMeshOpacity !== 1) {
                    tMesh.renderingGroupId = 1;
                }
                tMesh.visibility = this.mGroupMeshOpacity;
            }
        }
        let tMeshes_TL = CsgUtils.buildMeshes(tCSG_TL, this.mScene);
        if (tMeshes_TL != null) {
            for (let tMesh of tMeshes_TL) {
                // core.rendering.ShadowGenerator.disableMeshShadow(tMesh);
                tMesh.parent = pParent;
                tMesh.material = this.localMaterial_Arena_TrackLines;
                tMesh.name = pMeshNamePrefix + this.mType.toString();
                if (this.mGroupMeshOpacity !== 1) {
                    tMesh.renderingGroupId = 1;
                }
                tMesh.visibility = this.mGroupMeshOpacity;
            }
        }
        let tMeshes_PF = CsgUtils.buildMeshes(tCSG_PF, this.mScene);
        if (tMeshes_PF != null) {
            for (let tMesh of tMeshes_PF) {
                // core.rendering.ShadowGenerator.disableMeshShadow(tMesh);
                tMesh.parent = pParent;
                tMesh.material = this.localMaterial_Arena_PlayField;
                tMesh.name = pMeshNamePrefix + this.mType.toString();
                if (this.mGroupMeshOpacity !== 1) {
                    tMesh.renderingGroupId = 1;
                }
                tMesh.visibility = this.mGroupMeshOpacity;
            }
        }
        let tMeshes_SS = CsgUtils.buildMeshes(tCSG_SS, this.mScene);
        if (tMeshes_SS != null) {
            for (let tMesh of tMeshes_SS) {
                // core.rendering.ShadowGenerator.disableMeshShadow(tMesh);
                tMesh.parent = pParent;
                tMesh.material = this.localMaterial_Arena_SideSegments;
                tMesh.name = pMeshNamePrefix + this.mType.toString();
                if (this.mGroupMeshOpacity !== 1) {
                    tMesh.renderingGroupId = 1;
                }
                tMesh.visibility = this.mGroupMeshOpacity;
            }
        }
    }
    // tslint:disable-next-line:cyclomatic-complexity
    update2dGeometry(pDisplayOnly, pcutComplete = false) {
        let tCAGBase = this.mCAG;
        if (this.mCAG_notVisible != null && pcutComplete) {
            tCAGBase = this.mCAG_notVisible;
        }
        //dispose current geometries
        let t2dNodes = this.m2dGroup.getChildTransformNodes(true);
        for (let tNode of t2dNodes) {
            tNode.dispose();
        }
        if (this.mType === "SafetyZone") {
            if ((!pDisplayOnly) && (this.mCAGoriginal != null)) {
                this.setCAG(this.mCAGoriginal);
            }
            if (this.mCAG != null) {
                let tCSG = Util.CAG2CSG(this.mCAG);
                //tCSG = tCSG.translate([0, RoomManager.SafetyZone_Y_Position(), 0]) as CSG;
                let tMeshes = CsgUtils.buildMeshes(tCSG, this.mScene);
                if (tMeshes != null) {
                    for (let tMesh of tMeshes) {
                        ShadowGenerator.disableMeshShadow(tMesh);
                        tMesh.parent = this.m2dGroup;
                        tMesh.material = this.localMaterial;
                        tMesh.name = RoomManager.ROOM_SHAPE2D_PREFIX + this.mType.toString();
                        if (this.mGroupMeshOpacity !== 1) {
                            tMesh.renderingGroupId = 1;
                        }
                        if (this.mType === "SafetyZone") {
                            this.mRoomManager.app.model.layers.setMeshLayer(tMesh, "room_safetyzones");
                            tMesh.visibility = this.mSZ_visible ? 1 : 0;
                        }
                        else {
                            tMesh.visibility = this.mGroupMeshOpacity;
                        }
                    }
                    this.mdrawnMesh2d = tMeshes[0];
                }
            }
        }
        else if (this.mType === "Arena") {
            if (pDisplayOnly &&
                this.mCAG_Arena_InField != null &&
                this.mCAG_Arena_RunningsTracks != null &&
                this.mCAG_Arena_SideSegments != null &&
                this.mCAG_Arena_TrackLines != null) {
                this._updateGeometrie_Arena(this.m2dGroup, RoomManager.ROOM_SHAPE2D_PREFIX);
            }
            else {
                this.mGeometry.create2dGeometry(this.m2dGroup);
            }
        }
        else if (this.mType === "Grid") {
            this.mGeometry.create2dGeometry(this.m2dGroup);
        }
        else if (pDisplayOnly && tCAGBase != null) {
            let tOH = this.mType === "Road" ? this.mHeight : 0;
            tOH = this.mType === "Hedge" ? this.mHeight : tOH;
            let tCSG = new CSG();
            let tCSGtop = null;
            if (this.mType === "CustomBuilding") {
                tCSG = tCAGBase.extrude({ offset: [0, 0, this.mHeight] });
                tCSG = tCSG.rotate(new CSG.Vector3D([0, 0, 0]), new CSG.Vector3D([1, 0, 0]), 90);
                tCSG = tCSG.translate(new CSG.Vector3D([0, this.mHeight, 0]));
                //tCSGtop = Util.getTopPolygon(tCSG, this.mHeight);
                tCSG = Util.removeBottomTopOfPolygon(tCSG, this.mHeight);
            }
            else {
                tCSG = Util.CAG2CSG(tCAGBase, tOH);
            }
            let tMeshes = CsgUtils.buildMeshes(tCSG, this.mScene);
            if (tMeshes != null) {
                for (let tMesh of tMeshes) {
                    if (this.mType === "CustomBuilding") {
                        this.mRoomManager.app.model.layers.setMeshLayer(tMesh, "room:Building_Layer");
                    }
                    // ShadowGenerator.disableMeshShadow(tMesh);
                    tMesh.parent = this.m2dGroup;
                    tMesh.material = this.localMaterial;
                    tMesh.name = RoomManager.ROOM_SHAPE2D_PREFIX + this.mType.toString();
                    if (this.mGroupMeshOpacity !== 1) {
                        tMesh.renderingGroupId = 1;
                    }
                    tMesh.visibility = this.mGroupMeshOpacity;
                    if (this.mType === "CustomBuilding")
                        tMesh.visibility = this.mBuildingOpacity;
                }
            }
            if (tCSGtop != null) {
                tMeshes = CsgUtils.buildMeshes(tCSGtop, this.mScene);
                if (tMeshes != null) {
                    for (let tMesh of tMeshes) {
                        if (this.mType === "CustomBuilding") {
                            this.mRoomManager.app.model.layers.setMeshLayer(tMesh, "room:Building_Layer");
                        }
                        // ShadowGenerator.disableMeshShadow(tMesh);
                        tMesh.parent = this.m2dGroup;
                        tMesh.material = this.mRoomManager.MatManager.getMaterial("color_-_#999999");
                        tMesh.name = RoomManager.ROOM_SHAPE2D_PREFIX + this.mType.toString();
                        if (this.mGroupMeshOpacity !== 1) {
                            tMesh.renderingGroupId = 1;
                        }
                        tMesh.visibility = this.mBuildingOpacity; //this.mGroupMeshOpacity;
                    }
                }
            }
            if (this.mType === "Circle") {
                let tCircle = this.geometry;
                tCircle.drawRadiusLine(this.m2dGroup);
            }
        }
        else {
            this.mGeometry.create2dGeometry(this.m2dGroup);
        }
        if (this.mCAG != null) {
            let tCAG = this.mCAG;
            this.setArea(tCAG.area());
            this.setPerimeter(Util.getCAGPerimeter(tCAG));
        }
        this._update2dVisibility();
        this.createBase2dGeometry();
    }
    // tslint:disable-next-line:cyclomatic-complexity
    update3dGeometry(pDisplayOnly, pcutComplete = false) {
        let tCAGBase = this.mCAG;
        if (this.mCAG_notVisible != null && pcutComplete) {
            tCAGBase = this.mCAG_notVisible;
        }
        //dispose current geometries
        let t3dNodes = this.m3dGroup.getChildTransformNodes(true);
        for (let tNode of t3dNodes) {
            tNode.dispose();
        }
        if (this.mType === "SafetyZone") {
            if (!pDisplayOnly && this.mCAGoriginal != null) {
                this.setCAG(this.mCAGoriginal);
            }
            if (this.mCAG != null) {
                let tCSG = Util.CAG2CSG(this.mCAG);
                //tCSG = tCSG.translate([0, RoomManager.SafetyZone_Y_Position(), 0]) as CSG;
                let tMeshes = CsgUtils.buildMeshes(tCSG, this.mScene);
                if (tMeshes != null) {
                    for (let tMesh of tMeshes) {
                        ShadowGenerator.disableMeshShadow(tMesh);
                        tMesh.parent = this.m3dGroup;
                        tMesh.material = this.localMaterial;
                        tMesh.name = RoomManager.ROOM_SHAPE2D_PREFIX + this.mType.toString();
                        tMesh.receiveShadows = true;
                        if (this.mGroupMeshOpacity !== 1) {
                            tMesh.renderingGroupId = 1;
                        }
                        if (this.mType === "SafetyZone") {
                            this.mRoomManager.app.model.layers.setMeshLayer(tMesh, "room_safetyzones");
                            tMesh.visibility = this.mSZ_visible ? 1 : 0;
                        }
                        else {
                            tMesh.visibility = this.mGroupMeshOpacity;
                        }
                    }
                    this.mdrawnMesh3d = tMeshes[0];
                }
            }
        }
        else if (this.mType === "Arena") {
            if (pDisplayOnly &&
                this.mCAG_Arena_InField != null &&
                this.mCAG_Arena_RunningsTracks != null &&
                this.mCAG_Arena_SideSegments != null &&
                this.mCAG_Arena_TrackLines != null) {
                this._updateGeometrie_Arena(this.m3dGroup, RoomManager.ROOM_SHAPE2D_PREFIX);
            }
            else {
                this.mGeometry.create3dGeometry(this.m3dGroup);
            }
        }
        else if (this.mType === "Grid") {
            this.mGeometry.create3dGeometry(this.m3dGroup);
        }
        else if (pDisplayOnly && tCAGBase != null) {
            let tOH = this.mType === "Road" ? this.mHeight : 0;
            tOH = this.mType === "Hedge" ? this.mHeight : tOH;
            let tCSG = new CSG();
            let tCSGtop = null;
            if (this.mType === "CustomBuilding") {
                tCSG = tCAGBase.extrude({ offset: [0, 0, this.mHeight] });
                tCSG = tCSG.rotate(new CSG.Vector3D([0, 0, 0]), new CSG.Vector3D([1, 0, 0]), 90);
                tCSG = tCSG.translate(new CSG.Vector3D([0, this.mHeight, 0]));
                //tCSGtop = Util.getTopPolygon(tCSG, this.mHeight);
                tCSG = Util.removeBottomTopOfPolygon(tCSG, this.mHeight);
            }
            else {
                tCSG = Util.CAG2CSG(tCAGBase, tOH);
            }
            let tMeshes = CsgUtils.buildMeshes(tCSG, this.mScene);
            if (tMeshes != null) {
                for (let tMesh of tMeshes) {
                    if (this.mType === "CustomBuilding") {
                        this.mRoomManager.app.model.layers.setMeshLayer(tMesh, "room:Building_Layer");
                    }
                    // if (this.mType !== "Hedge") {
                    //     ShadowGenerator.disableMeshShadow(tMesh);
                    // }
                    tMesh.parent = this.m3dGroup;
                    tMesh.material = this.localMaterial;
                    tMesh.name = RoomManager.ROOM_SHAPE2D_PREFIX + this.mType.toString();
                    tMesh.receiveShadows = true;
                    if (tMesh.material !== null)
                        tMesh.material.backFaceCulling = false;
                    if (this.mGroupMeshOpacity !== 1) {
                        tMesh.renderingGroupId = 1;
                    }
                    tMesh.visibility = this.mGroupMeshOpacity;
                    if (this.mType === "CustomBuilding")
                        tMesh.visibility = this.mBuildingOpacity;
                }
            }
            if (tCSGtop != null) {
                let tMeshes1 = CsgUtils.buildMeshes(tCSGtop, this.mScene);
                if (tMeshes1 != null) {
                    for (let tMesh of tMeshes1) {
                        if (this.mType === "CustomBuilding") {
                            this.mRoomManager.app.model.layers.setMeshLayer(tMesh, "room:Building_Layer");
                        }
                        // ShadowGenerator.disableMeshShadow(tMesh);
                        tMesh.parent = this.m2dGroup;
                        tMesh.material = this.mRoomManager.MatManager.getMaterial("color_-_#999999");
                        tMesh.name = RoomManager.ROOM_SHAPE2D_PREFIX + this.mType.toString();
                        tMesh.receiveShadows = true;
                        if (this.mGroupMeshOpacity !== 1) {
                            tMesh.renderingGroupId = 1;
                        }
                        tMesh.visibility = this.mGroupMeshOpacity;
                    }
                }
            }
            if (this.mType === "Circle") {
                let tCircle = this.geometry;
                tCircle.drawRadiusLine(this.m3dGroup);
            }
        }
        else {
            this.mGeometry.create3dGeometry(this.m3dGroup);
            //dispose current geometries
            let taddNodes = this.mAdditionalGeometryGroup.getChildTransformNodes(true);
            for (let tNode of taddNodes) {
                tNode.dispose();
            }
        }
        this._update3dVisibility();
    }
    updateAudio() {
        const tmesh = this.m3dGroup.getChildMeshes();
        if (tmesh.length === 0) {
            return;
        }
        if (this.mSound == null) {
            const tsoundId = this.mSoundSet.category + RoomManager.CATVALUE_SPLITTER + this.mSoundSet.id;
            this.setAudioFile(tsoundId);
        }
        if (this.mSound == null) {
            return;
        }
        this.mSound.attachToMesh(tmesh[0]);
        this.mSound.updateOptions({ maxDistance: this.mWidth * 0.5 });
        this.mSound.setPosition(this.mPosition.clone());
    }
    updateAdditionalGeometry() {
        //dispose current geometries
        let t4dNodes = this.mAdditionalGeometryGroup.getChildTransformNodes(true);
        for (let tNode of t4dNodes) {
            tNode.dispose();
        }
        if (this.mType === "CustomBuilding")
            this.mAdditionalGeometryGroup.setEnabled(true);
        this.mGeometry.createAdditionalGeometry(this.mAdditionalGeometryGroup);
        this._updateAdditionalGeometryVisibility();
    }
    updateSpecialGeometry() {
        //dispose current geometries
        let t4dNodes = this.mSpecialGeometryGroup.getChildTransformNodes(true);
        for (let tNode of t4dNodes) {
            tNode.dispose();
        }
        this.mGeometry.createSpecialGeometry(this.mSpecialGeometryGroup);
        this._updateSpecialGeometryVisibility();
    }
    checkGridPoints_All(pMesh, ptransMatrix, pAllCellPoints) {
        const tgadaptedPoints = Util.getGridModeFieldPoints(pAllCellPoints, GridFieldPointMode.all);
        if (tgadaptedPoints == null) {
            // issue while converting points
            return (null);
        }
        // check points on polygon
        const tisOnLinePoly = Util.checkGridFieldPointsInMesh(pMesh, ptransMatrix, tgadaptedPoints, this.mScene);
        return (tisOnLinePoly);
    }
    checkGridPoints_AllCount(pMesh, ptransMatrix, pAllCellPoints) {
        const tgadaptedPoints = Util.getGridModeFieldPoints(pAllCellPoints, GridFieldPointMode.all);
        if (tgadaptedPoints == null) {
            // issue while converting points
            return (0);
        }
        // check points on polygon
        const tpointsOnMesh = Util.checkGridFieldPointsInMesh_Count(pMesh, ptransMatrix, tgadaptedPoints, this.mScene);
        return (tpointsOnMesh);
    }
    checkGridPoints_Full(pMesh, ptransMatrix, pAllCellPoints) {
        let tPoints = new Array();
        tPoints.push(new CSG.Vector2D(pAllCellPoints[0].x, pAllCellPoints[0].y));
        tPoints.push(new CSG.Vector2D(pAllCellPoints[5].x, pAllCellPoints[5].y));
        tPoints.push(new CSG.Vector2D(pAllCellPoints[7].x, pAllCellPoints[7].y));
        tPoints.push(new CSG.Vector2D(pAllCellPoints[2].x, pAllCellPoints[2].y));
        const tfull = CAG.fromPointsNoCheck(tPoints);
        if (this.mCAG != null) {
            const tcut = tfull.subtract(this.mCAG);
            if (tcut.area() < 0.1) {
                console.log('full');
            }
        }
        const tgadaptedPoints = Util.getGridModeFieldPoints(pAllCellPoints, GridFieldPointMode.full);
        if (tgadaptedPoints == null) {
            // issue while converting points
            return (null);
        }
        // check points on polygon
        const tisOnLinePoly = Util.checkGridFieldPointsInMesh(pMesh, ptransMatrix, tgadaptedPoints, this.mScene);
        if (tisOnLinePoly === PointOnPolygons.all) {
            return (true);
        }
        return (false);
    }
    checkGridPoints_Full_CAG(pAllCellPoints, pCAG) {
        const tfull = Util.getGridModeFieldCAG(pAllCellPoints, GridFieldPointMode.full);
        if (pCAG != null && tfull != null) {
            const tcut = tfull.subtract(pCAG);
            if (tcut.area() == 0) {
                return (true);
            }
        }
        return (false);
    }
    checkGridPoints_rest_CAG(pAllCellPoints, pCAG) {
        const tfull = Util.getGridModeFieldCAG(pAllCellPoints, GridFieldPointMode.full);
        if (pCAG != null && tfull != null) {
            if (tfull.intersect(pCAG).sides.length > 0) {
                return (true);
            }
        }
        return (false);
    }
    checkGridPoints_Opponents(pMesh, ptransMatrix, pAllCellPoints, pMaster, pOpponent) {
        const tgadaptedPoints = Util.getGridModeFieldPoints(pAllCellPoints, pMaster);
        if (tgadaptedPoints == null) {
            // issue while converting points
            return (null);
        }
        const tgadaptedPoints_Opponent = Util.getGridModeFieldPoints(pAllCellPoints, pOpponent);
        if (tgadaptedPoints_Opponent == null) {
            // issue while converting points
            return (null);
        }
        // check points on polygon
        const tisOnLinePoly0 = Util.checkGridFieldPointsInMesh(pMesh, ptransMatrix, tgadaptedPoints, this.mScene);
        const tisOnLinePoly1 = Util.checkGridFieldPointsInMesh(pMesh, ptransMatrix, tgadaptedPoints_Opponent, this.mScene);
        if (tisOnLinePoly0 === PointOnPolygons.all &&
            tisOnLinePoly1 === PointOnPolygons.none) {
            return (true);
        }
        return (false);
    }
    checkGridPoints_Opponents_CAG(pAllCellPoints, pMaster, pOpponent, pCAG) {
        const tCAG0 = Util.getGridModeFieldCAG(pAllCellPoints, pMaster);
        const tCAG1 = Util.getGridModeFieldCAG(pAllCellPoints, pOpponent);
        if (pCAG != null && tCAG0 != null && tCAG1 != null) {
            const tcut0 = tCAG0.subtract(pCAG);
            const tcut1 = tCAG1.subtract(pCAG);
            if (tcut0.area() == 0 && tcut1.area() == tCAG1.area()) {
                return (true);
            }
        }
        return (false);
    }
    createBase2dGeometry() {
        if (this.mType === "BackgroundImage") {
            this.mMeshBounds = this.mMeshBounds;
            let tHW = this.mWidth * 0.5;
            //let tHD = this.depth * 0.5;
            /*
            // disabled: 2020-07-27
            // jira: https://projects.kompan.com/browse/CFG-480
            let tLines = MeshBuilder.CreateLines("", {
                points: [
                    new Vector3(-tHW, 0.0, 0),
                    new Vector3(tHW, 0.0, 0)
                ],
                updatable: false,
                instance: null
            }, this.mScene);
            tLines.isPickable = true;
            tLines.intersectionThreshold = 0;
            tLines.color = Color3.Black();
            tLines.position.y = 0.0005;
            this.m2dGroup.position.y = 0;
            tLines.parent = this.m2dGroup;
            CameraControl.SetMeshCameraModeMask(tLines,
                CameraModeMask.Top | CameraModeMask.Bottom);
            ShadowGenerator.disableMeshShadow(tLines);
            tLines.freezeWorldMatrix();
            */
            let tPlane = CreatePlane("", { width: this.mWidth, height: this.depth }, this.mScene);
            //tPlane.rotation.x = PI_DIV2;
            tPlane.visibility = 0;
            tPlane.isPickable = true;
            tPlane.parent = this.m2dGroup;
            tPlane.material = this.mModel.materials.defaultMaterial;
            CameraControl.SetMeshCameraModeMask(tPlane, CameraModeMask.Top | CameraModeMask.Bottom);
            ShadowGenerator.disableMeshShadow(tPlane);
            tPlane.freezeWorldMatrix();
        }
    }
    async setMeshMaterial1(pUpdateGeometry = true) {
        if (this.mType === "WallSideDummy")
            return;
        await this.mRoomManager._loadExternalMaterial(this.mMaterialId);
        await this.mRoomManager.checkMaterial(this.mMaterialSet).then(() => {
            this.storeSafetyZoneData();
            let tDisplOnly = true;
            if (this.mParams.terrainType !== 'Flat' || this.mType === "Arena") {
                tDisplOnly = false;
            }
            this.updateGeometry(false, tDisplOnly);
        });
    }
    mergeCAG(pCAGdata) {
        let tRE = pCAGdata.RoomElement;
        let tCAG = pCAGdata.CAG;
        if (tRE == null || tCAG == null)
            return;
        let tentries = [];
        for (let tCAGdata of this.mCutCAG) {
            if (tCAGdata.RoomElement === pCAGdata.RoomElement && tCAGdata.CAG === pCAGdata.CAG) {
                tentries.push(tCAGdata);
            }
        }
        for (let tEntry of tentries)
            removeElement(this.mCutCAG, tEntry);
    }
    // tslint:disable-next-line:cyclomatic-complexity
    resetCuttedShaped() {
        if (this.mDeletedCAG.length === 0 && this.mCutCAG.length === 0) {
            this.updateGeometry(false, false);
            return;
        }
        while (this.mDeletedCAG.length > 0) {
            let t_delCAGData = this.mDeletedCAG.pop();
            if (t_delCAGData != null && this.mCAG != null) {
                this.mCAG = this.mCAG.union(t_delCAGData.CAG);
                let tRE = t_delCAGData.RoomElement;
                t_delCAGData.RoomElement = this;
                tRE.mergeCAG(t_delCAGData);
            }
        }
        this.updateGeometry(false, false);
        while (this.mCutCAG.length > 0) {
            let t_cutCAGData = this.mCutCAG.pop();
            if (t_cutCAGData == null)
                continue;
            let t_cutRE = t_cutCAGData.RoomElement;
            let t_cutCAG = t_cutCAGData.CAG;
            if (t_cutRE != null && t_cutCAG != null) {
                let tCAGRE = t_cutRE.CAG;
                if (tCAGRE != null) {
                    tCAGRE = tCAGRE.union(t_cutCAG);
                    t_cutRE.setCAG(tCAGRE);
                    t_cutRE._buildCSGMesh();
                    t_cutRE.updateGeometry(false, true);
                }
            }
        }
        this.mRoomManager.app.viewer.requestRenderFrame();
    }
    cmpWallElements(pW1, pW2) {
        if (pW1.mWall != null && pW2.mWall != null) {
            let tL1 = Vector2.Distance(Util.to2d(pW1.mPosition), pW1.mWall.startPoint);
            let tL2 = Vector2.Distance(Util.to2d(pW2.mPosition), pW2.mWall.startPoint);
            return (tL1 - tL2);
        }
        return (0);
    }
    static checkPoints(pPoints) {
        const tP2d = [];
        for (const tP of pPoints) {
            const tP3d = deserializeVector3(tP);
            tP2d.push(new CSG.Vector2D(tP3d.x, tP3d.z));
        }
        let tCAG = null;
        try {
            tCAG = CAG.fromPoints(tP2d);
        }
        catch (error) {
            return (false);
        }
        if (tCAG.isSelfIntersecting()) {
            return (false);
        }
        return (true);
    }
    // tslint:disable-next-line:cyclomatic-complexity
    static deserialize(pRoomManager, pContext, pIOElement) {
        if (pIOElement == null)
            return (null);
        let tIORoomElement = pIOElement;
        if (tIORoomElement.type === "DummySafetyZone" ||
            tIORoomElement.type === "Plane_BottomTop" ||
            tIORoomElement.type === "DummyElement") {
            return (null);
        }
        let tgrid = null;
        if (tIORoomElement.type === "Line") {
            tgrid = this.checkForLineGrid(pRoomManager, tIORoomElement);
        }
        if (tIORoomElement.type === "SafetyZone") {
            let tBasketId = tIORoomElement.basketId;
            let tSZvisibility = true;
            if (tBasketId !== "")
                tSZvisibility = tIORoomElement.SZvisible === undefined ? true : tIORoomElement.SZvisible;
            pRoomManager.SafetyZoneImportData.set(tIORoomElement.basketId, { OrderIndex: tIORoomElement.orderIndex,
                MaterialId: tIORoomElement.materialId,
                visible: tSZvisibility,
                label: tIORoomElement.label });
            return (null);
        }
        let t_floorPoints = null;
        let t_floorTransparent = true;
        if (tIORoomElement.type === "Floor") {
            if (tIORoomElement.geometryData != null) {
                let tgeoDataF = tIORoomElement.geometryData;
                if (tgeoDataF.ElementPoints != null) {
                    if (tgeoDataF.ElementPoints.length < 3) {
                        return (null);
                    }
                    else {
                        const tvalidPoints = RoomElement.checkPoints(tgeoDataF.ElementPoints);
                        if (!tvalidPoints) {
                            return (null);
                        }
                    }
                    t_floorPoints = Util.to2dArray(tgeoDataF.ElementPoints);
                }
                else
                    return (null);
                if (tgeoDataF.floorTransparent != null) {
                    t_floorTransparent = tgeoDataF.floorTransparent;
                }
            }
            else
                return (null);
        }
        let tcurvedElement = false;
        if (tIORoomElement.geometryData != null && tIORoomElement.type != "GeoMarker") {
            let tParams = tIORoomElement.geometryData;
            if ((tIORoomElement.type === "Road") && (tParams.curvedRoad !== undefined)) {
                tcurvedElement = tParams.curvedRoad;
            }
            if ((tIORoomElement.type === "Hedge") && (tParams.curvedHedge !== undefined)) {
                tcurvedElement = tParams.curvedHedge;
            }
            if (tIORoomElement.type !== "Text" &&
                tIORoomElement.type !== "Arena" &&
                tIORoomElement.type !== "PillarB" &&
                tIORoomElement.type !== "PillarC" &&
                tParams.ElementPoints.length < 2) {
                return (null);
            }
        }
        const tIOMaterialSet = tIORoomElement.materialSet != null ? tIORoomElement.materialSet : undefined;
        let tRoomElement = new RoomElement(pRoomManager, tIORoomElement.type, null, tcurvedElement, '', tIOMaterialSet);
        //rotation
        if (tIORoomElement.rotation != null) {
            if (!(tRoomElement.type === "Line" && tgrid != null)) {
                tRoomElement.mNode.rotationQuaternion = deserializeQuaternion(tIORoomElement.rotation);
                // tRoomElement.updateTransformation();
            }
        }
        if (tIORoomElement.materialSet != null) {
            tRoomElement.mMaterialSet = tIORoomElement.materialSet;
        }
        if (tIORoomElement.soundId != null) {
            const tsoundSet = pRoomManager.getSoundEffectsById(tIORoomElement.soundId);
            if (tsoundSet != null) {
                tRoomElement.mSoundSet = tsoundSet;
            }
        }
        if (tIORoomElement.soundVolume != null) {
            tRoomElement.mSoundVolume = tIORoomElement.soundVolume;
        }
        if (tIORoomElement.type === "Line" && tgrid != null) {
            tRoomElement.setWidth(tgrid.width, false, false);
            tRoomElement.setDepth(tgrid.depth, false, false);
            tRoomElement.setPosition(Vector3.Zero());
            tRoomElement.setGridRE_REF(tgrid);
            tgrid.setREforGrid_REF([tRoomElement]);
            pRoomManager.app.model.removeElement(tRoomElement, false, false);
            pRoomManager.app.model.addElement(tRoomElement, tgrid.node);
            tRoomElement.setGridRE_REF(tgrid);
            tgrid.setREforGrid_REF([tRoomElement]);
            tRoomElement.updateTransformation();
        }
        if (tIORoomElement.shapeLevel == null) {
            const tposY = deserializeVector3(tIORoomElement.position).y;
            if (Util.numbersEqual(tposY, RoomManager.ROOM_2dSHAPE_POSITION_DEFAULT_Y)) {
                tIORoomElement.shapeLevel = ShapeLevel.default;
                tRoomElement.mShapeLevel = ShapeLevel.default;
            }
            else if (Util.numbersEqual(tposY, RoomManager.ROOM_2dSHAPE_POSITION_ONTOP_Y)) {
                tIORoomElement.shapeLevel = ShapeLevel.OnTop;
                tRoomElement.mShapeLevel = ShapeLevel.OnTop;
            }
            else {
                tIORoomElement.shapeLevel = ShapeLevel.manual;
                tRoomElement.mShapeLevel = ShapeLevel.manual;
            }
        }
        else {
            if (tIORoomElement.shapeLevel === ShapeLevel.default) {
                tRoomElement.position.y = RoomManager.ROOM_2dSHAPE_POSITION_DEFAULT_Y;
            }
            if (tIORoomElement.shapeLevel === ShapeLevel.OnTop) {
                tRoomElement.position.y = RoomManager.ROOM_2dSHAPE_POSITION_ONTOP_Y;
            }
        }
        let tgeoData = tRoomElement._deserialize_Params(tIORoomElement.geometryData);
        if (tIORoomElement.geometryData != null &&
            (tgeoData.ElementPoints != null && tgeoData.ElementPoints.length > 0)) {
            let tZero = 0;
            for (let tP of tgeoData.ElementPoints) {
                if (tP.x === 0 && tP.y === 0 && tP.z === 0)
                    tZero++;
            }
            if (tZero > 1 && (tIORoomElement.type === "Hedge" || tIORoomElement.type === "Fence")) {
                tRoomElement.dispose();
                return (null);
            }
            if (tZero === tgeoData.ElementPoints.length) {
                tgeoData = tIORoomElement.geometryData;
            }
        }
        // material
        if (tIORoomElement.materialSet !== undefined) {
            tRoomElement.mMaterialSet = tIORoomElement.materialSet;
            if (tRoomElement.type !== "CustomBuilding") {
                tRoomElement.mMaterialSet.idAddOn = pRoomManager.materialSplitStr + tRoomElement.mIdStr;
            }
            else {
                tRoomElement.mMaterialSet.idAddOn = tIORoomElement.materialSet.idAddOn;
            }
            tRoomElement.mMaterialSet.loaded = true;
        }
        if (tRoomElement.mType === "Arena") {
            tRoomElement._deserialize_Arena_Material(pRoomManager, tRoomElement, tIORoomElement);
        }
        if (tIORoomElement.textureRotation !== undefined) {
            tRoomElement.mMaterialSet.rotation = tIORoomElement.textureRotation;
        }
        if (tgeoData != null) {
            let tParams = tgeoData;
            if (Util.averagePointDistToZero(tParams.ElementPoints) > 1000) {
                tRoomElement.dispose();
                return (null);
            }
            // tslint:disable-next-line:no-collapsible-if
            if (Util.arrayContainsSamePoints(tParams.ElementPoints)) {
                if (tRoomElement.mType !== "CustomBuilding" &&
                    tRoomElement.mType !== "Road") {
                    tRoomElement.dispose();
                    return (null);
                }
            }
            if (tIORoomElement.type === "Road" || tIORoomElement.type === "Hedge") {
                if (tParams.ElementPoints.length < 2) {
                    tRoomElement.dispose();
                    return (null);
                }
                if (tParams.ElementPoints.length >= 2) {
                    if (!(tParams.ElementPoints[0] instanceof Vector3)) {
                        tRoomElement.dispose();
                        return (null);
                    }
                    let tP0 = tParams.ElementPoints[0].clone();
                    let tP1 = tParams.ElementPoints[1].clone();
                    const tDist = Vector3.Distance(tP0, tP1);
                    if (Util.numbersEqual(tDist, 0)) {
                        tRoomElement.dispose();
                        return (null);
                    }
                }
            }
            if (tParams.ElementOriginalPositionX !== undefined) {
                tRoomElement.Params.ElementOriginalPositionX = tParams.ElementOriginalPositionX;
            }
            if (tParams.ElementOriginalPositionY !== undefined) {
                tRoomElement.Params.ElementOriginalPositionY = tParams.ElementOriginalPositionY;
            }
            if (tParams.ElementOriginalPositionZ !== undefined) {
                tRoomElement.Params.ElementOriginalPositionZ = tParams.ElementOriginalPositionZ;
            }
            if (tParams.ElementOriginalHeight !== undefined) {
                tRoomElement.Params.ElementOriginalHeight = tParams.ElementOriginalHeight;
            }
            if (tParams.ElementOriginalPoints !== undefined) {
                const tPoints = tParams.ElementOriginalPoints.slice();
                tRoomElement.Params.ElementOriginalPoints = tPoints;
            }
            if (tParams.ElementPoints !== undefined) {
                let tPoints = tParams.ElementPoints.slice();
                if (tIORoomElement.type === "CustomBuilding" &&
                    tPoints.length > 1 &&
                    Util.samePointsV3(tPoints[0], tPoints[tPoints.length - 1], EPSILON)) {
                    tPoints.pop();
                }
                tRoomElement.Params.ElementPoints = tPoints.slice();
            }
            if (tParams.FencePostMATCategory !== undefined) {
                tRoomElement.Params.FencePostMATCategory = tParams.FencePostMATCategory;
            }
            if (tParams.FencePostMATValue !== undefined) {
                tRoomElement.Params.FencePostMATValue = tParams.FencePostMATValue;
            }
            if (tParams.FenceRoundPost !== undefined) {
                tRoomElement.Params.FenceRoundPost = tParams.FenceRoundPost;
            }
            if (tParams.terrainType !== undefined) {
                tRoomElement.Params.terrainType = tParams.terrainType;
            }
            if (tParams.hillProfile !== undefined) {
                tRoomElement.Params.hillProfile = tParams.hillProfile;
            }
            if (tParams.hillHeight !== undefined) {
                tRoomElement.Params.hillHeight = tParams.hillHeight;
            }
            if (tParams.hillOffset !== undefined) {
                tRoomElement.Params.hillOffset = tParams.hillOffset;
            }
            if (tParams.hillTopOffset !== undefined) {
                tRoomElement.Params.hillTopOffset = tParams.hillTopOffset;
            }
            tRoomElement.Params.animationSpeed = tParams.animationSpeed;
            if (tParams.CamPointTargetsCustom != null) {
                tRoomElement.Params.CamPointTargetsCustom = [];
                for (let tPOI of tParams.CamPointTargetsCustom) {
                    // const tPOIdata = {} as RoomElement.POIdata;
                    tRoomElement.Params.CamPointTargetsCustom.push(tPOI);
                }
            }
            if (tRoomElement.mType === "Arena") {
                tRoomElement.mParams.arenaLaneScale = tParams.arenaLaneScale;
                tRoomElement.mParams.arenaFieldScale = tParams.arenaFieldScale;
                tRoomElement.mParams.arenaLaneCount = tParams.arenaLaneCount;
                tRoomElement.mParams.arenaPlayFieldVisible = tParams.arenaPlayFieldVisible;
                tRoomElement.mParams.arenaSideSegmentsVisible = tParams.arenaSideSegmentsVisible;
                tRoomElement.mParams.arenaCircle = false;
                if (tParams.arenaCircle != null) {
                    tRoomElement.mParams.arenaCircle = tParams.arenaCircle;
                }
            }
            if (tRoomElement.mType === "Sphere") {
                if (tParams.audioSphere != null) {
                    tRoomElement.mParams.audioSphere = tParams.audioSphere;
                }
                if (tParams.soundEffectId != null) {
                    tRoomElement.mParams.soundEffectId = tParams.soundEffectId;
                }
            }
        }
        if (t_floorPoints != null) {
            //pRoomManager.FloorsPolygons.set(tIORoomElement.wallIds, { floor: t_floorPoints, floorRE: tRoomElement, materialId: tIORoomElement.materialId});
            pRoomManager.FloorsPolygons.set(tIORoomElement.wallIds, { floor: t_floorPoints, floorRE: tRoomElement,
                materialSet: tIORoomElement.materialSet, transparent: t_floorTransparent });
        }
        tRoomElement.mHeight = tIORoomElement.height;
        tRoomElement.mDepth = tIORoomElement.depth;
        tRoomElement.mWidth = tIORoomElement.width;
        if (tIORoomElement.label !== undefined) {
            tRoomElement.mLabel = tIORoomElement.label;
        }
        if (tIORoomElement.color.indexOf("#") === 0)
            tRoomElement.mColor = tIORoomElement.color;
        tRoomElement.mOrderIndex = tIORoomElement.orderIndex;
        // tRoomElement.mObjectType = tIORoomElement.objectType;
        if (tIORoomElement.materialSet !== undefined && tRoomElement.type === "CustomBuilding") {
            tRoomElement.mMaterialSet = tIORoomElement.materialSet;
            tRoomElement.mMaterialSet.idAddOn = pRoomManager.materialSplitStr + tRoomElement.mIdStr;
        }
        if ((tIORoomElement.type !== "Circle") && (tIORoomElement.type !== "Building")
            && (tIORoomElement.type !== "CustomBuilding") && (tIORoomElement.type !== "BackgroundImage")) {
            tRoomElement.setFloorOffset(tIORoomElement.floorOffset, false);
        }
        if (tRoomElement.mType === "Floor")
            tRoomElement.mWallIds = tIORoomElement.wallIds;
        if (tIORoomElement.opacity != null)
            tRoomElement.mBuildingOpacity = tIORoomElement.opacity;
        if (tIORoomElement.text != null)
            tRoomElement.mText = tIORoomElement.text;
        if (tIORoomElement.textcolor != null)
            tRoomElement.mTextColor = tIORoomElement.textcolor;
        if (tIORoomElement.textscale != null)
            tRoomElement.mTextHeight = tIORoomElement.textscale;
        if (tRoomElement.type === "CameraPath") {
            if (pRoomManager.hideAnimationPath) {
                tRoomElement.node.setEnabled(false);
            }
            const tREgeo = tRoomElement.geometry;
            if (tREgeo != null) {
                tREgeo.computeCamPathData();
            }
        }
        if (tIORoomElement.materialId != null) {
            let tmatTMP = tIORoomElement.materialId;
            if (tmatTMP.indexOf("_#_") !== -1) {
                tmatTMP = tmatTMP.replace(/_#_/g, "_-_");
                if (pRoomManager.MatManager.hasMaterial(tIORoomElement.materialId)) {
                    let toldMat = pRoomManager.MatManager.getMaterial(tIORoomElement.materialId);
                    if (toldMat !== null) {
                        toldMat.name = tmatTMP;
                        pRoomManager.MatManager.addMaterial(tmatTMP, toldMat);
                    }
                }
            }
            tRoomElement.mMaterialId = tmatTMP;
            let tmatId = tRoomElement.mMaterialId.split(pRoomManager.materialSplitStr);
            const tspltCnt = (tRoomElement.mMaterialId.match(/_-_/g) || []).length;
            if (tspltCnt === 2) {
                tmatId.pop();
            }
            let tmatValue = tmatId.pop();
            let tmatCategory = tmatId.pop();
            if (tmatValue !== undefined && tmatCategory !== undefined) {
                tRoomElement.setMaterialCategory(tmatCategory, tmatValue, true, tRoomElement.mMaterialSet, true);
            }
        }
        //position
        let tPos = deserializeVector3(tIORoomElement.position);
        if ((tIORoomElement.type === "Building") || (tIORoomElement.type === "CustomBuilding")) {
            if (tRoomElement.RE_reference == null) {
                let tBuilding_BottomTop = new RoomElement(pRoomManager, "Plane_BottomTop");
                pRoomManager.app.model.addElement(tBuilding_BottomTop);
                tBuilding_BottomTop.setRE_reference(tRoomElement);
                tRoomElement.setRE_reference(tBuilding_BottomTop);
                tBuilding_BottomTop.setHeight(tRoomElement.height, false);
            }
            if (tIORoomElement.floorOffset !== undefined) {
                tPos.y += tIORoomElement.floorOffset;
            }
            if (tPos.y !== 0) {
                tPos.y = 0;
            }
            pRoomManager.addBuilding(tRoomElement);
            pRoomManager.addInitialBuilding(tRoomElement, tRoomElement.height);
        }
        //rotation
        if (tIORoomElement.rotation != null) {
            if (!(tRoomElement.type === "Line" && tgrid != null)) {
                tRoomElement.mNode.rotationQuaternion = deserializeQuaternion(tIORoomElement.rotation);
                tRoomElement.updateTransformation();
            }
        }
        if (tIORoomElement.type === "GeoMarker") {
            let tgeo_InitPosition = Vector3.Zero();
            let tgeo_InitMapRotation = 0;
            let tgeo_InitLatitude = 0;
            let tgeo_InitLongitude = 0;
            let tgeoLatitude = 0;
            let tgeoLongitude = 0;
            if (tIORoomElement.geo_initPosition != null) {
                tgeo_InitPosition = deserializeVector3(tIORoomElement.geo_initPosition);
            }
            if (tIORoomElement.geo_initMapRotation != null) {
                tgeo_InitMapRotation = tIORoomElement.geo_initMapRotation;
            }
            if (tIORoomElement.geo_initLatitude != null) {
                tgeo_InitLatitude = tIORoomElement.geo_initLatitude;
            }
            if (tIORoomElement.geo_initLongitude != null) {
                tgeo_InitLongitude = tIORoomElement.geo_initLongitude;
            }
            if (tIORoomElement.geo_latitude != null) {
                tgeoLatitude = tIORoomElement.geo_latitude;
            }
            if (tIORoomElement.geo_longitude != null) {
                tgeoLongitude = tIORoomElement.geo_longitude;
            }
            tRoomElement.mInitalPosition = tgeo_InitPosition;
            tRoomElement.mInitGeo_MapRotation = tgeo_InitMapRotation;
            tRoomElement.mInitGeo_Latitude = tgeo_InitLatitude;
            tRoomElement.mInitGeo_Longitude = tgeo_InitLongitude;
            tRoomElement.setPosition(tPos);
            tRoomElement.setGeoPosition(tgeoLatitude, tgeoLongitude);
        }
        if (tRoomElement.type === "BackgroundImage") {
            tPos.y -= tIORoomElement.floorOffset;
        }
        if (!(tRoomElement.type === "Line" && tgrid != null)) {
            tRoomElement.mNode.position = tPos;
            tRoomElement.setPosition(tRoomElement.mNode.position, false);
        }
        if (tgeoData != null) {
            let tParams = tgeoData;
            tRoomElement.mMaterial = tParams.material;
            if (tParams.materialColor !== undefined) {
                if (tParams.materialColor.indexOf("#") === 0) {
                    tRoomElement.mColor = tParams.materialColor;
                }
                else {
                    tParams.materialColor = tRoomElement.mColor;
                }
            }
            if (tRoomElement.type === "CameraPath") {
                const tREgeo = tRoomElement.geometry;
                if (tREgeo != null) {
                    tREgeo.computeCamPathData();
                }
            }
            if (tRoomElement.type === "Line" && tRoomElement.GridRE_REF != null) {
                tRoomElement.countGridFields(tRoomElement.mCAG);
            }
            tRoomElement.geometry.loadIOData(tgeoData, pContext);
            tRoomElement.setMeshMaterial1().then(() => {
                tRoomElement.notifyChange(ElementChangedFlags.PropertyValues);
                tRoomElement.notifyChange(ElementChangedFlags.Properties);
                tRoomElement.updateGeometry();
                if (!tRoomElement.mSZ_visible) {
                    tRoomElement.set_SafetyZone_Visibility(false);
                }
                if (tRoomElement.type === "Spline" || tRoomElement.type === "Rectangle" || tRoomElement.type === "Line" || tRoomElement.type === "Circle") {
                    pRoomManager.finalCutAfterLoad();
                }
            });
        }
        if (tgrid != null) {
            // if grid always exists in ModelManager return null
            if (contains(pRoomManager.app.model.elements, tgrid)) {
                return (null);
            }
            return (tgrid);
        }
        return (tRoomElement);
    }
    static checkForLineGrid(pRoomManager, pIORoomElement) {
        if (pIORoomElement.type !== "Line" ||
            pIORoomElement.gridXInt == null ||
            pIORoomElement.gridZInt == null ||
            pIORoomElement.gridwidth == null ||
            pIORoomElement.gridwidth == null ||
            pIORoomElement.gridId == null ||
            pIORoomElement.position == null) {
            return (null);
        }
        const tpos = deserializeVector3(pIORoomElement.position);
        let trot = null;
        if (pIORoomElement.rotation != null) {
            trot = deserializeQuaternion(pIORoomElement.rotation);
        }
        const tWH = pIORoomElement.gridwidth * 0.5;
        const tDH = pIORoomElement.griddepth * 0.5;
        const tpoints = [];
        tpoints.push(new Vector3(-tWH, 0, tDH), new Vector3(-tWH, 0, -tDH), new Vector3(tWH, 0, -tDH), new Vector3(tWH, 0, tDH));
        const tgrid = pRoomManager._insertGrid(tpoints, pIORoomElement.gridId.trim());
        if (tgrid != null) {
            tgrid.setREId(pIORoomElement.gridId.trim());
            tgrid.setPosition(tpos);
            tgrid.node.rotationQuaternion = trot;
            tgrid.updateTransformation();
        }
        return (tgrid);
    }
    // tslint:disable-next-line:cyclomatic-complexity
    _getArenaCAGVerticesData(pPartCAG, pPartMatId, pPart) {
        let tCSG = new CSG();
        tCSG = Util.CAG2CSG(pPartCAG, this.mHeight);
        if (Util.numbersEqual(this.mNode.position.y, RoomManager.ROOM_2dSHAPE_POSITION_ONTOP_Y, 0.0001)) {
            tCSG = tCSG.translate(new CSG.Vector3D([0, -RoomManager.ROOM_2dSHAPE_POSITION_ONTOP_Y, 0]));
        }
        let tmeshName = RoomManager.ROOM_SHAPE2D_PREFIX + this.mType.toString();
        let tMeshes2d = CsgUtils.buildMeshes(tCSG, this.mScene);
        if (tMeshes2d != null) {
            for (let tMesh of tMeshes2d) {
                tMesh.rotate(new Vector3(0, 1, 0), 0);
                tMesh.material = this.mRoomManager.MatManager.getMaterial(pPartMatId);
                tMesh.name = tmeshName;
            }
            if (tMeshes2d[0] != null) {
                let tmeshVertices = tMeshes2d[0].getVerticesData(VertexBuffer.PositionKind);
                let tmeshIndices = tMeshes2d[0].getIndices();
                switch (pPart) {
                    case ArenaPart.PlayField:
                        this.mParams.MeshVertices_PlayingField = null;
                        this.mParams.MeshIndices_PlayingField = null;
                        if (this.mParams.arenaPlayFieldVisible) {
                            this.mParams.MeshVertices_PlayingField = Array.prototype.slice.call(tmeshVertices);
                            this.mParams.MeshIndices_PlayingField = Array.prototype.slice.call(tmeshIndices);
                        }
                        break;
                    case ArenaPart.SideSegments:
                        this.mParams.MeshVertices_SideSegments = null;
                        this.mParams.MeshIndices_SideSegments = null;
                        if (this.mParams.arenaSideSegmentsVisible) {
                            this.mParams.MeshVertices_SideSegments = Array.prototype.slice.call(tmeshVertices);
                            this.mParams.MeshIndices_SideSegments = Array.prototype.slice.call(tmeshIndices);
                        }
                        break;
                    case ArenaPart.RunningTracks:
                        this.mParams.MeshVertices_RunningTracks = Array.prototype.slice.call(tmeshVertices);
                        this.mParams.MeshIndices_RunningTracks = Array.prototype.slice.call(tmeshIndices);
                        break;
                    case ArenaPart.TrackLines:
                        this.mParams.MeshVertices_TrackLines = Array.prototype.slice.call(tmeshVertices);
                        this.mParams.MeshIndices_TrackLines = Array.prototype.slice.call(tmeshIndices);
                        break;
                    default:
                        break;
                }
            }
            else {
                switch (pPart) {
                    case ArenaPart.PlayField:
                        this.mParams.MeshVertices_PlayingField = undefined;
                        this.mParams.MeshIndices_PlayingField = undefined;
                        break;
                    case ArenaPart.SideSegments:
                        this.mParams.MeshVertices_SideSegments = undefined;
                        this.mParams.MeshIndices_SideSegments = undefined;
                        break;
                    case ArenaPart.RunningTracks:
                        this.mParams.MeshVertices_RunningTracks = undefined;
                        this.mParams.MeshIndices_RunningTracks = undefined;
                        break;
                    case ArenaPart.TrackLines:
                        this.mParams.MeshVertices_TrackLines = undefined;
                        this.mParams.MeshIndices_TrackLines = undefined;
                        break;
                    default:
                        break;
                }
            }
            for (let tMesh of tMeshes2d)
                tMesh.dispose();
        }
    }
    // tslint:disable-next-line:cyclomatic-complexity
    _serialize_Params(pParams) {
        let tRes = {};
        tRes.privacyFilter = pParams.privacyFilter;
        tRes.blindVisible = pParams.blindVisible;
        tRes.ColumnCount = pParams.ColumnCount;
        tRes.blindDepth = pParams.blindDepth;
        tRes.blindOpeningAngle = pParams.blindOpeningAngle;
        tRes.blindOpeningWidth = pParams.blindOpeningWidth;
        tRes.ElementOriginalHeight = pParams.ElementOriginalHeight;
        tRes.ElementOriginalPositionX = pParams.ElementOriginalPositionX;
        tRes.ElementOriginalPositionY = pParams.ElementOriginalPositionY;
        tRes.ElementOriginalPositionZ = pParams.ElementOriginalPositionZ;
        tRes.MeshVertices = pParams.MeshVertices;
        tRes.MeshIndices = pParams.MeshIndices;
        tRes.MeshVertices_PlayingField = pParams.MeshVertices_PlayingField;
        tRes.MeshIndices_PlayingField = pParams.MeshIndices_PlayingField;
        tRes.MeshVertices_SideSegments = pParams.MeshVertices_SideSegments;
        tRes.MeshIndices_SideSegments = pParams.MeshIndices_SideSegments;
        tRes.MeshVertices_RunningTracks = pParams.MeshVertices_RunningTracks;
        tRes.MeshIndices_RunningTracks = pParams.MeshIndices_RunningTracks;
        tRes.MeshVertices_TrackLines = pParams.MeshVertices_TrackLines;
        tRes.MeshIndices_TrackLines = pParams.MeshIndices_TrackLines;
        tRes.MeshUVcoord = pParams.MeshUVcoord;
        tRes.fanlight = pParams.fanlight;
        tRes.fanlightHeight = pParams.fanlightHeight;
        tRes.floorColor = pParams.floorColor;
        tRes.floorTransparent = pParams.floorTransparent;
        tRes.material = pParams.material;
        tRes.material_UScale = pParams.material_UScale;
        tRes.material_VScale = pParams.material_VScale;
        tRes.materialColor = pParams.materialColor;
        tRes.textureBase64 = pParams.textureBase64;
        tRes.roadWidth = pParams.roadWidth;
        tRes.minWidth = pParams.minWidth;
        tRes.openingAngle = pParams.openingAngle;
        tRes.frameOffset = pParams.frameOffset;
        tRes.sill_materialId = pParams.sill_materialId;
        tRes.leaf_materialId = pParams.leaf_materialId;
        tRes.frame_materialId = pParams.frame_materialId;
        tRes.sash_materialId = pParams.sash_materialId;
        tRes.materialSet_Sill = pParams.materialSet_Sill;
        tRes.materialSet_Leaf = pParams.materialSet_Leaf;
        tRes.materialSet_Frame = pParams.materialSet_Frame;
        tRes.materialSet_Sash = pParams.materialSet_Sash;
        tRes.curvedRoad = pParams.curvedRoad;
        tRes.HedgeDepth = pParams.HedgeDepth;
        tRes.FenceFieldLength = pParams.FenceFieldLength;
        tRes.FenceMirrorTexture = pParams.FenceMirrorTexture;
        tRes.FenceRoundPost = pParams.FenceRoundPost;
        tRes.FencePostMATCategory = pParams.FencePostMATCategory;
        tRes.FencePostMATValue = pParams.FencePostMATValue;
        tRes.curvedHedge = pParams.curvedHedge;
        tRes.terrainType = pParams.terrainType;
        tRes.hillProfile = pParams.hillProfile;
        tRes.hillHeight = pParams.hillHeight;
        tRes.hillOffset = pParams.hillOffset;
        tRes.hillTopOffset = serializeVector3(pParams.hillTopOffset);
        tRes.audioSphere = pParams.audioSphere;
        if (this.mType == "Sphere" && this.mParams.audioSphere) {
            tRes.soundEffectId = this.mParams.soundEffectId;
        }
        if (pParams.floorPoints != null) {
            tRes.floorPoints = [];
            for (let tP of pParams.floorPoints)
                tRes.floorPoints.push(serializeVector2(tP));
        }
        if (pParams.ElementPoints != null) {
            tRes.ElementPoints = [];
            for (let tP of pParams.ElementPoints)
                tRes.ElementPoints.push(serializeVector3(tP));
        }
        if (pParams.ElementOriginalPoints != null) {
            tRes.ElementOriginalPoints = [];
            for (let tP of pParams.ElementOriginalPoints)
                tRes.ElementOriginalPoints.push(serializeVector3(tP));
        }
        if (pParams.ElementCutPoints != null) {
            tRes.ElementCutPoints = [];
            for (let tP of pParams.ElementCutPoints)
                tRes.ElementCutPoints.push(serializeVector3(tP));
        }
        if (pParams.ElementCutHoles != null) {
            tRes.ElementCutHoles = [];
            for (let tP of pParams.ElementCutHoles)
                tRes.ElementCutHoles.push(serializeVector3(tP));
        }
        if (this.mType === "Arena") {
            tRes.arenaLaneScale = pParams.arenaLaneScale;
            tRes.arenaFieldScale = pParams.arenaFieldScale;
            tRes.arenaLaneCount = pParams.arenaLaneCount;
            tRes.arenaPlayFieldVisible = pParams.arenaPlayFieldVisible;
            tRes.arenaSideSegmentsVisible = pParams.arenaSideSegmentsVisible;
            tRes.arenaCircle = false;
            if (pParams.arenaCircle != null) {
                tRes.arenaCircle = pParams.arenaCircle;
            }
        }
        tRes.animationSpeed = pParams.animationSpeed;
        if (pParams.CamPointTargetsCustom != null && pParams.CamPointTargetsCustom.length > 0) {
            tRes.camCustomTarget_index = [];
            tRes.camCustomTarget_camPosition = [];
            tRes.camCustomTarget_target = [];
            tRes.camCustomTarget_height = [];
            tRes.camCustomTarget_viewAlongPath = [];
            for (let tPOIdata of pParams.CamPointTargetsCustom) {
                tRes.camCustomTarget_index.push(tPOIdata.index);
                tRes.camCustomTarget_camPosition.push(serializeVector3(tPOIdata.camPosition));
                tRes.camCustomTarget_target.push(serializeVector3(tPOIdata.target));
                tRes.camCustomTarget_height.push(tPOIdata.targetHeight);
                tRes.camCustomTarget_viewAlongPath.push(tPOIdata.viewAlongPath);
            }
        }
        return (tRes);
    }
    // tslint:disable-next-line:cyclomatic-complexity
    _buildCSGMesh_Arena() {
        if (this.mCAG_Arena_InField == null ||
            this.mCAG_Arena_SideSegments == null ||
            this.mCAG_Arena_RunningsTracks == null ||
            this.mCAG_Arena_TrackLines == null)
            return;
        this.mRoomManager.checkMaterial(this.mMaterialSet).then(() => {
            this.updateMaterial(this.mMaterialSet);
        });
        this.mRoomManager.checkMaterial(this.mMaterialSet_Arena_PlayField).then(() => {
            this.updateMaterial(this.mMaterialSet_Arena_PlayField);
        });
        this.mRoomManager.checkMaterial(this.mMaterialSet_Arena_SideSegments).then(() => {
            this.updateMaterial(this.mMaterialSet_Arena_SideSegments);
        });
        this.mRoomManager.checkMaterial(this.mMaterialSet_Arena_TrackLines).then(() => {
            this.updateMaterial(this.mMaterialSet_Arena_TrackLines);
        });
        //dispose current geometries
        let t3dNodes = this.m3dGroup.getChildTransformNodes(true);
        for (let tNode of t3dNodes) {
            tNode.dispose();
        }
        //dispose current geometries
        let t2dNodes = this.m2dGroup.getChildTransformNodes(true);
        for (let tNode of t2dNodes) {
            tNode.dispose();
        }
        let tRErotateY = 0;
        let tCSG_Arena_InField = Util.CAG2CSG(this.mCAG_Arena_InField, this.mHeight);
        let tCSG_Arena_SideSegments = Util.CAG2CSG(this.mCAG_Arena_SideSegments, this.mHeight);
        let tCSG_Arena_RunningTracks = Util.CAG2CSG(this.mCAG_Arena_RunningsTracks, this.mHeight);
        let tCSG_Arena_TrackLines = Util.CAG2CSG(this.mCAG_Arena_TrackLines, this.mHeight);
        this.mArea = this.mCAG_Arena_TrackLines.area();
        this.mPerimeter = this.perimeter;
        // infield
        if (this.mParams.arenaPlayFieldVisible) {
            let tMeshes2d_InField = CsgUtils.buildMeshes(tCSG_Arena_InField, this.mScene);
            if (tMeshes2d_InField != null) {
                for (let tMesh of tMeshes2d_InField) {
                    tMesh.rotate(new Vector3(0, 1, 0), tRErotateY);
                    tMesh.parent = this.m2dGroup;
                    tMesh.material = this.localMaterial_Arena_PlayField;
                    tMesh.receiveShadows = true;
                    ShadowGenerator.disableMeshShadow(tMesh);
                    tMesh.name = RoomElement.LABEL_ARENA_INFIELD;
                }
            }
            let tMeshes3d_InField = CsgUtils.buildMeshes(tCSG_Arena_InField, this.mScene);
            if (tMeshes3d_InField != null) {
                for (let tMesh of tMeshes3d_InField) {
                    tMesh.rotate(new Vector3(0, 1, 0), tRErotateY);
                    tMesh.parent = this.m3dGroup;
                    tMesh.material = this.localMaterial_Arena_PlayField;
                    tMesh.receiveShadows = true;
                    ShadowGenerator.disableMeshShadow(tMesh);
                    tMesh.name = RoomElement.LABEL_ARENA_INFIELD;
                }
            }
        }
        // sidesegments
        if (this.mParams.arenaSideSegmentsVisible) {
            let tMeshes2d_SideSegments = CsgUtils.buildMeshes(tCSG_Arena_SideSegments, this.mScene);
            if (tMeshes2d_SideSegments != null) {
                for (let tMesh of tMeshes2d_SideSegments) {
                    tMesh.rotate(new Vector3(0, 1, 0), tRErotateY);
                    tMesh.parent = this.m2dGroup;
                    tMesh.material = this.localMaterial_Arena_SideSegments;
                    tMesh.receiveShadows = true;
                    ShadowGenerator.disableMeshShadow(tMesh);
                    tMesh.name = RoomElement.LABEL_ARENA_SIDESEGMENTS;
                }
            }
            let tMeshes3d_SideSegments = CsgUtils.buildMeshes(tCSG_Arena_SideSegments, this.mScene);
            if (tMeshes3d_SideSegments != null) {
                for (let tMesh of tMeshes3d_SideSegments) {
                    tMesh.rotate(new Vector3(0, 1, 0), tRErotateY);
                    tMesh.parent = this.m3dGroup;
                    tMesh.material = this.localMaterial_Arena_SideSegments;
                    tMesh.receiveShadows = true;
                    ShadowGenerator.disableMeshShadow(tMesh);
                    tMesh.name = RoomElement.LABEL_ARENA_SIDESEGMENTS;
                }
            }
        }
        // running tracks
        let tMeshes2d_RunningTracks = CsgUtils.buildMeshes(tCSG_Arena_RunningTracks, this.mScene);
        if (tMeshes2d_RunningTracks != null) {
            for (let tMesh of tMeshes2d_RunningTracks) {
                tMesh.rotate(new Vector3(0, 1, 0), tRErotateY);
                tMesh.parent = this.m2dGroup;
                tMesh.material = this.localMaterial;
                tMesh.receiveShadows = true;
                ShadowGenerator.disableMeshShadow(tMesh);
                tMesh.name = RoomElement.LABEL_ARENA_RUNNINGTRACKS;
            }
        }
        let tMeshes3d_RunningTracks = CsgUtils.buildMeshes(tCSG_Arena_RunningTracks, this.mScene);
        if (tMeshes3d_RunningTracks != null) {
            for (let tMesh of tMeshes3d_RunningTracks) {
                tMesh.rotate(new Vector3(0, 1, 0), tRErotateY);
                tMesh.parent = this.m3dGroup;
                tMesh.material = this.localMaterial;
                tMesh.receiveShadows = true;
                ShadowGenerator.disableMeshShadow(tMesh);
                tMesh.name = RoomElement.LABEL_ARENA_RUNNINGTRACKS;
            }
        }
        // track lines
        let tMeshes2d_TrackLines = CsgUtils.buildMeshes(tCSG_Arena_TrackLines, this.mScene);
        if (tMeshes2d_TrackLines != null) {
            for (let tMesh of tMeshes2d_TrackLines) {
                tMesh.rotate(new Vector3(0, 1, 0), tRErotateY);
                tMesh.parent = this.m2dGroup;
                tMesh.material = this.localMaterial_Arena_TrackLines;
                tMesh.receiveShadows = true;
                ShadowGenerator.disableMeshShadow(tMesh);
                tMesh.name = RoomElement.LABEL_ARENA_TRACKLINES;
            }
        }
        let tMeshes3d_TrackLines = CsgUtils.buildMeshes(tCSG_Arena_TrackLines, this.mScene);
        if (tMeshes3d_TrackLines != null) {
            for (let tMesh of tMeshes3d_TrackLines) {
                tMesh.rotate(new Vector3(0, 1, 0), tRErotateY);
                tMesh.parent = this.m3dGroup;
                tMesh.material = this.localMaterial_Arena_TrackLines;
                tMesh.receiveShadows = true;
                ShadowGenerator.disableMeshShadow(tMesh);
                tMesh.name = RoomElement.LABEL_ARENA_TRACKLINES;
            }
        }
        this.mRoomManager.app.viewer.requestRenderFrame();
    }
    // tslint:disable-next-line:cyclomatic-complexity
    _deserialize_Params(pParams) {
        let tRes = {};
        tRes.privacyFilter = pParams.privacyFilter;
        tRes.blindVisible = pParams.blindVisible;
        tRes.ColumnCount = pParams.ColumnCount;
        tRes.blindDepth = pParams.blindDepth;
        tRes.blindOpeningAngle = pParams.blindOpeningAngle;
        tRes.blindOpeningWidth = pParams.blindOpeningWidth;
        tRes.ElementOriginalHeight = pParams.ElementOriginalHeight;
        tRes.ElementOriginalPositionX = pParams.ElementOriginalPositionX;
        tRes.ElementOriginalPositionY = pParams.ElementOriginalPositionY;
        tRes.ElementOriginalPositionZ = pParams.ElementOriginalPositionZ;
        tRes.MeshVertices = pParams.MeshVertices;
        tRes.MeshIndices = pParams.MeshIndices;
        tRes.MeshUVcoord = pParams.MeshUVcoord;
        tRes.fanlight = pParams.fanlight;
        tRes.fanlightHeight = pParams.fanlightHeight;
        tRes.floorColor = pParams.floorColor;
        tRes.floorTransparent = pParams.floorTransparent;
        tRes.material = pParams.material;
        tRes.material_UScale = pParams.material_UScale;
        tRes.material_VScale = pParams.material_VScale;
        tRes.materialColor = pParams.materialColor;
        tRes.textureBase64 = pParams.textureBase64;
        tRes.roadWidth = pParams.roadWidth;
        tRes.minWidth = pParams.minWidth;
        tRes.openingAngle = pParams.openingAngle;
        tRes.frameOffset = pParams.frameOffset;
        tRes.sill_materialId = pParams.sill_materialId;
        tRes.leaf_materialId = pParams.leaf_materialId;
        tRes.frame_materialId = pParams.frame_materialId;
        tRes.sash_materialId = pParams.sash_materialId;
        tRes.materialSet_Sill = pParams.materialSet_Sill;
        tRes.materialSet_Leaf = pParams.materialSet_Leaf;
        tRes.materialSet_Frame = pParams.materialSet_Frame;
        tRes.materialSet_Sash = pParams.materialSet_Sash;
        tRes.curvedRoad = pParams.curvedRoad;
        tRes.HedgeDepth = pParams.HedgeDepth;
        tRes.FenceFieldLength = pParams.FenceFieldLength;
        tRes.FenceMirrorTexture = pParams.FenceMirrorTexture;
        tRes.FenceRoundPost = pParams.FenceRoundPost;
        tRes.FencePostMATCategory = pParams.FencePostMATCategory;
        tRes.FencePostMATValue = pParams.FencePostMATValue;
        tRes.curvedHedge = pParams.curvedHedge;
        tRes.terrainType = pParams.terrainType;
        tRes.hillProfile = pParams.hillProfile;
        tRes.hillHeight = pParams.hillHeight;
        tRes.hillOffset = pParams.hillOffset;
        if (pParams.hillTopOffset != null) {
            tRes.hillTopOffset = deserializeVector3(pParams.hillTopOffset);
        }
        if (pParams.floorPoints != null) {
            tRes.floorPoints = [];
            for (let tP of pParams.floorPoints) {
                const tP_v3 = Util.deserializeV2(tP);
                tRes.floorPoints.push(tP_v3);
            }
        }
        if (pParams.ElementPoints != null) {
            tRes.ElementPoints = [];
            for (let tP of pParams.ElementPoints) {
                const tP_v3 = Util.deserializeV3(tP);
                tRes.ElementPoints.push(tP_v3);
            }
        }
        if (pParams.ElementOriginalPoints != null) {
            tRes.ElementOriginalPoints = [];
            for (let tP of pParams.ElementOriginalPoints) {
                const tP_v3 = Util.deserializeV3(tP);
                tRes.ElementOriginalPoints.push(tP_v3);
            }
        }
        if (pParams.ElementCutPoints != null) {
            tRes.ElementCutPoints = [];
            for (let tP of pParams.ElementCutPoints) {
                const tP_v3 = Util.deserializeV3(tP);
                tRes.ElementCutPoints.push(tP_v3);
            }
        }
        if (pParams.ElementCutHoles != null) {
            tRes.ElementCutHoles = [];
            for (let tP of pParams.ElementCutHoles) {
                const tP_v3 = Util.deserializeV3(tP);
                tRes.ElementCutHoles.push(tP_v3);
            }
        }
        if (pParams.animationSpeed != null) {
            tRes.animationSpeed = pParams.animationSpeed;
        }
        if (this.mType === "Arena") {
            tRes.arenaLaneScale = pParams.arenaLaneScale;
            tRes.arenaFieldScale = pParams.arenaFieldScale;
            tRes.arenaLaneCount = pParams.arenaLaneCount;
            tRes.arenaPlayFieldVisible = pParams.arenaPlayFieldVisible;
            tRes.arenaSideSegmentsVisible = pParams.arenaSideSegmentsVisible;
            tRes.arenaCircle = false;
            if (pParams.arenaCircle != null) {
                tRes.arenaCircle = pParams.arenaCircle;
            }
        }
        if (this.mType === "Sphere") {
            if (pParams.audioSphere != null) {
                tRes.audioSphere = pParams.audioSphere;
            }
            if (pParams.soundEffectId != null) {
                tRes.soundEffectId = pParams.soundEffectId;
            }
        }
        if (pParams.camCustomTarget_index != null &&
            pParams.camCustomTarget_target != null &&
            pParams.camCustomTarget_height != null &&
            pParams.camCustomTarget_viewAlongPath != null) {
            const tlen_Idx = pParams.camCustomTarget_index.length;
            const tlen_target = pParams.camCustomTarget_target.length;
            const tlen_height = pParams.camCustomTarget_height.length;
            const tlen_viewAP = pParams.camCustomTarget_viewAlongPath.length;
            let tlen_camPosition = 0;
            if (pParams.camCustomTarget_camPosition != null) {
                tlen_camPosition = pParams.camCustomTarget_camPosition.length;
            }
            if (tlen_Idx !== tlen_target || tlen_Idx !== tlen_height || tlen_Idx !== tlen_viewAP) {
                return (tRes);
            }
            tRes.CamPointTargetsCustom = [];
            for (let i = 0; i < tlen_Idx; i++) {
                const tPOI = {};
                tPOI.index = pParams.camCustomTarget_index[i];
                tPOI.target = deserializeVector3(pParams.camCustomTarget_target[i]);
                tPOI.targetHeight = pParams.camCustomTarget_height[i];
                tPOI.viewAlongPath = pParams.camCustomTarget_viewAlongPath[i];
                if (tlen_camPosition === tlen_target) {
                    tPOI.camPosition = deserializeVector3(pParams.camCustomTarget_camPosition[i]);
                }
                tRes.CamPointTargetsCustom.push(tPOI);
            }
        }
        return (tRes);
    }
    createPickPlane() {
        let tmeshNormals = this.mdrawnMesh3d.getVerticesData(VertexBuffer.NormalKind);
        let positions = this.mdrawnMesh3d.getVerticesData(VertexBuffer.PositionKind);
        if (tmeshNormals != null && positions != null) {
            let v1 = Vector3.FromArray(positions, 0);
            let v2 = v1.add(Vector3.FromArray(tmeshNormals, 0).scaleInPlace(1));
            let tP1 = v1.add(this.mdrawnMesh3d.position);
            let tP2 = v2.add(this.mdrawnMesh3d.position);
            let tNV = new Vector3(tP2.x - tP1.x, tP2.y - tP1.y, tP2.z - tP1.z);
            let tRotation = getRotationQuaternion(this.node).clone();
            let tM = new Matrix();
            tRotation.toRotationMatrix(tM);
            let tNV_rot = Vector3.TransformCoordinates(tNV, tM);
            let tPos = this.node.absolutePosition;
            let tnormal = tNV_rot;
            let tPickPlane = Plane.FromPositionAndNormal(tPos, tnormal);
            //if (tPickPlane.d > 0)
            //    tPickPlane.d *= -1;
            tPickPlane.normalize();
            this.mPickPlane = tPickPlane;
        }
    }
    createSound(pMesh) {
        this.stop_preview();
        this.mSound = new Sound("soundEffect", this.mAudioURL, this.mScene, undefined, { loop: true, autoplay: true, maxDistance: this.mWidth * 0.5 });
        if (this.mSound != null) {
            this.mSound.attachToMesh(pMesh);
        }
        this.notifyChange(ElementChangedFlags.PropertyValues);
    }
    cleanCuttedRoomElements() {
        for (let tRE of this.mCuttedRoomElements) {
            if (!contains(this.mRoomManager.RoomElements, tRE)) {
                removeElement(this.mCuttedRoomElements, tRE);
                tRE.dispose();
            }
        }
    }
    clone_grid(pNewParent) {
        let tGridPoly = null;
        let tClonedGrid = new RoomElement(this.mRoomManager, this.mGeometry.getType());
        tClonedGrid.beginGeometryUpdate();
        tClonedGrid.node.parent = pNewParent != null ? pNewParent : null;
        tClonedGrid.copyDataFrom(this);
        tClonedGrid.endGeometryUpdate();
        this.mInitialClone = true;
        tClonedGrid.eventAdded.addListener(() => this.afterClone());
        tClonedGrid.setMaterialId(this.mMaterialId);
        if (this.mREforGrid_REF != null && this.mREforGrid_REF.length > 0) {
            for (let tGP of this.mREforGrid_REF) {
                tGridPoly = tGP.clone();
                if (tGridPoly != null && tClonedGrid != null) {
                    const tLine = tGridPoly;
                    tLine.setGridRE_REF(tClonedGrid);
                    tClonedGrid.setREforGrid_REF([tLine]);
                    tLine.setPosition(Vector3.Zero());
                    this.mRoomManager.app.model.removeElement(tLine, false, false);
                    this.mRoomManager.app.model.addElement(tLine, tClonedGrid.node);
                    tLine.updateTransformation();
                }
            }
        }
        return (tClonedGrid);
    }
    calcnewGeoCoordinates() {
        //
    }
    calcNormalVector() {
        if (this.mNode == null)
            return;
        let tperpVec = new Vector3(0, 1, 0);
        let trotMatrix = this.mNode.getWorldMatrix().getRotationMatrix();
        let tnormal = Vector3.TransformCoordinates(tperpVec, trotMatrix);
        tnormal = tnormal.normalize().scale(1);
        let tnodePos = this.mNode.absolutePosition;
        let tPickPlane = Plane.FromPositionAndNormal(tnodePos, tnormal);
        tPickPlane.normalize();
        if (this.mType !== "CameraPath") {
            this.mPickPlane = tPickPlane;
        }
        /*
        // display normal vector
        let tPoints: Array<Vector3> = [];
        tPoints.push(this.mNode.position);
        tPoints.push(this.mNode.position.add(tnormal));

        Util.drawLine(tPoints, this.mScene);
        */
    }
}
//// private ///////////////////////////////////////////////////////////////////////////////
RoomElement.INITIAL_2D_SHAPE_HEIGHT = 0.004;
RoomElement.PROP_KEY_LABEL = "room:RoomElement:label";
RoomElement.PROP_KEY_WIDTH = "room:RoomElement:width";
RoomElement.PROP_KEY_DEPTH = "room:RoomElement:depth";
RoomElement.PROP_KEY_LENGTH = "room:RoomElement:length";
RoomElement.PROP_KEY_CURVED = "room:RoomElement:curved";
RoomElement.PROP_KEY_RADIUS = "room:RoomElement:radius";
RoomElement.PROP_KEY_AREA = "room:RoomElement:area";
RoomElement.PROP_KEY_PERIMETER = "room:RoomElement:perimeter";
RoomElement.PROP_KEY_Height = "room:RoomElement:height";
RoomElement.PROP_KEY_Opacity = "room:RoomElement:opacity";
RoomElement.PROP_KEY_COLOR = "room:RoomElement:color";
RoomElement.PROP_KEY_MATERIAL = "room:RoomElement:material";
RoomElement.PROP_KEY_MATERIALCATEGORY = "room:RoomElement:materialCategory";
RoomElement.PROP_KEY_ROTATE_TEXTURE = "room:RoomElement:rotateTexture";
RoomElement.PROP_KEY_TEXTURE_OFFSETU = "room:RoomElement:textureOffsetU";
RoomElement.PROP_KEY_TEXTURE_OFFSETV = "room:RoomElement:textureOffsetV";
RoomElement.PROP_KEY_TEXTURE_SCALEU = "room:RoomElement:textureScaleU";
RoomElement.PROP_KEY_TEXTURE_SCALEV = "room:RoomElement:textureScaleV";
RoomElement.PROP_KEY_ONSITE = "room:RoomElement:onsite";
RoomElement.PROP_KEY_TERRAIN_HillProfile = "room:RoomElement:terrain_HillProfile";
RoomElement.PROP_KEY_TERRAIN_Type = "room:RoomElement:terrain_Type";
RoomElement.PROP_KEY_TERRAIN_HillOffset = "room:RoomElement:terrain_HillOffset";
RoomElement.PROP_KEY_TERRAIN_HillHeight = "room:RoomElement:terrain_HillHeight";
RoomElement.PROP_KEY_CROSSHAIR = "room:RoomElement:crosshair";
//private static readonly PROP_KEY_FLOOROFFSET = "room:RoomElement:floorOffset";
RoomElement.LABEL_ARENA_INFIELD = "room_Arena_PlayingField";
RoomElement.LABEL_ARENA_SIDESEGMENTS = "room_Arena_SideSegments";
RoomElement.LABEL_ARENA_RUNNINGTRACKS = "room_Arena_RunningTracks";
RoomElement.LABEL_ARENA_TRACKLINES = "room_Arena_TrackLines";
RoomElement.PROP_KEY_GRIDVISIBILITY = "room:RoomElement:gridVisibility";
RoomElement.NODESCALE = 10;
//add nested types
(function (RoomElement) {
    let TrackPart;
    (function (TrackPart) {
        TrackPart[TrackPart["TrackLine"] = 0] = "TrackLine";
        TrackPart[TrackPart["RunningTrack"] = 1] = "RunningTrack";
    })(TrackPart = RoomElement.TrackPart || (RoomElement.TrackPart = {}));
})(RoomElement || (RoomElement = {}));
function getPBR(pPBRMat, scene, pCoreApp) {
    let tMat = new BABYLON.PBRCustomMaterial(pPBRMat.id + '_hill', scene);
    tMat.environmentBRDFTexture = null;
    tMat.Vertex_Definitions(`
        varying mat4 vAlbedoMatrix;
        varying vec3 origPos;
        varying vec3 origNorm;
    `);
    tMat.Vertex_MainEnd(`
        origPos = position;
        origNorm = normal;
        vAlbedoMatrix = albedoMatrix;
    `);
    tMat.Fragment_Begin(`
        precision highp float;
        precision highp int;
        `);
    tMat.Fragment_Definitions(`
        varying mat4 vAlbedoMatrix;
        varying vec3 origPos;
        varying vec3 origNorm;
        vec2 vTextureUVX;
        vec2 vTextureUVY;
        vec2 vTextureUVZ;
        vec3 Blend(vec3 normal){
            vec3 blend=vec3(0.0);
            vec2 xzBlend = abs(normalize(normal.xz));
            blend.xz = max(vec2(0.0), xzBlend - vec2(0.675));
            blend.xz /= max(vec2(0.00001), dot(blend.xz, vec2(1.0)));
            blend.y = clamp((abs(normal.y)-0.675)*80.0,0.0,1.0);
            blend.xz *= (1.0 - blend.y);
            return blend;
        }

        `);
    tMat.Fragment_MainBegin(`
        vTextureUVX = vec2(vAlbedoMatrix*vec4(origPos.zy, 1.0, 0.0));
        vTextureUVY = vec2(vAlbedoMatrix*vec4(origPos.xz, 1.0, 0.0));
        vTextureUVZ = vec2(vAlbedoMatrix*vec4(origPos.xy, 1.0, 0.0));
        vec3 axSign = sign(origNorm);
        vTextureUVX.x *= axSign.x;
        vTextureUVY.x *= axSign.y;
        vTextureUVZ.x *= -axSign.z;
        `);
    tMat.Fragment_Custom_Albedo(`
            vec3 tBlendCoefficients = Blend(origNorm);
            surfaceAlbedo=vAlbedoColor.rgb;
            vec3 t=vec3(0);
            t+=(texture2D(albedoSampler,  vTextureUVX)).rgb*tBlendCoefficients.x;
            t+=(texture2D(albedoSampler,  vTextureUVY)).rgb*tBlendCoefficients.y;
            t+=(texture2D(albedoSampler,  vTextureUVZ)).rgb*tBlendCoefficients.z;
            surfaceAlbedo*= toLinearSpace(t);
        `);
    tMat.useRoughnessFromMetallicTextureAlpha = false;
    tMat.useRoughnessFromMetallicTextureGreen = false;
    tMat.useLinearAlphaFresnel = true;
    tMat.metallic = 0;
    tMat.roughness = 1;
    tMat.invertNormalMapX = true;
    tMat.invertNormalMapY = true;
    tMat.disableLighting = true;
    tMat.imageProcessingConfiguration = pCoreApp.model.materials.pbrImageProcessing;
    tMat.reflectionTexture = pCoreApp.model.materials.getDefaultReflectionTexture().clone();
    if (tMat.reflectionTexture) {
        tMat.reflectionTexture.level = 0;
    }
    //const grass: BABYLON.Texture = new BABYLON.Texture('w-cf-room/data/textures/test.png', scene);
    if (pPBRMat.albedoTexture != null) {
        tMat.albedoTexture = pPBRMat.albedoTexture.clone(); // grass;
    }
    tMat.zOffset = 1;
    tMat.backFaceCulling = false;
    // tMat.id = pPBRMat.id + '_hill';
    // tMat.name = pPBRMat.id + '_hill';
    tMat.freeze();
    return tMat;
}
export var GridMatType;
(function (GridMatType) {
    GridMatType[GridMatType["full"] = 0] = "full";
    GridMatType[GridMatType["diagonal"] = 1] = "diagonal";
    GridMatType[GridMatType["vertical"] = 2] = "vertical";
    GridMatType[GridMatType["horizontal"] = 3] = "horizontal";
    GridMatType[GridMatType["freeform"] = 4] = "freeform";
})(GridMatType || (GridMatType = {}));
